import { partnerOnboardingMessages } from "./components/partner-onboarding-messages";
import { partnerIntakeMessages } from "./components/partner-intake-messages";
import { quickPrintEnglish } from "./i18n/quick-print-messages";
import { providerConsoleEnglish } from "./i18n/provider-console-messages";
import {
  localeMeta,
  supportedLocaleFor,
  supportedLocales,
  type LocaleMeta,
  type SupportedLocale
} from "@tinkr/shared/locales";
import { messagePartLoader, messagesSplit } from "./i18n/message-loaders";
import type { DeferredMessagePart, MessagePart } from "./i18n/message-parts";

export {
  localeMeta,
  supportedLocaleFor,
  supportedLocales,
  type LocaleMeta,
  type SupportedLocale
};

const en = {
  "adminQueue.loadMoreApplications": "Load more applications",
  "adminQueue.loadingApplications": "Loading applications…",
  "adminQueue.loadMoreIncidents": "Load more incidents",
  "adminQueue.loadingIncidents": "Loading more incidents…",
  "workQueue.loadMore": "Load more",
  "workQueue.summaryPartial": "This summary covers the latest work. Older jobs or quality incidents may still need attention. Open the work queue and use Load more to review every page; counts appear when the full list is loaded.",
  "workQueue.statusUnknown": "Status unknown",
  "workQueue.loadingMore": "Loading more…",
  "operations.shipping.unavailable": "International delivery is currently unavailable. Your saved preferences and rates are kept. You can still configure domestic delivery.",
  "operations.shipping.guide": "Delivery settings show the international destinations currently available to you. Opt in and set a delivery rate and parcel limits for each destination you serve. Shipping groups identify permitted routes; they do not set your prices or waive import charges. Updating domestic delivery keeps your international preferences and rates.",
  "operations.shipping.optInRequired": "Enable international delivery to use this saved rate.",
  "operations.shipping.addInternational": "Add international delivery rate",

  ...quickPrintEnglish,
  ...providerConsoleEnglish,
  "admin.general.attention.queue.partnerLegacyPayoutReview": "Legacy partner commissions needing reconciliation",
  "admin.general.attention.queue.partnerWisePayoutReview": "Partner Wise batches needing review",
  ...partnerIntakeMessages,
  ...partnerOnboardingMessages,
  "admin.partners.subtab.appLaunches": "App launches",
  "orderLimit.full": "All order slots are in use. Ship an active order or request a higher limit in Operations.",
  "orderLimit.title": "Order capacity",
  "orderLimit.refresh": "Refresh",
  "orderLimit.error": "Unable to complete this action. Refresh and try again.",
  "orderLimit.sent": "Request sent for admin review.",
  "orderLimit.usage": "{used} of {limit} order slots used",
  "orderLimit.policy": "Each accepted order uses one slot until shipped. Adding printers does not raise your limit.",
  "orderLimit.unlimited": "No order limit. Accept as many orders as you can ship on time.",
  "orderLimit.activeCount": "{count} active orders",
  "orderLimit.completed": "{count} completed orders",
  "orderLimit.pending": "An increase request is awaiting review.",
  "orderLimit.threshold": "Complete 3 orders before requesting a higher limit.",
  "orderLimit.requested": "Requested order limit",
  "orderLimit.explanation": "Explain how you will fulfill more orders",
  "orderLimit.photo": "Equipment photo (optional)",
  "orderLimit.evidenceHint": "Private admin evidence. Up to 6 portfolio photos, 15 MB each. Photos attached to requests are retained with the review history.",
  "orderLimit.existingPhotos": "Choose an existing portfolio photo",
  "orderLimit.noPhoto": "No photo",
  "orderLimit.submit": "Request increase",
  "orderLimit.history": "Request history",
  "orderLimit.open": "Request a higher limit",
  "orderLimit.status.pending": "Pending review",
  "orderLimit.atMaximum": "You're at the highest order limit.",
  "orderLimit.hint.limitRange": "Enter a limit from {min} to {max}.",
  "orderLimit.hint.explanation": "Add a short explanation (at least 10 characters).",
  "orderLimit.approved": "Approved",
  "orderLimit.rejected": "Rejected",
  "premium.title": "Quality verification",
  "premium.refresh": "Refresh",
  "premium.status.notPremium": "Not quality verified yet",
  "premium.status.business": "Eligible through your business account",
  "premium.status.approved": "Quality verified print provider since {date}",
  "premium.status.approvedNoDate": "You are a quality verified print provider",
  "premium.status.draft": "Quality verification request in progress",
  "premium.status.pending": "Under review, submitted {date}",
  "premium.status.declined": "Quality verification declined",
  "premium.status.withdrawn": "Request withdrawn",
  "premium.intro": "Quality verified print providers can accept Doodles orders and premium orders from apps that need a quality guarantee.",
  "premium.howTo": "To request quality verification, add {min} to {max} photos of finished prints you made, on Tinkr or elsewhere, and a short note.",
  "premium.reviewedOn": "The Tinkr team looks at your photos, your portfolio and your completed Tinkr orders.",
  "premium.businessHint": "Business accounts can take Doodles and premium orders without a separate quality verification.",
  "premium.approvedHint": "You can accept Doodles orders and premium orders. Premium orders are marked in your work queue.",
  "premium.pendingHint": "The Tinkr team is reviewing your request. The decision will appear here.",
  "premium.declineReasonLabel": "Reason from the Tinkr team",
  "premium.declinedHint": "You can request another quality verification with new photos at any time.",
  "premium.notEligible": "You can request quality verification once your provider account is active.",
  "premium.start": "Start a quality verification request",
  "premium.requestAgain": "Request another verification",
  "premium.draftHint": "Add {min} to {max} photos of finished prints you made, on Tinkr or elsewhere, then submit for review.",
  "premium.photoCount": "{count} of {max} photos added",
  "premium.caption": "Caption for the next photo (optional)",
  "premium.photo": "Add a photo",
  "premium.photoHint": "JPEG, PNG, WebP or HEIC, up to 15 MB each. Only the Tinkr review team sees these photos.",
  "premium.photoAlt": "Photo {number}",
  "premium.removePhoto": "Remove",
  "premium.removePhotoLabel": "Remove photo {number}",
  "premium.uploading": "Uploading photo…",
  "premium.note": "Note for the review team (optional)",
  "premium.noteHint": "Tell us about your printers, the materials you use and the kind of work you make. Up to {max} characters.",
  "premium.photosNeeded": "Add at least {min} photos to submit.",
  "premium.submit": "Submit for review",
  "premium.submitted": "Quality verification request sent.",
  "premium.withdraw": "Withdraw request",
  "premium.withdrawConfirm": "Withdraw this quality verification request? You can start a new one later.",
  "premium.withdrawConfirmYes": "Yes, withdraw",
  "premium.withdrawCancel": "Keep request",
  "premium.error.load": "Unable to load your quality verification status. Refresh and try again.",
  "premium.error.generic": "Unable to complete this action. Refresh and try again.",
  "premium.error.notAllowed": "Your account can't request quality verification right now. It is open to active individual makers who are not quality verified yet.",
  "premium.error.exists": "A quality verification request is already open. Refresh to see it.",
  "premium.error.photoLimit": "You can add up to {max} photos.",
  "premium.error.photosRequired": "Add at least {min} photos before submitting.",
  "premium.error.invalidState": "This quality verification request has changed. Refresh and try again.",
  "premium.error.notFound": "This quality verification request could not be found. Refresh and try again.",
  "premium.error.fileType": "Choose a JPEG, PNG, WebP or HEIC photo.",
  "premium.error.fileSize": "Each photo must be 15 MB or smaller.",
  "premium.error.upload": "The photo could not be uploaded. Try again.",
  "common.close": "Close",
  "common.dismiss": "Dismiss",
  "common.labelSeparator": ": ",
  "locale.loading": "Loading language…",
  "locale.loadError": "We couldn't load that language.",
  "locale.retry": "Retry",
  "boot.loadError.title": "This page didn't finish loading",
  "boot.loadError.body": "Check your connection, then try again. If that doesn't work, reload the page.",
  "boot.loadError.retry": "Try again",
  "boot.loadError.reload": "Reload page",
  "apiError.validation.generic": "Some information is invalid. Check the form and try again.",
  "apiError.validation.fieldInvalid": "{field}: Check this value and try again.",
  "apiError.validation.required": "{field}: This field is required.",
  "apiError.validation.rule": "The value does not meet the {rule} rule.",
  "apiError.permission.actorInactive": "Your account is not active for this action. Contact support if this is unexpected.",
  "apiError.rejection.unknown": "The request couldn't be completed. Refresh and try again.",
  "access.hero.readinessSummary": "Provider readiness summary",
  "pluginKeys.expired": "Expired",
  "partnerKeysSelf.expiredKeys": "Expired keys",
  "docs.contract.eyebrow": "Developers",
  "docs.contract.title": "API contract preview",
  "docs.contract.ledeBefore": "Browse the live API contract emitted by",
  "docs.contract.ledeAfter": "Operations expand to show the request and response schema for each route.",
  "docs.contract.hide": "Hide live preview",
  "docs.contract.show": "Show live preview",
  "docs.contract.loadingPreview": "Loading preview…",
  "docs.contract.audience": "Audience",
  "docs.contract.audiencePublic": "Public",
  "docs.contract.audienceProvider": "Provider",
  "docs.contract.loading": "Loading API contract…",
  "docs.contract.error": "We couldn't load the API contract. Try again in a moment.",
  "docs.contract.empty": "No operations are available for this audience.",
  "docs.contract.operation": "API operation",
  "docs.contract.sourceSummary": "Original API description:",
  "docs.contract.operationId": "Operation ID:",
  "docs.contract.requestBody": "Request body",
  "docs.contract.noRequestBody": "No request body.",
  "docs.contract.response200": "Response (200)",
  "docs.contract.noResponseBody": "No documented response body.",
  "docs.contract.sampleCurl": "Sample curl",
  "documents.title": "Statements & invoices",
  "documents.view": "Show more",
  "documents.refresh": "Refresh",
  "documents.download": "Get PDF",
  "documents.open": "Open PDF",
  "documents.empty": "No statements or invoices are available yet.",
  "documents.error": "Something went wrong. Please try again.",
  "documents.loading": "Loading…",
  "documents.receipt": "Payment receipt",
  "documents.statement": "Settlement statement",
  "documents.remittance": "Payment advice",
  "documents.supplier": "Your invoice",
  "documents.note": "A payment receipt is not a VAT invoice.",
  "documents.expires": "This secure link expires in two minutes.",
  "documents.superseded": "Superseded original",
  "documents.upload": "Send an invoice",
  "documents.file": "Invoice PDF (maximum 2 MiB)",
  "documents.number": "Invoice number",
  "documents.currency": "Currency",
  "documents.total": "Invoice total",
  "documents.tax": "Invoice tax (optional)",
  "documents.commit": "Submit invoice",
  "documents.invoiceClaim": "Upload your original invoice for this settlement. It records your charges, not a payment.",
  "documents.pending": "Verifying invoice…",
  "documents.rejected": "Couldn't verify this invoice. Check the PDF and try again.",
  "documents.error.amountFormat": "Enter the amount as a number, for example {example}.",
  "documents.error.amountAmbiguous": "This amount could be read two ways. Write it without a thousands separator, for example {example}.",
  "documents.error.amountDecimals": "{currency} amounts have at most {decimals} decimal places.",
  "documents.error.fileSize": "Choose a PDF of at most 2 MiB.",
  "documents.error.fileType": "Choose a PDF file.",
  "documents.complete": "Invoice saved.",
  'admin.general.subtab.finance': 'Accounting',
  'admin.finance.title': 'Finance',
  'admin.finance.subtitle': 'Tinkr App Ltd · accountant evidence · GBP reporting · Europe/London',
  'admin.finance.overview': 'Overview',
  'admin.finance.sources': 'Sources & imports',
  'admin.finance.evidence': 'Review evidence',
  'admin.finance.exports': 'Accountant packs',
  'admin.finance.refresh': 'Refresh view',
  'admin.finance.month': 'Accounting month',
  'admin.finance.mode': 'Money mode',
  'admin.finance.live': 'Live money',
  'admin.finance.test': 'Test only',
  'admin.finance.provisional': 'Provisional — these records are evidence, not signed-off accounts.',
  'admin.finance.unconfigured': 'Accounting policy is not configured. Do not assume VAT status, a financial year end or revenue recognition.',
  'admin.finance.documents': 'Financial documents',
  'admin.finance.movements': 'Recorded source movements — not revenue or profit',
  'admin.finance.moneyIn': 'Money in',
  'admin.finance.moneyOut': 'Money out',
  'admin.finance.net': 'Net movement',
  'admin.finance.empty': 'No records for this period. Missing coverage does not mean zero activity.',
  'admin.finance.prepare': 'Prepare accountant pack',
  'admin.finance.download': 'Prepare secure download',
  'admin.finance.more': 'Load more',
  'admin.finance.upload': 'Upload a statement',
  'admin.finance.preview': 'Validate & preview',
  'admin.finance.commit': 'Confirm import',
  'admin.finance.confirm': 'I checked the source, period, currency totals and warnings, and will retain the original institution statement.',
  'admin.finance.originals': 'The uploaded file is retained privately. For a manually mapped CSV, also keep the original bank/Wise statement for your accountant.',
  'admin.finance.loading': 'Loading finance evidence…',
  'admin.finance.error': 'Could not complete the finance request. No money was sent.',
  'admin.finance.noSession': 'Sign in with an active admin account to view finance.',
  "rateCard.appFit.ceiling": "Supplier payout ceilings, including shipping. Your full job quote must fit the applicable ceiling.",
  "rateCard.appFit.solid": "Solid",
  "rateCard.appFit.multicolor": "Multicolor",
  'admin.general.subtab.doodlesConfig': 'Doodles settings',
  'admin.general.subtab.doodlesAccounts': 'Doodles Accounts',
  'admin.general.subtab.doodlesRecovery': 'Doodles Recovery',
  "brand.fulfillment": "Fulfillment",
  "brand.operations": "Operations",
  "nav.apiAccess": "API access",
  "nav.login": "Login",
  "nav.returnDashboard": "Return to dashboard",
  "nav.dashboard": "Dashboard",
  "nav.language": "Language",
  "nav.themeLight": "Switch to light theme",
  "nav.themeDark": "Switch to dark theme",
  "splash.providerSignup": "Become a provider",
  "splash.apiAccess": "Partner API access",
  "splash.appsEyebrow": "Tinkr app store",
  "splash.appsTitle": "Apps built on the Tinkr network.",
  "splash.appsBody":
    "First-party apps and tools that turn an idea into a delivered print. More are on the way.",
  "catalog.launchDate": "Launches {date}",
  "catalog.unavailable": "Temporarily unavailable",
  "catalog.openApp": "Open app",
  "catalog.loading": "Loading apps…",
  "catalog.loadError": "Could not load apps.",
  "catalog.retry": "Try again",
  "catalog.empty": "No apps are listed right now.",
  "admin.general.subtab.appCatalog": "App catalog",
  "splash.appsStatusLive": "Live",
  "splash.appsStatusBeta": "Beta",
  "splash.appsStatusComingSoon": "Coming soon",
  "splash.appsPlatformWeb": "Web",
  "splash.appsPlatformApple": "iOS",
  "splash.appsPlatformAndroid": "Android",
  "splash.appsDoodlesDesc":
    "Turn a child's drawing into a colorful 3D-printed figurine, printed and shipped to your door. No modeling skills needed.",
  "splash.appsDoodlesCta": "Open Tinkr Doodles",
  "splash.appsMakerchipDesc":
    "Design a custom 3D-printable chip in your browser, then order it printed and shipped. No modeling skills needed.",
  "splash.appsMakerchipCta": "Open MakerChip",
  "splash.appsDecorDesc":
    "Browse and order printable home decor from your phone. A mobile app for everyday makers.",
  "splash.appsDecorCta": "Coming soon",
  // Partner application page (design "Tinkr Auth" A5)
  "api.title": "API access for approved channels.",
  "api.body":
    "Send production-ready orders into the Tinkr network from your storefront, app, or internal tools. Access is granted per company after a short review, not an instant public API.",
  "api.docsLink": "API docs",
  "api.loginWithAccess": "Login with access",
  "api.reviewSla": "Reviews typically complete within 3 business days.",
  "api.comingSoonChip": "Coming soon",
  "api.comingSoonNotice":
    "The partner API is coming soon. We're accepting sign-up requests now, and approved partners get access first at launch.",
  "api.lanePartnerJobs": "Partner jobs API",
  "api.lanePartnerJobsDesc":
    "Create jobs from locked quotes; we route them to matching providers.",
  "api.laneWebhookDelivery": "Webhook delivery",
  "api.laneWebhookDeliveryDesc":
    "Signed status events for every job: accepted, printing, shipped, delivered.",
  "api.laneSettlementEvidence": "Settlement evidence",
  "api.laneSettlementEvidenceDesc":
    "Itemized statements and payout records for reconciliation.",
  "api.laneProviderPlugins": "Slicer & plugin hooks",
  "api.laneProviderPluginsDesc":
    "Embed ordering in your own tools with prebuilt components.",
  "api.controlledLane":
    "A controlled integration lane: every partner is reviewed before the first job.",
  "api.explainerUseCaseTitle": "Who should apply",
  "api.explainerUseCaseBody":
    "Storefronts, marketplaces, and product teams that need printing fulfilled at scale.",
  "api.explainerAccessTitle": "What approval enables",
  "api.explainerAccessBody":
    "Live API keys, webhook endpoints, sandbox environment, and a named contact.",
  "api.explainerReviewTitle": "What we review",
  "api.explainerReviewBody":
    "Your use case, expected volume, and that the content you'll send is printable and permitted.",
  "api.alreadyApproved": "Already approved?",
  "auth.signupTitle": "Create your provider account",
  "auth.loginTitle": "Welcome back",
  // Teal brand-panel copy ("Tinkr Auth" split screen)
  "auth.panelLoginTitle1": "Your printers.",
  "auth.panelLoginTitle2": "The world's ideas.",
  "auth.panelLoginBody": "Sign in to your workshop: offers, jobs, and payouts in one place.",
  "auth.panelSignupTitle1": "Get more print jobs.",
  "auth.panelSignupTitle2": "Zero chasing.",
  "auth.panelSignupBody":
    "Set your machines, materials, and prices once. Matching jobs come straight to your queue with payouts handled.",
  "auth.panelTagline": "Ideas made real · tinkrapp.com",
  // Mode-switch link rows (replace the old segmented tabs)
  "auth.newToTinkr": "New to Tinkr?",
  "auth.createAccountLink": "Create an account",
  "auth.alreadyHaveAccount": "Already have one?",
  "auth.signInLink": "Sign in",
  "auth.forgotShort": "Forgot?",
  "auth.showPassword": "Show",
  "auth.hidePassword": "Hide",
  "auth.businessName": "Name or business name",
  "auth.businessNamePlaceholder": "Your name or registered business",
  "auth.accountCountry": "Account country",
  "auth.accountCountryPlaceholder": "Select account country",
  "auth.accountCountryHelp": "Choose the country where you or your business is based. It cannot be changed after signup.",
  "auth.countrySearch": "Search countries",
  "auth.countryNotOpen": "Not open yet",
  "auth.countryNoMatches": "No matching countries",
  "auth.countryAvailabilityHelp": "Only countries open for provider onboarding can be selected. Your location is a suggestion; confirm where you or your business is based.",
  "auth.countryNoneOpen": "Provider onboarding is not open in any country yet.",
  "auth.countryChecking": "Checking country availability…",
  "auth.countryCheckFailed": "We couldn’t check country availability. Refresh and try again.",
  "auth.countryUnavailable": "Provider signup is not available in this country yet. Please check back later.",
  "auth.accountCountryLocked": "This is the country recorded at signup and cannot be changed here. If it is incorrect, create a new account with your correct country. If your country is not supported yet, please check back later.",
  "auth.countryRequired": "Choose your account country before signup.",
  "auth.businessEmail": "Email",
  "auth.emailPlaceholder": "you@company.com",
  "auth.password": "Password",
  "auth.passwordPlaceholder": "At least 8 characters",
  "auth.passwordHelp": "Use a password you do not use on other services.",
  "auth.passwordTooShort": "Password must be at least 8 characters.",
  "auth.forgotPassword": "Forgot password?",
  "auth.dashboardResetLink": "Can't use your current password? Email me a reset link.",
  "auth.passwordResetTitle": "Reset your password.",
  "auth.passwordResetBody": "Enter your email and we will send a password reset link.",
  "auth.passwordResetSent": "Password reset email sent.",
  "auth.passwordResetLinkRequired": "Open the password reset link from your email before setting a new password.",
  "auth.passwordResetReadyBody": "Choose a new password for this account.",
  "auth.inviteSetupTitle": "Set your password.",
  "auth.inviteSetupReadyBody": "Your partner invite is confirmed. Choose a password before opening the dashboard.",
  "auth.inviteSetupLinkRequired": "Open the partner invite link from your email before setting a password.",
  "auth.setPassword": "Set password",
  "auth.invitePasswordUpdated": "Password set. Sign in with your new password to open the partner dashboard.",
  "auth.newPassword": "New password",
  "auth.confirmPassword": "Confirm password",
  "auth.confirmPasswordPlaceholder": "Repeat the new password",
  "auth.passwordMismatch": "Passwords do not match.",
  "auth.updatePassword": "Update password",
  "auth.passwordUpdated": "Password updated.",
  "auth.backToLogin": "Back to login",
  "auth.changePassword": "Change password",
  "auth.currentPassword": "Current password",
  "auth.currentPasswordPlaceholder": "Current password",
  "auth.changePasswordTitle": "Change your password",
  "auth.changePasswordBody": "Enter your current password, then choose a new one.",
  "auth.currentPasswordRequired": "Enter your current password.",
  "auth.passwordServerRequirement": "Password does not meet the current security requirements. Use a stronger password and try again.",
  "auth.invalidCredentials": "Email or password is incorrect.",
  "auth.rateLimited": "Too many attempts. Wait a moment and try again.",
  "auth.missingConfig": "Provider signup and login are not available in this preview yet.",
  "auth.checkEmailTitle": "Check your email",
  "auth.checkEmailBody": "Confirm your email address before opening the dashboard.",
  "auth.signupConfirmationBody": "We sent a confirmation link to the email above. Open it to confirm your account, then sign in.",
  "auth.verifyTitle": "Confirm your email to continue.",
  "auth.verifyBody": "We need a verified email before showing protected dashboard screens.",
  "auth.verifyBeforeDashboard": "Confirm your email before opening the dashboard.",
  "auth.resendVerification": "Resend verification email",
  "auth.verificationResent": "Verification email sent.",
  "auth.resolvingSession": "Checking your session…",
  "auth.createAccount": "Create account",
  "auth.signInWithPassword": "Sign in",
  "auth.loginSuccess": "Signed in. Opening your dashboard.",
  "auth.errorFallback": "Login request failed",
  "auth.sending": "Sending",
  "auth.sendSecureLink": "Send secure link",
  "auth.continueWithGoogle": "Continue with Google",
  "auth.continueWithApple": "Continue with Apple",
  "auth.or": "or",
  "auth.legalConsent": "I agree to the {terms} and the {privacy}.",
  "auth.legalConsentTerms": "Terms of Service",
  "auth.legalConsentPrivacy": "Privacy Policy",
  "auth.legalConsentRequired":
    "Agree to the Terms of Service and Privacy Policy before signup.",
  "auth.legalDraftUnavailable":
    "Provider signup is paused until the current Terms of Service and Privacy Policy are published.",
  "legalGate.eyebrow": "Policy update",
  "legalGate.title": "Review our updated terms.",
  "legalGate.body":
    "Before you continue to your dashboard, please review and accept the current versions of the documents below.",
  "legalGate.versionLabel": "Version {version}",
  "legalGate.effectiveLabel": "Effective {date}",
  "legalGate.readDocument": "Read the full document",
  "legalGate.checkbox": "I have read and agree to the documents listed above.",
  "legalGate.accept": "Agree and continue",
  "legalGate.acceptError": "We could not record your acceptance. Please try again.",
  "legalGate.checking": "Checking for policy updates…",
  "legalGate.unavailableEyebrow": "Agreements unavailable",
  "legalGate.unavailableTitle": "We cannot verify the current agreements.",
  "legalGate.unavailableBody":
    "Dashboard access is paused until the current terms and privacy policy can be verified.",
  "legalGate.retry": "Try again",
  "legalGate.signOut": "Sign out",
  "stitch.nav.account": "Account",
  "account.eyebrow": "Account",
  "account.title": "Account & data",
  "account.deletion.title": "Delete account",
  "account.deletion.body":
    "Deleting your account starts a 14-day grace period. During that time you can sign back in and cancel. After it ends, your profile is removed and your personal details are erased.",
  "account.deletion.retentionNote":
    "Some financial records and documents are retained after account deletion as described in the Privacy Policy.",
  "account.deletion.blockedJobs":
    "You still have accepted jobs in progress. Finish or cancel them before deleting your account.",
  "account.deletion.blockedPayouts":
    "You still have payouts on the way. Deletion becomes available once they complete.",
  "account.deletion.blockedPartner":
    "Your organization is still active. Contact support to offboard the organization first.",
  "account.deletion.confirmLabel":
    "I understand my account and personal data will be permanently removed.",
  "account.deletion.passwordLabel": "Confirm your password",
  "account.deletion.submit": "Delete my account",
  "account.deletion.pendingTitle": "Account deletion scheduled",
  "account.deletion.pendingBody":
    "Your account is scheduled for deletion on {date}. You can cancel any time before then.",
  "account.deletion.cancel": "Keep my account",
  "account.deletion.cancelled": "Deletion cancelled. Your account stays active.",
  "account.deletion.requested": "Deletion scheduled for {date}.",
  "account.deletion.error": "We could not process the request. Please try again.",
  "account.deletion.statusLoading": "Checking account status…",
  "account.export.title": "Download your data",
  "account.actions.title": "Account actions",
  "account.actions.body": "Send feedback, change your password or sign out from here.",
  "account.export.body":
    "Get a copy of your account details, order history, and payout records as a JSON file. Confirm your password to start the download.",
  "account.export.submit": "Download my data",
  "account.export.ready": "Your data file has been downloaded.",
  "account.export.error": "We could not prepare your data. Please try again.",
  "footer.contact": "Contact",
  "footer.registered": "Registered in England and Wales",
  "footer.companyNo": "Company No.",
  "footer.registeredOffice": "Registered office:",
  "footer.terms": "Terms of service",
  "footer.privacy": "Privacy policy",
  "footer.legalLinksLabel": "Legal",
  "footer.rights": "All rights reserved.",
  "consent.aria": "Cookie choices",
  "consent.body":
    "Can we use optional analytics cookies to understand how this site is used and make it better?",
  "consent.accept": "Yes, that's fine",
  "consent.decline": "No thanks",
  "help.title": "Need a hand?",
  "help.body": "Reach the operator team, browse docs, or report a bug.",
  "help.supportTitle": "Email support",
  "help.supportSubject": "Tinkr dashboard support",
  "help.docsTitle": "Documentation",
  "help.docsBody": "Setup, integration, and operational guides.",
  "help.bugTitle": "Report a bug",
  "help.bugBody": "Tell us what went wrong and how to reproduce it.",
  "help.bugSubject": "Tinkr dashboard bug report",
  "feedback.eyebrow": "Feedback",
  "feedback.title": "Send feedback",
  "feedback.openLabel": "Send feedback",
  "feedback.body": "Tell us what broke, what's confusing, or what you'd like to see. It goes straight to the team.",
  "feedback.category": "What kind of feedback?",
  "feedback.category.bug": "Something's broken",
  "feedback.category.feature": "Feature request",
  "feedback.category.general": "General feedback",
  "feedback.message": "Message",
  "feedback.messagePlaceholder": "What happened? What did you expect to happen?",
  "feedback.contextNote": "We'll include where you were in the app and your account type so we can investigate faster.",
  "feedback.submit": "Send feedback",
  "feedback.sending": "Sending",
  "feedback.success": "Thanks, we got it. We read every report.",
  "feedback.error": "Could not send your feedback. Try again in a moment, or email support instead.",
  "feedback.limitReached": "You've sent several messages today, and we'll read each one. You can send another tomorrow.",
  "feedback.cancel": "Cancel",
  "feedback.close": "Close",
  "account.reauthUnavailable": "We could not check your password right now. This is a problem on our side, not your password. Try again shortly.",
  "opsNotices.eyebrow": "Operational status",
  "opsNotices.title": "Network notices",
  "opsNotices.refresh": "Refresh operational notices",
  "opsNotices.errorTitle": "Notice refresh failed",
  "opsNotices.acknowledge": "Acknowledge",
  "opsNotices.ackSaving": "Acknowledging",
  "opsNotices.incidentTitle": "Platform incident",
  "opsNotices.maintenanceTitle": "Maintenance active",
  "opsNotices.degradedTitle": "Platform degraded",
  "opsNotices.normalTitle": "Platform notice",
  "opsNotices.writeGateBody": "Some writes are temporarily blocked while maintenance is active.",
  "opsNotices.statusBody": "Review the current platform status before starting new work.",
  "dashboard.providerEyebrow": "Provider dashboard",
  "dashboard.providerTitle": "Provider home",
  "dashboard.signOut": "Sign out",
  "dashboard.signingOut": "Signing out…",
  "dashboard.commandCenter": "Provider command center",
  "dashboard.nav.label": "Provider dashboard sections",
  "dashboard.nav.home": "Dashboard",
  "dashboard.nav.onboarding": "Onboarding",
  "dashboard.nav.operations": "Operations",
  "dashboard.nav.workQueue": "Work queue",
  "dashboard.nav.capabilities": "Capabilities",
  "dashboard.nav.rateCards": "Rate cards",
  "dashboard.nav.quoteSimulator": "Quote simulator",
  "dashboard.nav.pluginKeys": "Plugin keys",
  "dashboard.nav.adminGroup": "Admin controls",
  "dashboard.nav.adminAccess": "Access gate",
  "dashboard.nav.adminProviders": "Providers",
  "dashboard.nav.adminPartners": "Partners",
  "dashboard.nav.adminSettlement": "Settlement",
  "dashboard.nav.adminQuality": "Quality",
  "dashboard.nav.adminHealth": "Health",
  "access.defaultGateTitle": "Provider approval required before production work.",
  "access.networkLive": "Job routing",
  "access.hero.status": "Network status",
  "access.hero.application": "Application",
  "access.hero.payouts": "Payout readiness",
  "access.hero.business": "Business profile",
  "access.hero.networkPrivate": "Job routing paused",
  "access.hero.networkOpen": "Job routing open",
  "access.hero.noApplication": "Not submitted",
  "access.hero.noProvider": "Set up after approval",
  "access.hero.ready": "Ready",
  "access.hero.needsSetup": "Needs setup",
  "access.hero.titlePending": "Your provider application is waiting for review.",
  "access.hero.titleReady": "Set up your provider profile.",
  "access.hero.titleProvider": "Provider workspace",
  "access.hero.titleRejected": "Application needs attention.",
  "access.hero.titleDefault": "Start your provider application.",
  "access.hero.bodyPending": "We are checking the business details before opening production onboarding.",
  "access.hero.bodyReady": "Create your profile, connect payouts, add capabilities, and test pricing before production work opens.",
  "access.hero.bodyProvider": "Manage readiness, rate cards, work offers, payout status, and plugin keys from one provider control room.",
  "access.hero.bodyRejected": "Review the note from the operator team and contact support if the details changed.",
  "access.hero.bodyDefault": "Apply with your business details so we can approve you before production jobs are routed.",
  "access.apiProxyNotConfigured": "Dashboard API proxy is not configured for this environment.",
  "access.sessionExpired": "Your session expired. Sign in again to continue.",
  "access.signInToContinue": "Sign in to continue.",
  "access.noDashboardAccess": "This account does not have access to that dashboard area yet.",
  "access.roleConflictEyebrow": "Account role",
  "access.roleConflictTitle": "This account has conflicting roles.",
  "access.roleConflictBody": "Admin, provider, and partner access must use separate accounts. Contact support to fix the account role before continuing.",
  "access.requestFailed": "Request failed",
  "access.adminReauthRequired": "This admin action requires password confirmation. Retry the action and complete the password prompt.",
  "access.adminReauthEyebrow": "Admin reauth",
  "access.adminReauthTitle": "Confirm this admin action",
  "access.adminReauthBody": "Enter your current password to continue. The original action will retry after confirmation.",
  "access.adminReauthPassword": "Current password",
  "access.adminReauthPasswordPlaceholder": "Enter your current password",
  "access.adminReauthSubmit": "Confirm and retry",
  "access.adminReauthVerifying": "Verifying",
  "access.gate.notSubmitted.title": "Provider application not submitted",
  "access.gate.notSubmitted.body": "Apply with your business details before onboarding opens.",
  "access.gate.pending.title": "Application pending admin approval",
  "access.gate.pending.body": "We will review the business before enabling production onboarding.",
  "access.gate.awaitingLaunch.title": "Approved, continue setup",
  "access.gate.awaitingLaunch.body": "Your initial application is approved. Finish your profile, identity verification, application review, agreement, capabilities, and service area so Tinkr can activate your account for jobs.",
  "access.gate.ready.title": "Ready for provider onboarding",
  "access.gate.ready.body": "Continue to profile setup, identity verification, application review, capabilities, and service area. Tinkr activates your account separately before you can receive jobs.",
  "access.gate.providerCreated.title": "Provider profile created",
  "access.gate.providerCreated.body": "Manage readiness, rate cards, offers, and payout setup from the provider dashboard.",
  "access.gate.rejected.title": "Application not approved",
  "access.gate.rejected.body": "Review the admin note and contact support if your business details have changed.",
  "access.provider.eyebrow": "Provider access",
  "access.provider.title": "Application gate",
  "access.provider.refresh": "Refresh provider application status",
  "access.provider.loadingTitle": "Loading your dashboard",
  "access.provider.loadingBody": "Loading your workspace…",
  "access.dashboardLoading": "Loading dashboard panels…",
  "dashboard.refresh": "Refresh",
  "dashboard.refreshing": "Refreshing…",
  "access.summary.business": "Business",
  "access.summary.status": "Status",
  "access.summary.contact": "Contact",
  "access.summary.locale": "Locale",
  "access.form.businessName": "Business name",
  "access.form.businessEmail": "Business email",
  "access.form.businessWebsite": "Business website",
  "access.form.businessWebsitePlaceholder": "https://yourshop.com",
  "access.form.submitting": "Submitting",
  "access.form.submitApplication": "Submit provider application",
  "access.form.continueOnboarding": "Continue to onboarding",
  "adminProductionReview.loading": "Loading production applications",
  "adminProductionReview.refresh": "Refresh production applications",
  "adminProductionReview.filterStatus": "Status",
  "adminProductionReview.empty": "No production applications in this view.",
  "adminProductionReview.reasonRequired": "A reason of at least 3 characters is required.",
  "adminProductionReview.cancel": "Cancel",
  "adminProductionReview.saving": "Saving…",
  "adminProductionReview.confirm": "Confirm",
  "adminProductionReview.approve": "Approve production",
  "adminProductionReview.reject": "Reject",
  "adminProductionReview.requestChanges": "Request changes",
  "adminProductionReview.status.pending": "Pending",
  "adminProductionReview.status.approved": "Approved",
  "adminProductionReview.status.rejected": "Rejected",
  "adminProductionReview.status.changes_requested": "Changes requested",
  "adminProductionReview.status.all": "All",
  "adminProductionReview.done.approve_production": "Production approved. Partner access stage is now {accessStage}.",
  "adminProductionReview.done.reject_production": "Production application rejected. Sandbox access is unchanged.",
  "adminProductionReview.done.request_production_changes": "Changes requested. The partner can resubmit.",
  "adminProductionReview.drawer.approve_production": "Approve production access",
  "adminProductionReview.drawer.reject_production": "Reject production application",
  "adminProductionReview.drawer.request_production_changes": "Request changes",
  "adminProductionReview.field.useCase": "Use case",
  "adminProductionReview.field.volume": "Expected monthly orders",
  "adminProductionReview.field.support": "Support contact",
  "adminProductionReview.field.payment": "Payment / refund flow",
  "adminProductionReview.field.handling": "Customer handling",
  "adminProductionReview.field.appUrl": "App URL",
  "adminProductionReview.field.countries": "Target countries",
  "adminProductionReview.field.decision": "Decision reason",
  "adminProductionReview.field.reason": "Reason (audit-logged)",
  "adminProductionReview.handling.uploads": "Customers upload files",
  "adminProductionReview.handling.ai": "AI-generated models",
  "adminProductionReview.handling.none": "None specified",
  "adminPartnerApplications.load": "Load applications",
  "adminPartnerApplications.loading": "Loading applications",
  "adminPartnerApplications.empty": "No applications match this filter.",
  "adminPartnerApplications.applicationId": "Application ID",
  "adminPartnerApplications.filterStatus": "Status",
  "adminPartnerApplications.status.pending": "Pending",
  "adminPartnerApplications.status.approved": "Approved",
  "adminPartnerApplications.status.rejected": "Rejected",
  "adminPartnerApplications.status.withdrawn": "Withdrawn",
  "adminPartnerApplications.status.all": "All",
  "adminPartnerApplications.field.contactEmail": "Contact email",
  "adminPartnerApplications.field.contactName": "Contact name",
  "adminPartnerApplications.field.intendedUseCase": "Intended use case",
  "adminPartnerApplications.field.expectedMonthlyVolume": "Expected monthly volume",
  "adminPartnerApplications.field.integrationPlan": "Integration plan",
  "adminPartnerApplications.field.proposedTier": "Proposed tier",
  "adminPartnerApplications.field.rateLimitTier": "Approved rate-limit tier",
  "adminPartnerApplications.field.submittedAt": "Submitted",
  "adminPartnerApplications.field.reviewedAt": "Reviewed",
  "adminPartnerApplications.field.decisionReason": "Decision reason",
  "adminPartnerApplications.field.partnerId": "Partner ID",
  "adminPartnerApplications.field.reason": "Reason (audit log)",
  "adminPartnerApplications.approve": "Approve",
  "adminPartnerApplications.reject": "Reject",
  "adminPartnerApplications.confirmApprove": "Confirm approval",
  "adminPartnerApplications.confirmReject": "Confirm rejection",
  "adminPartnerApplications.cancel": "Cancel",
  "adminPartnerApplications.saving": "Saving",
  "adminPartnerApplications.approved": "Approved — invite sent to {email}; partner {partnerId} provisioned.",
  "adminPartnerApplications.rejected": "Application rejected.",
  "adminPartnerApplications.reasonRequired": "Provide a reason (minimum 3 characters).",
  "adminPartnerApplications.error.notFound": "Application not found. It may have been withdrawn already.",
  "adminPartnerApplications.gate.loading": "Loading intake status",
  "adminPartnerApplications.gate.title": "Public application form",
  "adminPartnerApplications.gate.open": "On",
  "adminPartnerApplications.gate.closed": "Off",
  "adminPartnerApplications.gate.body":
    "When off, the public application form refuses new requests and applicants see that applications are closed. Requests already in the queue stay, and you can still approve or reject them. Turn it off during spam waves.",
  "adminPartnerApplications.gate.pause": "Turn off",
  "adminPartnerApplications.gate.resume": "Turn on",
  "adminPartnerApplications.gate.saving": "Saving intake status",
  "adminPartnerApplications.gate.paused": "Public application form is now Off. New requests are refused.",
  "adminPartnerApplications.gate.resumed": "Public application form is now On. New requests are accepted.",
  "adminProviders.eyebrow": "Admin operations",
  "adminProviders.jobId": "Job ID",
  "adminProviders.jobLookup": "Resolve job → provider",
  "adminProviders.jobLookupLoading": "Resolving…",
  "adminProviders.jobLookupFound":
    "Job is currently in state \"{state}\". Provider {provider} pre-filled below — click Lookup provider to drill in.",
  "adminProviders.jobNoProvider": "is not yet assigned",
  "adminProviders.title": "Provider state controls",
  "adminProviders.subtitle": "Look up a provider or load the queue, then run audited state transitions. Destructive actions require a recent admin reauth.",
  "adminProviders.awaiting.title": "Awaiting activation",
  "adminProviders.awaiting.subtitle": "Providers who submitted for review but aren't active yet. Submitted oldest-first.",
  "adminProviders.awaiting.load": "Load awaiting activation",
  "adminProviders.awaiting.loading": "Loading awaiting activation queue",
  "adminProviders.awaiting.empty": "No providers are awaiting activation right now.",
  "adminProviders.awaiting.error": "Could not load the awaiting activation queue. Try again.",
  "adminProviders.awaiting.setupReady": "Setup ready",
  "adminProviders.awaiting.setupPending": "Setup incomplete",
  "adminProviders.awaiting.businessReview.updated": "Application review saved for {provider}.",
  "adminProviders.awaiting.businessReview.open": "Review application",
  "adminProviders.awaiting.businessReview.updating": "Updating review…",
  "adminProviders.awaiting.businessReview.validation": "Choose a decision and enter an internal audit note.",
  "adminProviders.awaiting.businessReview.close": "Cancel application review",
  "adminProviders.awaiting.businessReview.cancel": "Cancel",
  "adminProviders.awaiting.businessReview.saving": "Saving decision…",
  "adminProviders.awaiting.businessReview.save": "Save application review",
  "adminProviders.awaiting.businessReview.statusPrefix": "Current status:",
  "adminProviders.awaiting.businessReview.statusBody": "This audited decision is required before provider activation and is independent from payout setup.",
  "adminProviders.awaiting.businessReview.title": "Review application: {provider}",
  "adminProviders.awaiting.businessReview.decisionLabel": "Decision",
  "adminProviders.awaiting.businessReview.decisionPlaceholder": "Choose a decision",
  "adminProviders.awaiting.businessReview.noteLabel": "Internal audit note",
  "adminProviders.awaiting.businessReview.notePlaceholder": "Record the evidence reviewed and the reason for this decision",
  "adminProviders.awaiting.businessReview.decision.pending": "Mark pending",
  "adminProviders.awaiting.businessReview.decision.approved": "Approve business",
  "adminProviders.awaiting.businessReview.decision.rejected": "Reject business",
  "adminProviders.awaiting.applicationReview.decision.approved": "Approve application review",
  "adminProviders.awaiting.applicationReview.decision.rejected": "Reject application review",
  "adminProviders.awaiting.businessReview.decision.moreInformation": "Request more information",
  "adminProviders.awaiting.activationBlocked.lifecycle": "This provider is no longer awaiting initial approval. Refresh the queue and use provider operations for lifecycle changes.",
  "adminProviders.awaiting.activationBlocked.complete": "Complete before activation: {gates}.",
  "adminProviders.awaiting.activationBlocked.identity": "identity verification",
  "adminProviders.awaiting.activationBlocked.agreement": "current provider agreement",
  "adminProviders.awaiting.activationBlocked.serviceArea": "service area",
  "adminProviders.awaiting.activationBlocked.fulfillment": "fulfillment method",
  "adminProviders.awaiting.activationBlocked.shippingCarrier": "shipping carrier",
  "adminProviders.awaiting.activationBlocked.capability": "active capability",
  "adminProviders.awaiting.activationBlocked.rateCard": "active rate card",
  "adminProviders.awaiting.activationBlocked.shippingRate": "active shipping rate",
  "adminProviders.awaiting.notReady": "Provider needs verified payouts, service area, fulfillment method, a saved capability, an active rate card, and a shipping lane before activation.",
  "adminProviders.awaiting.searchPlaceholder": "Search by name or ID",
  "adminProviders.awaiting.clearSearch": "Clear search",
  "adminProviders.awaiting.filterCountry": "Country",
  "adminProviders.awaiting.filterCountryAll": "All countries",
  "adminProviders.awaiting.filterBlocked": "Blocker",
  "adminProviders.awaiting.filterBlockedAll": "All providers",
  "adminProviders.awaiting.filterBlockedReady": "Ready to approve",
  "adminProviders.awaiting.filterBlockedReview": "Blocked on provider review",
  "adminProviders.awaiting.filterBlockedSetup": "Blocked on setup",
  "adminProviders.awaiting.resultCount": "Showing {visible} of {total}",
  "adminProviders.awaiting.cappedNotice": "list capped at the latest 100",
  "adminProviders.awaiting.clearFilters": "Clear filters",
  "adminProviders.awaiting.emptyFiltered": "No providers match the current filters.",
  "adminProviders.awaiting.expandLabel": "Expand provider details",
  "adminProviders.awaiting.collapseLabel": "Collapse provider details",
  "adminProviders.awaiting.column.expand": "Expand",
  "adminProviders.awaiting.column.provider": "Provider",
  "adminProviders.awaiting.column.country": "Country",
  "adminProviders.awaiting.column.submitted": "Submitted",
  "adminProviders.awaiting.column.setup": "Setup",
  "adminProviders.awaiting.column.status": "Status",
  "adminProviders.awaiting.column.action": "Action",
  "adminProviders.awaiting.count.capabilities": "printers",
  "adminProviders.awaiting.count.rateCards": "rate cards",
  "adminProviders.awaiting.count.shippingRates": "lanes",
  "adminProviders.awaiting.relative.justNow": "just now",
  "adminProviders.awaiting.relative.minutesAgo": "{n}m ago",
  "adminProviders.awaiting.relative.hoursAgo": "{n}h ago",
  "adminProviders.awaiting.relative.daysAgo": "{n}d ago",
  "adminProviders.awaiting.detail.providerId": "Provider ID",
  "adminProviders.awaiting.detail.copyId": "Copy ID",
  "adminProviders.awaiting.detail.copied": "Copied",
  "adminProviders.awaiting.detail.submittedAt": "Submitted",
  "adminProviders.awaiting.detail.country": "Tax / account country",
  "adminProviders.awaiting.detail.serviceArea": "Service area",
  "adminProviders.awaiting.detail.serviceAreaPresent": "Set",
  "adminProviders.awaiting.detail.noServiceArea": "No service area set",
  "adminProviders.awaiting.detail.fulfillment": "Fulfillment methods",
  "adminProviders.awaiting.detail.fulfillmentPresent": "Set",
  "adminProviders.awaiting.detail.noFulfillment": "No fulfillment method set",
  "adminProviders.awaiting.detail.carriers": "Shipping carriers",
  "adminProviders.awaiting.detail.carriersPresent": "Set",
  "adminProviders.awaiting.detail.noCarriers": "No shipping carriers set",
  "adminProviders.awaiting.detail.setupCounts": "Setup counts",
  "adminProviders.awaiting.detail.capabilitiesCount": "{count} active capabilities",
  "adminProviders.awaiting.detail.rateCardsCount": "{count} active rate cards",
  "adminProviders.awaiting.detail.shippingRatesCount": "{count} active shipping lanes",
  "adminProviders.awaiting.rejected": "{provider} was sent back with feedback.",
  "adminProviders.awaiting.rejectReason": "Provider setup needs changes; sent back via admin reject.",
  "adminProviders.awaiting.reject.open": "Reject",
  "adminProviders.awaiting.reject.title": "Send {provider} back with feedback",
  "adminProviders.awaiting.reject.body": "The provider stays on file and goes back to the wizard. They'll see your reason in their language and can edit and resubmit.",
  "adminProviders.awaiting.reject.reasonLabel": "Reason",
  "adminProviders.awaiting.reject.reasonPlaceholder": "Pick a reason",
  "adminProviders.awaiting.reject.reasonRequired": "Pick a reason before sending back.",
  "adminProviders.awaiting.reject.noteLabel": "Note to the provider (optional)",
  "adminProviders.awaiting.reject.notePlaceholder": "Specific things to change, links to docs, etc.",
  "adminProviders.awaiting.reject.noteHelp": "Shown verbatim in your original language under a 'Note from admin' header.",
  "adminProviders.awaiting.reject.cancel": "Cancel",
  "adminProviders.awaiting.reject.submit": "Send back",
  "adminProviders.awaiting.reject.submitting": "Sending",
  "adminProviders.awaiting.reject.code.businessDocumentsUnclear": "Business documents are unclear or unverifiable",
  "adminProviders.awaiting.reject.code.payoutCountryUnsupported": "Country isn't supported by our payout rail yet",
  "adminProviders.awaiting.reject.code.serviceAreaMisaligned": "Service area doesn't match your operations",
  "adminProviders.awaiting.reject.code.capabilitySpecsUnverifiable": "Listed printer capabilities couldn't be verified",
  "adminProviders.awaiting.reject.code.pricingUnrealistic": "Rate-card pricing is outside reasonable range",
  "adminProviders.awaiting.reject.code.portfolioInsufficient": "Portfolio photos insufficient to verify work quality or authenticity",
  "adminProviders.awaiting.reject.code.otherSeeMessage": "Other — see admin note",
  "adminProviders.gate.businessVerification": "provider verification is not complete",
  "adminProviders.gate.identityVerification": "identity verification is not complete",
  "adminProviders.gate.businessReview": "application review is not approved",
  "adminProviders.gate.providerAgreement": "provider agreement is not accepted",
  "adminProviders.gate.payoutSetup": "payout method is not configured",
  "adminProviders.gate.providerApproval": "provider approval is incomplete",
  "adminProviders.gate.reviewSubmission": "provider has not submitted for review",
  "adminProviders.gate.adminReview": "provider has not been marked verified by admin",
  "adminProviders.gate.serviceArea": "service area is incomplete",
  "adminProviders.gate.fulfillmentMethods": "fulfillment methods are missing",
  "adminProviders.gate.shippingCarriers": "shipping carriers are missing",
  "adminProviders.gate.capability": "no active printer capability",
  "adminProviders.gate.rateCard": "no active rate card with material pricing",
  "adminProviders.gate.shippingRate": "no active shipping lane",
  "adminProviders.gate.portfolioEvidence": "portfolio evidence incomplete",
  "adminProviders.refresh": "Refresh admin provider details",
  "adminProviders.providerId": "Provider ID",
  "adminProviders.lookup": "Lookup provider",
  "adminProviders.loading": "Loading",
  "adminProviders.empty": "Lookup a provider to review readiness and run audited state changes.",
  "adminProviders.filterOperational": "Operational filter",
  "adminProviders.filterVerification": "Identity verification filter",
  "adminProviders.filterBusiness": "Application review filter",
  "adminProviders.filterPayoutSetup": "Payout setup filter",
  "adminProviders.filterAny": "Any",
  "adminProviders.loadQueue": "Load provider queue",
  "adminProviders.queueEmpty": "No providers loaded yet. Choose filters and load the queue.",
  "adminProviders.queueCapped":
    "Showing a full page of 25 matches — additional providers may exist. Narrow the filters to check.",
  "adminProviders.legalName": "Legal name",
  "adminProviders.displayName": "Display name",
  "adminProviders.operationalStatus": "Operational status",
  "adminProviders.businessVerification": "Application review",
  "adminProviders.capabilities": "Capabilities",
  "adminProviders.rateCards": "Rate cards",
  "adminProviders.command": "Admin action",
  "adminProviders.reason": "Audit reason",
  "adminProviders.defaultReason": "Provider state reviewed by admin.",
  "adminProviders.reasonEditRequired":
    "Destructive actions need a case-specific reason — replace the default text before submitting.",
  // Premium print provider (ADR premium-print-provider). Admin chrome: English only.
  "adminProviders.premium.checkbox": "Also mark as quality verified print provider",
  "adminProviders.premium.title": "Quality verification",
  "adminProviders.premium.subtitle": "Quality verified print providers can take Doodles and other premium orders. This is separate from account approval, and all normal eligibility checks still apply.",
  "adminProviders.premium.approved": "Quality verified print provider",
  "adminProviders.premium.approvedSince": "Quality verified print provider since {date}",
  "adminProviders.premium.notApproved": "Not quality verified",
  "adminProviders.premium.businessDefault": "Business accounts are eligible for Doodles and premium orders without a separate quality verification.",
  "adminProviders.premium.approve": "Approve as quality verified",
  "adminProviders.premium.revoke": "Revoke quality verification",
  "adminProviders.premium.hint": "Only approve after reviewing print quality. This can be changed later.",
  "admin.premiumReviews.title": "Quality verification requests",
  "admin.premiumReviews.subtitle": "Individual providers request quality verification with photos of prints they have made. Approving lets them take Doodles and other premium orders.",
  "admin.premiumReviews.filter.pending": "Pending",
  "admin.premiumReviews.filter.all": "All",
  "admin.premiumReviews.refresh": "Refresh requests",
  "admin.premiumReviews.loading": "Loading quality verification requests…",
  "admin.premiumReviews.loadFailed": "Could not load quality verification requests.",
  "admin.premiumReviews.emptyPending": "No pending quality verification requests.",
  "admin.premiumReviews.emptyAll": "No quality verification requests yet.",
  "admin.premiumReviews.loadMore": "Load more",
  "admin.premiumReviews.col.provider": "Provider",
  "admin.premiumReviews.col.country": "Country",
  "admin.premiumReviews.col.status": "Status",
  "admin.premiumReviews.col.photos": "Photos",
  "admin.premiumReviews.col.submitted": "Submitted",
  "admin.premiumReviews.status.draft": "Draft",
  "admin.premiumReviews.status.pending": "Pending",
  "admin.premiumReviews.status.approved": "Approved",
  "admin.premiumReviews.status.declined": "Declined",
  "admin.premiumReviews.status.withdrawn": "Withdrawn",
  "admin.premiumReviews.detail.title": "Quality verification request",
  "admin.premiumReviews.detail.submitted": "Submitted {date}",
  "admin.premiumReviews.detail.notSubmitted": "Not submitted yet",
  "admin.premiumReviews.detail.loading": "Loading quality verification request…",
  "admin.premiumReviews.detail.loadFailed": "Could not load this quality verification request.",
  "admin.premiumReviews.detail.refresh": "Refresh evidence",
  "admin.premiumReviews.detail.openDirectory": "Open in provider directory",
  "admin.premiumReviews.detail.note": "Provider note",
  "admin.premiumReviews.detail.noNote": "The provider did not add a note.",
  "admin.premiumReviews.detail.photos": "Review photos",
  "admin.premiumReviews.detail.photosHelp": "Prints the provider made, inside or outside Tinkr. Photo links expire after 15 minutes; refresh the evidence to renew them.",
  "admin.premiumReviews.detail.noPhotos": "No photos attached.",
  "admin.premiumReviews.detail.photoLabel": "Review photo {index}",
  "admin.premiumReviews.detail.photoUnavailable": "Image unavailable. Refresh evidence to renew the link.",
  "admin.premiumReviews.detail.photoNoPreview": "This browser cannot preview {type} photos.",
  "admin.premiumReviews.detail.photoOpen": "Open original",
  "admin.premiumReviews.detail.provider": "Provider",
  "admin.premiumReviews.detail.field.name": "Display name",
  "admin.premiumReviews.detail.field.entityType": "Entity type",
  "admin.premiumReviews.detail.field.country": "Country",
  "admin.premiumReviews.detail.field.status": "Account status",
  "admin.premiumReviews.detail.field.premium": "Quality verification",
  "admin.premiumReviews.detail.field.joined": "Joined",
  "admin.premiumReviews.detail.field.showcase": "Website or showcase",
  "admin.premiumReviews.detail.entity.individual": "Individual",
  "admin.premiumReviews.detail.entity.business": "Registered business",
  "admin.premiumReviews.detail.notRecorded": "Not recorded",
  "admin.premiumReviews.detail.showcaseOpen": "Open website / showcase",
  "admin.premiumReviews.detail.trackRecord": "Tinkr track record",
  "admin.premiumReviews.detail.metric.completedOrders": "Completed orders",
  "admin.premiumReviews.detail.metric.completedNonDoodles": "Completed non-Doodles orders",
  "admin.premiumReviews.detail.metric.strikes": "Fulfillment strikes",
  "admin.premiumReviews.detail.metric.qualityIncidents": "Quality incidents",
  "admin.premiumReviews.detail.recentJobs": "Recent completed jobs (excluding Doodles)",
  "admin.premiumReviews.detail.recentJobsEmpty": "No completed non-Doodles jobs yet.",
  "admin.premiumReviews.detail.col.id": "Job ID",
  "admin.premiumReviews.detail.col.state": "State",
  "admin.premiumReviews.detail.col.channel": "Channel",
  "admin.premiumReviews.detail.col.completed": "Completed",
  "admin.premiumReviews.detail.portfolio": "Onboarding portfolio",
  "admin.premiumReviews.detail.decision": "Decision",
  "admin.premiumReviews.detail.decidedOn": "{status} on {date}",
  "admin.premiumReviews.detail.notPending": "Only pending requests can be decided.",
  "admin.premiumReviews.detail.reasonLabel": "Reason (audited)",
  "admin.premiumReviews.detail.reasonHint": "3–1000 characters. A decline reason is shown to the provider.",
  "admin.premiumReviews.action.approve": "Approve as quality verified",
  "admin.premiumReviews.action.decline": "Decline request",
  "admin.premiumReviews.action.close": "Close",
  "admin.premiumReviews.feedback.approved": "{provider} is now a quality verified print provider.",
  "admin.premiumReviews.feedback.declined": "Quality verification request from {provider} declined.",
  "admin.premiumReviews.error.invalidState": "This request is no longer pending. Refresh to see its current status.",
  "admin.premiumReviews.error.notFound": "This quality verification request no longer exists.",
  "admin.premiumReviews.error.decisionFailed": "The decision could not be saved. Check your admin reauthentication and try again.",
  "admin.premiumConfig.title": "Premium uplift",
  "admin.premiumConfig.body": "Added to quoted premium orders as a separate premium service line, calculated on the item subtotal (print plus platform service, excluding shipping). Fixed-price orders such as Doodles never add it. The whole uplift is paid to the provider who fulfils the order, as part of their payout; Tinkr keeps none of it and its fees are unchanged.",
  "admin.premiumConfig.current": "{percent} of the item subtotal ({bps} basis points)",
  "admin.premiumConfig.unavailable": "Premium uplift configuration is unavailable.",
  "admin.premiumConfig.updatedAt": "Last changed {date}",
  "admin.premiumConfig.note": "Each premium order keeps the uplift that applied when it was created. Changing it does not reprice existing orders.",
  "admin.premiumConfig.edit": "Edit premium uplift",
  "admin.premiumConfig.refresh": "Refresh premium uplift",
  "admin.premiumConfig.drawerTitle": "Edit premium uplift",
  "admin.premiumConfig.drawerSubtitle": "Fresh admin reauthentication and an audit reason are required.",
  "admin.premiumConfig.field.bps": "Premium uplift (basis points)",
  "admin.premiumConfig.field.bpsHint": "100 basis points = 1%. Allowed: 0–5,000 (0% to 50%).",
  "admin.premiumConfig.field.preview": "= {percent} of the item subtotal",
  "admin.premiumConfig.field.reason": "Reason (audited)",
  "admin.premiumConfig.cancel": "Cancel",
  "admin.premiumConfig.save": "Save premium uplift",
  "admin.premiumConfig.saved": "Premium uplift saved and audited.",
  "admin.premiumConfig.saveFailed": "Premium uplift could not be saved.",
  "adminProviders.action.approve": "Approve provider",
  "adminProviders.action.activate": "Activate provider",
  "adminProviders.action.deactivate": "Deactivate provider",
  "adminProviders.action.suspend": "Suspend provider",
  "adminProviders.beforeAfter": "Current: operational {fromOperational}, verification {fromVerification}. Action: {command}.",
  "adminProviders.confirmDanger": "I understand this can block the provider from receiving production work.",
  "adminProviders.applyTransition": "Apply provider transition",
  "adminProviders.saving": "Applying transition",
  "adminProviders.transitionSaved": "Provider transition applied. Operational: {status}. Review states refresh separately.",
  "adminProviders.transitionRejected": "Transition rejected: {reason}.",
  "adminProviders.bulk.selected": "Select provider for bulk action",
  "adminProviders.bulk.selectAll": "Select all loaded providers",
  "adminProviders.bulk.clear": "Clear selection",
  "adminProviders.bulk.tray": "Selected providers ({count})",
  "adminProviders.bulk.apply": "Apply to {count} providers",
  "adminProviders.bulk.applying": "Applying to {count} providers",
  "adminProviders.bulk.resultPending": "Pending",
  "adminProviders.bulk.resultSuccess": "Success",
  "adminProviders.bulk.resultFailed": "Failed: {reason}",
  "adminProviders.bulk.remove": "Remove from selection",
  "adminPartners.eyebrow": "Partner operations",
  "adminPartners.title": "Partner/API controls",
  "adminPartners.subtitle": "Look up a partner or load the queue to review API access, settlement, and webhook controls. Destructive actions require a recent admin reauth.",
  "adminPartners.refresh": "Refresh admin partner details",
  "adminPartners.noSession": "Sign in with admin access to manage partner controls.",
  "adminPartners.partnerId": "Partner ID",
  "adminPartners.lookup": "Lookup partner",
  "adminPartners.loading": "Loading",
  "adminPartners.empty": "Lookup a partner to review API access, settlement controls, and support rows.",
  "adminPartners.filterStatus": "Status filter",
  "adminPartners.filterTier": "Tier filter",
  "adminPartners.filterSettlement": "Settlement filter",
  "adminPartners.filterAny": "Any",
  "adminPartners.loadQueue": "Load partner queue",
  "adminPartners.queueEmpty": "No partners loaded yet. Choose filters and load the queue.",
  "adminPartners.tierCount": "{count} tiers",
  "adminPartners.legalName": "Legal name",
  "adminPartners.displayName": "Display name",
  "adminPartners.status": "Status",
  "adminPartners.settlementMode": "Settlement mode",
  "adminPartners.settlementMode.legacy": "Legacy: {value} (select a replacement)",
  "adminPartners.settlementMode.legacyHelp":
    "This partner is stored with a settlement mode this build no longer offers. Nothing has been changed for you. Pick a replacement mode to save.",
  "adminPartners.creditLimit": "Credit limit",
  "adminPartners.noLimit": "No limit",
  "adminPartners.reserve": "Reserve requirement",
  "adminPartners.payoutHold": "Payout hold policy",
  "adminPartners.apiKeys": "API keys",
  "adminPartners.settlementPolicies": "Settlement policies",
  "adminPartners.referralFees": "Referral fees",
  "adminPartners.webhooks": "Webhooks",
  "adminPartners.auditRows": "Audit rows",
  "adminPartners.enabled": "Enabled",
  "adminPartners.disabled": "Disabled",
  "adminPartners.webhookSubscriptionsTitle": "Webhook subscriptions",
  "adminPartners.webhookSubscriptionsBody": "Review subscribed endpoints, enabled event families, delivery attempts, and last delivery time before enabling a partner integration.",
  "adminPartners.noWebhookSubscriptions": "No webhook subscriptions configured for this partner.",
  "adminPartners.noLastDelivery": "No deliveries yet",
  "adminPartners.attempts": "{count} attempts",
  "adminPartners.webhookFamilies": "{enabled} of {available} webhook families enabled by partner flow flags.",
  "adminPartners.quoteDisplayQaTitle": "Quote-display QA",
  "adminPartners.quoteDisplayQaReady": "Flag enabled",
  "adminPartners.quoteDisplayQaBlocked": "Flag disabled",
  "adminPartners.quoteDisplayQaBody": "Partner may show Tinkr quote totals once their client has passed quote-display QA and support/refund ownership is clear.",
  "adminPartners.quoteDisplayQaDisabled": "Keep external quote display disabled until partner UI review confirms totals, shipping warnings, and customer-facing ownership are prominent.",
  "adminPartners.settlementPoliciesTitle": "Settlement policies",
  "adminPartners.settlementPoliciesBody": "Per-key settlement policy rows attached to this partner's API keys.",
  "adminPartners.noSettlementPolicies": "No settlement policies returned.",
  "adminPartners.policyAnyCurrency": "Agreement gated",
  "adminPartners.command": "Admin action",
  "adminPartners.reason": "Audit reason",
  "adminPartners.defaultReason": "Partner controls reviewed by admin.",
  "adminPartners.action.approve": "Approve partner",
  "adminPartners.action.updateControls": "Update partner controls",
  "adminPartners.action.reactivate": "Reactivate partner",
  "adminPartners.action.suspend": "Suspend partner",
  "adminPartners.action.terminate": "Terminate partner",
  "adminPartners.controls": "Capability and settlement controls",
  "adminPartners.section.capabilities": "Capabilities",
  "adminPartners.section.settlement": "Settlement and ownership",
  "adminPartners.enabledTiers": "Enabled tiers",
  "adminPartners.enabledMessaging": "Messaging modes",
  "adminPartners.enabledSettlement": "Settlement modes",
  "adminPartners.enabledFlows": "Flow flags",
  "adminPartners.paymentMethods": "Payment method IDs",
  "adminPartners.creditLimitMinor": "Credit limit amount",
  "adminPartners.reserveRequirementMinor": "Reserve requirement amount",
  "adminPartners.refundOwner": "Refund owner",
  "adminPartners.chargebackOwner": "Chargeback owner",
  "adminPartners.customerSupportOwner": "Customer support owner",
  "adminPartners.beforeAfter": "Current: status {fromStatus}, settlement {fromSettlement}. Action: {command}.",
  "adminPartners.confirmDanger": "I understand this can block or permanently terminate partner access.",
  "adminPartners.applyTransition": "Apply partner transition",
  "adminPartners.saving": "Applying transition",
  "adminPartners.transitionSaved": "Partner transition applied. Status: {status}. Settlement: {settlementMode}.",
  "adminPartners.transitionRejected": "Transition rejected: {reason}.",
  "adminPartners.webhookManagerTitle": "Webhook management",
  "adminPartners.webhookCount": "{count} subscriptions",
  "adminPartners.webhookEndpoint": "Endpoint URL",
  "adminPartners.webhookEvents": "Subscribed events",
  "adminPartners.webhookEnabled": "Enable delivery",
  "adminPartners.webhookSecretName": "Signing secret binding",
  "adminPartners.webhookMaxAttempts": "Max attempts",
  "adminPartners.webhookCreate": "Create webhook",
  "adminPartners.webhookUpdate": "Update webhook",
  "adminPartners.webhookDelete": "Delete webhook",
  "adminPartners.webhookNew": "New webhook",
  "adminPartners.webhookSaved": "Webhook subscription saved.",
  "adminPartners.webhookDeleted": "Webhook subscription deleted.",
  "adminPartners.confirmWebhookDelete": "I understand this deletes the selected webhook subscription.",
  "adminPartners.quoteQaManagerTitle": "Quote-display QA controls",
  "adminPartners.quoteQaDefaultReason": "Partner quote-display QA reviewed by admin.",
  "adminPartners.quoteQaApiTotal": "Partner UI displays the API total and currency as source of truth.",
  "adminPartners.quoteQaLineItems": "Customer-visible line items preserve included/excluded cost distinctions.",
  "adminPartners.quoteQaImportWarning": "Import-cost and DAP buyer-responsibility warnings are prominent.",
  "adminPartners.quoteQaNoRecalculation": "Partner attests it does not recalculate totals from mutable fields.",
  "adminPartners.quoteQaEvidence": "Evidence URL",
  "adminPartners.quoteQaNotes": "QA notes",
  "adminPartners.quoteQaSave": "Save QA",
  "adminPartners.quoteQaApprove": "Approve QA",
  "adminPartners.quoteQaReject": "Reject QA",
  "adminPartners.quoteQaRevoke": "Revoke QA",
  "adminPartners.quoteQaSaved": "Quote-display QA saved.",
  "adminPartners.confirmQuoteQaRevoke": "I understand this revokes external quote display for this partner.",
  "adminPartners.quoteQaReviewedAt": "Last reviewed {date}.",
  // ADR-P10 §5: audited admin override of the partner's provider entity
  // preference. English-only admin surface.
  "adminPartners.entityPreferenceTitle": "Provider entity preference",
  "adminPartners.entityPreferenceBody":
    "Which provider divisions fulfill this partner's jobs. Partners set this themselves from their dashboard; an admin override is audit-logged with the reason below.",
  "adminPartners.entityPreferenceUnknown": "Not loaded",
  "adminPartners.entityPreference.both": "Both divisions",
  "adminPartners.entityPreference.business_only": "Businesses only",
  "adminPartners.entityPreferenceNew": "Override to",
  "adminPartners.entityPreferenceReasonRequired": "Provide a reason (minimum 3 characters).",
  "adminPartners.entityPreferenceApply": "Apply override",
  "adminPartners.entityPreferenceSaved": "Partner entity preference set to {value}.",
  "adminPartners.error.tierRequired": "Enable at least one partner tier before activating or updating controls.",
  "adminPartners.error.settlementRequired": "Enable at least one settlement mode.",
  "adminPartners.error.settlementNotEnabled": "The selected settlement mode must also be enabled.",
  "adminPartners.error.settlementLegacy":
    "This partner's stored settlement mode ({value}) is retired. Select a replacement mode before saving.",
  "adminPartners.error.invalidReserve": "Reserve requirement must be a non-negative integer.",
  "adminPartners.error.invalidCredit": "Credit limit must be empty or a non-negative integer.",
  "partnerKeys.eyebrow": "Partner API access",
  "partnerKeys.title": "Admin-managed partner keys",
  "partnerKeys.activePartner": "Partner active",
  "partnerKeys.partnerMustBeActive": "Activate partner first",
  "partnerKeys.messagingMode": "Messaging mode",
  "partnerKeys.webhookCoverage": "Webhook coverage",
  "partnerKeys.docsState": "Docs access",
  "partnerKeys.docsPending": "Phase 8 gated",
  "partnerKeys.disabled": "Disabled",
  "partnerKeys.externalRefsGuidance": "Partners use opaque external_user_ref and external_conversation_ref values only. Do not put raw handles, emails, phone numbers, or private channel names into Tinkr records.",
  "partnerKeys.rotationPolicy": "Rotation replaces only the secret. To change scopes, rate limit tier, or expiry, revoke this key and create a new one.",
  "partnerKeys.name": "Key name",
  "partnerKeys.namePlaceholder": "Partner production integration",
  "partnerKeys.expires": "Optional expiry date",
  "partnerKeys.rateLimitTier": "Rate limit tier",
  "partnerKeys.reason": "Audit reason",
  "partnerKeys.defaultReason": "Partner API key approved by admin.",
  "partnerKeys.scopes": "Partner scopes",
  "partnerKeys.create": "Create partner key",
  "partnerKeys.creating": "Creating key",
  "partnerKeys.created": "Partner key created. Store the plaintext now; it will not be shown again.",
  "partnerKeys.rotated": "Partner key rotated. Store the new plaintext now; the old key is revoked.",
  "partnerKeys.revoked": "Partner key revoked.",
  "partnerKeys.noKeys": "No partner API keys yet.",
  "partnerKeys.activeKeys": "Active partner keys",
  "partnerKeys.revokedBadge": "Revoked",
  "partnerKeys.rotateReason": "Rotating partner API key for credential hygiene.",
  "partnerKeys.revokeReasonDefault": "Revoking partner API key by admin request.",
  "partnerKeys.rotateConfirmTitle": "Rotate partner key?",
  "partnerKeys.rotateConfirmBody": "Rotating immediately revokes the current key and reveals a replacement once. Coordinate this before changing production integrations.",
  "partnerKeys.revokeConfirmTitle": "Revoke partner key?",
  "partnerKeys.revokeConfirmBody": "Revoking immediately stops any partner integration using this key.",
  "partnerKeys.confirmRotate": "Confirm rotate",
  "partnerKeys.writeRejected": "Partner key update rejected: {reason}.",
  "partnerKeys.error.nameRequired": "Enter a key name.",
  "partnerKeys.error.scopeRequired": "Select at least one partner scope.",
  "partnerKeys.error.reasonRequired": "Enter an audit reason.",
  "partnerKeys.scope.jobsCreate": "Create and operate partner jobs",
  "partnerKeys.scope.jobsRead": "Read partner jobs",
  "partnerKeys.scope.paymentsAttest": "Submit settlement evidence",
  "partnerKeys.scope.messagesRead": "Read message state",
  "partnerKeys.scope.messagesWrite": "Send partner messages",
  "partnerKeys.scope.messagesProxyCustomer": "Proxy customer messages",
  "partnerKeys.scope.webhooksManage": "Manage webhooks",
  // Partner Platform Batch P2 — partner self-serve application + key panels.
  // Partner-facing strings are localized across all supported languages.
  "partnerApplication.eyebrow": "Partner program",
  "partnerApplication.title": "Apply for partner API access",
  "partnerApplication.body": "Tell us what you're building. We'll reply to your contact email.",
  "partnerApplication.loading": "Loading your partner application.",
  "partnerHub.lookupFailedTitle": "We couldn't check your partner access",
  "partnerHub.lookupFailedBody": "This is a connection problem, not a change to your account. Try again in a moment.",
  "partnerHub.lookupRetry": "Try again",
  "partnerApplication.submit": "Submit application",
  "partnerApplication.submitting": "Submitting application",
  "partnerApplication.submitted": "Application submitted. We will email you when it is reviewed.",
  "partnerApplication.pendingTitle": "Your application is under review",
  "partnerApplication.pendingBody":
    "Our team reviews each application manually. You will receive an email when a decision is made.",
  "partnerApplication.withdraw": "Withdraw application",
  "partnerApplication.withdrawing": "Withdrawing application",
  "partnerApplication.withdrawn": "Application withdrawn. You can submit a new one any time.",
  "partnerApplication.withdrawnNote":
    "Your previous application was withdrawn. Submitting a new application starts a fresh review.",
  "partnerApplication.approvedTitle": "Approved",
  "partnerApplication.provisioningTitle": "Finishing partner setup",
  "partnerApplication.provisioningBody":
    "Your invite was accepted. Partner provisioning is still catching up; refresh in a moment before minting keys.",
  "partnerApplication.refresh": "Refresh status",
  "partnerApplication.rejectedTitle": "This application was not approved",
  "partnerApplication.field.companyName": "Company name",
  "partnerApplication.field.contactName": "Contact name (optional)",
  "partnerApplication.field.contactEmail": "Contact email",
  "partnerApplication.field.intendedUseCase": "Intended use case",
  "partnerApplication.field.intendedUseCase.help":
    "Briefly describe who you are and what kind of integration you want. Minimum {min} characters.",
  "partnerApplication.field.integrationPlan": "Integration plan",
  "partnerApplication.field.expectedVolume": "Expected monthly volume",
  "partnerApplication.field.proposedTier": "Proposed rate-limit tier (optional)",
  "partnerApplication.field.proposedTier.unset": "Let admin decide",
  "partnerApplication.field.proposedTier.help":
    "Admins decide the final tier at approval. This is a suggestion only.",
  "partnerApplication.volume.lt100": "Fewer than 100 jobs / month",
  "partnerApplication.volume.midA": "100–1,000 jobs / month",
  "partnerApplication.volume.midB": "1,000–10,000 jobs / month",
  "partnerApplication.volume.gt10k": "More than 10,000 jobs / month",
  "partnerApplication.captchaMissing": "Complete the security check before submitting.",
  "partnerApplication.captchaUnavailable":
    "The security check is unavailable. Try again in a moment.",
  "partnerApplication.captchaNotConfigured":
    "Partner applications aren't accepting submissions right now. Please email support@tinkrapp.com and we'll get back to you.",
  "partnerApplication.captchaFailed": "The security check failed. Try again.",
  "partnerApplication.error.closed":
    "New partner applications are temporarily paused. Try again later.",
  "partnerApplication.error.rateLimited":
    "Too many application attempts. Wait a while before trying again.",
  "partnerKeysSelf.eyebrow": "API keys",
  "partnerKeysSelf.title": "Your partner API keys",
  "partnerKeysSelf.loading": "Loading API keys.",
  "partnerKeysSelf.field.name": "Key name",
  "partnerKeysSelf.field.namePlaceholder": "Production integration",
  "partnerKeysSelf.field.rateLimitTier": "Rate limit tier",
  "partnerKeysSelf.field.rateLimitTier.inherit": "Inherit partner default",
  "partnerKeysSelf.field.reason": "Reason for audit log",
  "partnerKeysSelf.field.scopes": "Scopes",
  "partnerKeysSelf.defaultReason": "Mint partner API key.",
  "partnerKeysSelf.create": "Generate new key",
  "partnerKeysSelf.creating": "Generating key",
  "partnerKeysSelf.created": "Key created. Copy the plaintext now — it will not be shown again.",
  "partnerKeysSelf.rotated":
    "Key rotated. Copy the new plaintext now. The old key continues to work for {grace} more days.",
  "partnerKeysSelf.revoked": "Key revoked.",
  "partnerKeysSelf.activeKeys": "Active keys",
  "partnerKeysSelf.revokedKeys": "Revoked keys",
  "partnerKeysSelf.noKeys": "You do not have any active API keys yet.",
  "partnerKeysSelf.field.environment": "Environment",
  "partnerKeysSelf.env.sandbox": "Sandbox",
  "partnerKeysSelf.env.production": "Production",
  "partnerKeysSelf.env.productionLocked": "Production keys unlock after your production access is approved.",
  "partnerKeysSelf.noSandboxKeys": "No sandbox keys yet — create one to start testing.",
  "partnerKeysSelf.noProductionKeys": "No production keys yet.",
  "partnerLifecycle.eyebrow": "Partner access",
  "partnerLifecycle.title": "Sandbox and production",
  "partnerLifecycle.state.sandbox_active": "Sandbox active",
  "partnerLifecycle.state.production_review_pending": "Production review pending",
  "partnerLifecycle.state.production_active": "Production active",
  "partnerLifecycle.state.suspended": "Suspended",
  "partnerLifecycle.state.terminated": "Closed",
  "partnerLifecycle.suspended.body": "Your partner account is currently suspended. Please contact Tinkr support.",
  "partnerLifecycle.review.pending": "Your production launch request is under review. Sandbox access remains active in the meantime.",
  "partnerLifecycle.sandbox.body": "Sandbox access is active. Once your integration is working and ready for real customers, apply for production access.",
  "partnerLifecycle.applyCta": "Apply for production access",
  "partnerLifecycle.form.intro": "Tell us about your app so we can review it for production. We review use case, customer handling, payments, and safety before granting live access.",
  "partnerLifecycle.field.appName": "App name",
  "partnerLifecycle.field.appUrl": "App URL",
  "partnerLifecycle.field.useCase": "What your app does",
  "partnerLifecycle.field.volume": "Expected monthly orders",
  "partnerLifecycle.field.supportEmail": "Support contact email",
  "partnerLifecycle.field.paymentFlow": "Payment and refund flow",
  "partnerLifecycle.field.handling": "Customer handling",
  "partnerLifecycle.field.usersUploadFiles": "Customers upload their own files",
  "partnerLifecycle.field.aiModelsUsed": "AI-generated models are used",
  "partnerLifecycle.field.termsUrl": "Terms URL (optional)",
  "partnerLifecycle.field.privacyUrl": "Privacy URL (optional)",
  "partnerLifecycle.cancel": "Cancel",
  "partnerLifecycle.submit": "Submit for review",
  "partnerLifecycle.submitting": "Submitting…",
  "partnerLifecycle.error.alreadyProduction": "Your partner account already has production access.",
  "partnerKeysSelf.scopes": "Scopes",
  "partnerKeysSelf.tier": "Tier",
  "partnerKeysSelf.tierRestricted": "Limited",
  "partnerKeysSelf.tierStandard": "Standard",
  "partnerKeysSelf.tierHigh": "High volume",
  "partnerKeysSelf.expires": "Expires",
  "partnerKeysSelf.lastUsed": "Last used",
  "partnerKeysSelf.noExpiry": "No expiry",
  "partnerKeysSelf.neverUsed": "Never used",
  "partnerKeysSelf.graceBadge": "Rotating",
  "partnerKeysSelf.graceExpiresAt": "Grace ends",
  "partnerKeysSelf.rotate": "Rotate",
  "partnerKeysSelf.revoke": "Revoke",
  "partnerKeysSelf.cancel": "Cancel",
  "partnerKeysSelf.rotate.confirmTitle": "Rotate this key?",
  "partnerKeysSelf.rotate.confirmBody":
    "Rotation issues a new key now. The current key keeps working for {grace} days so you can swap it into production safely, then is revoked automatically.",
  "partnerKeysSelf.rotate.confirmAction": "Rotate key",
  "partnerKeysSelf.rotate.defaultReason": "Routine key rotation.",
  "partnerKeysSelf.revoke.confirmTitle": "Revoke this key?",
  "partnerKeysSelf.revoke.confirmBody":
    "Revoke immediately stops every integration using this key. Use only if the key is leaked or no longer needed.",
  "partnerKeysSelf.revoke.confirmAction": "Revoke key",
  "partnerKeysSelf.revoke.defaultReason": "Revoking partner API key.",
  "partnerKeysSelf.revealedCreated": "Your new API key",
  "partnerKeysSelf.revealedRotated": "Your rotated API key",
  "partnerKeysSelf.autoHide": "This value disappears in 60 seconds and cannot be recovered.",
  "partnerKeysSelf.copy": "Copy",
  "partnerKeysSelf.copied": "Copied.",
  "partnerKeysSelf.copyFailed": "Copy failed. Select the value manually.",
  "partnerKeysSelf.hide": "Hide",
  "partnerKeysSelf.locked.title": "Apply to mint keys",
  "partnerKeysSelf.locked.body":
    "Submit and complete a partner application before minting API keys.",
  "partnerKeysSelf.locked.applyCta": "Open partner application",
  "partnerKeysSelf.error.nameRequired": "Enter a key name before generating.",
  "partnerKeysSelf.error.scopeRequired": "Select at least one scope.",
  "partnerKeysSelf.error.reasonRequired": "Provide a reason for the audit log.",
  "partnerKeysSelf.error.notApproved":
    "Your partner invite is accepted, but partner provisioning is still finishing. Refresh in a moment.",
  // Partner Platform Batch P3 — partner self-serve webhook subscription panel.
  // Owned by apps/web/src/components/PartnerWebhooksPanel.tsx.
  "partnerWebhooksSelf.eyebrow": "Webhook subscriptions",
  "partnerWebhooksSelf.title": "Your webhook endpoints",
  "partnerWebhooksSelf.subtitle":
    "Receive signed event deliveries on your own infrastructure. HTTPS endpoints only; retries follow the configured max attempts before dead-lettering.",
  "partnerWebhooksSelf.create": "Add endpoint",
  "partnerWebhooksSelf.refresh": "Refresh",
  "partnerWebhooksSelf.loading": "Loading subscriptions…",
  "partnerWebhooksSelf.empty": "No webhook endpoints configured yet.",
  "partnerWebhooksSelf.gatePending":
    "Webhook subscriptions unlock after your partner application is approved.",
  "partnerWebhooksSelf.gateApplyCta": "Open partner application",
  "partnerWebhooksSelf.status.enabled": "Enabled",
  "partnerWebhooksSelf.status.disabled": "Disabled",
  "partnerWebhooksSelf.subscriptionId": "Subscription ID",
  "partnerWebhooksSelf.update": "Edit",
  "partnerWebhooksSelf.delete": "Delete",
  "partnerWebhooksSelf.deleteWarning":
    "This will stop deliveries to the endpoint. Already-claimed events will not be retried after deletion.",
  "partnerWebhooksSelf.confirmCreate": "Create endpoint",
  "partnerWebhooksSelf.confirmUpdate": "Save changes",
  "partnerWebhooksSelf.confirmDelete": "Delete endpoint",
  "partnerWebhooksSelf.cancel": "Cancel",
  "partnerWebhooksSelf.saving": "Saving…",
  "partnerWebhooksSelf.created": "Webhook endpoint created.",
  "partnerWebhooksSelf.updated": "Webhook endpoint updated.",
  "partnerWebhooksSelf.deleted": "Webhook endpoint deleted.",
  "partnerWebhooksSelf.field.endpointUrl": "Endpoint URL",
  "partnerWebhooksSelf.field.eventTypes": "Event types",
  "partnerWebhooksSelf.field.eventTypesNone": "No event types subscribed",
  "partnerWebhooksSelf.field.enabled": "Enable delivery",
  "partnerWebhooksSelf.field.maxAttempts": "Max delivery attempts",
  "partnerWebhooksSelf.signingSecretAutoGeneratedHint":
    "A unique HMAC signing secret will be generated for this subscription and shown once after you save. Copy it then — it can't be retrieved later.",
  "partnerWebhooksSelf.revealed.eyebrow": "Signing secret",
  "partnerWebhooksSelf.revealed.title": "Copy this secret now",
  "partnerWebhooksSelf.revealed.body":
    "Tinkr will sign every delivery to this endpoint with this secret. Store it securely on your side — it won't be shown again. If you lose it, delete this subscription and create a new one to receive a fresh secret.",
  "partnerWebhooksSelf.revealed.value": "Signing secret",
  "partnerWebhooksSelf.revealed.subscriptionId": "Subscription",
  "partnerWebhooksSelf.revealed.copy": "Copy secret",
  "partnerWebhooksSelf.revealed.dismiss": "I've saved it",
  "partnerWebhooksSelf.revealed.copied": "Copied to clipboard.",
  "partnerWebhooksSelf.revealed.copyFailed": "Copy failed — select the value and copy manually.",
  "partnerWebhooksSelf.field.reason": "Reason for audit log",
  "partnerWebhooksSelf.field.lastDeliveryAt": "Last delivery",
  "partnerWebhooksSelf.field.createdAt": "Created",
  "partnerWebhooksSelf.error.reasonRequired":
    "Provide a reason for the audit log (3+ characters).",
  "partnerWebhooksSelf.error.httpsRequired":
    "Endpoint URL must use https://.",
  "partnerWebhooksSelf.error.notApproved":
    "Your partner invite is accepted, but partner provisioning is still finishing. Refresh in a moment.",
  // Partner Platform — sandbox order playground panel.
  // Owned by apps/web/src/components/SandboxPlaygroundPanel.tsx. English-only by
  // practice (the partner dashboard is not localized).
  "sandboxPlayground.eyebrow": "Sandbox testing",
  "sandboxPlayground.title": "Sandbox order playground",
  "sandboxPlayground.panelTitle": "Your sandbox orders",
  "sandboxPlayground.intro":
    "Create a simulated order and step it through the full lifecycle so you can test your integration end to end. Each step sends a real, sandbox-flagged webhook to your subscription. Nothing here touches live providers, payments, or payouts.",
  "sandboxPlayground.gatePending":
    "Sandbox testing unlocks after your partner application is approved.",
  "sandboxPlayground.gateApplyCta": "Go to partner application",
  "sandboxPlayground.field.material": "Material",
  "sandboxPlayground.field.quantity": "Quantity",
  "sandboxPlayground.create": "Create sandbox order",
  "sandboxPlayground.creating": "Creating…",
  "sandboxPlayground.refresh": "Refresh",
  "sandboxPlayground.loading": "Loading sandbox orders…",
  "sandboxPlayground.empty": "No sandbox orders yet. Create one above to test the flow.",
  "sandboxPlayground.webhookNote":
    "Steps deliver to your sandbox webhook subscription, flagged with environment \"sandbox\" so you can tell test traffic apart from live.",
  "sandboxPlayground.advanceTo": "Advance to",
  "sandboxPlayground.advancing": "Advancing…",
  "sandboxPlayground.terminal": "This order reached a final state.",
  "sandboxPlayground.state.created": "Created",
  "sandboxPlayground.state.estimating": "Estimating",
  "sandboxPlayground.state.quote_locked": "Quote locked",
  "sandboxPlayground.state.delivered": "Delivered",
  "sandboxPlayground.state.completed": "Completed",
  "sandboxPlayground.state.cancelled": "Cancelled",
  "sandboxPlayground.error.load": "Could not load sandbox orders. Try again.",
  "sandboxPlayground.error.create": "Could not create the sandbox order. Try again.",
  "sandboxPlayground.error.advance": "Could not advance the sandbox order. Try again.",
  "partnerApiBrief.eyebrow": "AI build brief",
  "partnerApiBrief.title": "Copy the Tinkr API instructions",
  "partnerApiBrief.body":
    "Paste this into your coding agent so a developer can build against the partner API without reading the docs first. It contains no secret keys.",
  "partnerApiBrief.locked.title": "Partner login required",
  "partnerApiBrief.locked.body":
    "API instructions unlock after you sign in with an approved partner account.",
  "partnerApiBrief.locked.cta": "Open partner access",
  "partnerApiBrief.copy": "Copy agent instructions",
  "partnerApiBrief.copied": "Agent instructions copied.",
  "partnerApiBrief.copyFailed": "Copy failed. Select the preview text manually.",
  "partnerApiBrief.loading": "Loading approved partner instructions.",
  "partnerApiBrief.loadFailed": "Could not load the approved partner instructions.",
  "partnerApiBrief.accessDenied":
    "Your partner invite is accepted, but partner provisioning is still finishing. Refresh in a moment.",
  "partnerApiBrief.requestId": "Request ID: {requestId}.",
  "partnerApiBrief.retry": "Retry",
  "partnerApiBrief.previewLabel": "Tinkr partner API agent instruction preview",
  "partnerApiBrief.previewTitle": "Agent instructions",
  "partnerApiBrief.previewSubtitle": "Safe to paste into Codex, Claude, or a repo issue.",
  "partnerApiBrief.sdk.eyebrow": "Partner SDK",
  "partnerApiBrief.sdk.title": "Download the TypeScript SDK",
  "partnerApiBrief.sdk.body": "Download this small package, move the file into your project directory, then run the install command there.",
  "partnerApiBrief.sdk.version": "Version {version}",
  "partnerApiBrief.sdk.size": "{size} KiB",
  "partnerApiBrief.sdk.download": "Download SDK",
  "partnerApiBrief.sdk.downloading": "Checking download…",
  "partnerApiBrief.sdk.retry": "Retry download",
  "partnerApiBrief.sdk.installLabel": "Install the downloaded file locally",
  "partnerApiBrief.sdk.copyInstall": "Copy command",
  "partnerApiBrief.sdk.safety": "Keep partner API keys on your server. Never put them in browser code or this package.",
  "partnerApiBrief.sdk.next": "Read the package README and example before connecting your app.",
  "partnerApiBrief.sdk.updates": "SDK updates appear in dashboard notifications. Breaking changes may also be emailed; the dashboard notice is the authoritative record. Download and install updates yourself.",
  "partnerApiBrief.sdk.saved": "SDK download started. Check your Downloads folder.",
  "partnerApiBrief.sdk.accessDenied": "SDK downloads require an active, approved partner account. Check your account status or contact Tinkr support.",
  "partnerApiBrief.sdk.changed": "A newer SDK is available. Refresh these instructions before downloading so the install command matches.",
  "partnerApiBrief.sdk.invalid": "The SDK download could not be verified. No file was saved. Retry or contact support.",
  "partnerApiBrief.sdk.failed": "Could not download the SDK. Retry in a moment.",
  "partnerApiBrief.sdk.refresh": "Refresh instructions",
  "partnerApiBrief.sdk.installCopied": "Install command copied.",
  "partnerApiBrief.sdk.installCopyFailed": "Could not copy the command. Select it manually.",
  "partnerDocs.title": "Partner docs",
  "partnerDocs.body": "Overview links match the capabilities enabled for this partner. Detailed integration instructions are delivered to the partner inside their dashboard Docs section.",
  "partnerDocs.noneEnabled": "No partner capabilities are enabled yet. Approve a tier or settlement mode to unlock docs.",
  "partnerDocs.showDisabled": "Show {count} capabilities not yet enabled",
  "partnerDocs.statusComingSoon": "Coming soon",
  "partnerDocs.statusNotEnabled": "Not enabled",
  "partnerDocs.tier1Title": "Tier 1 hosted handoff",
  "partnerDocs.tier1Body": "Lead/job referral handoffs to the hosted Tinkr flow.",
  "partnerDocs.tier2Title": "Tier 2 API integration",
  "partnerDocs.tier2Body": "Direct job creation, attestation, and webhook delivery via the partner API.",
  "partnerDocs.messagingPartnerMediatedTitle": "Partner-mediated messaging",
  "partnerDocs.messagingPartnerMediatedBody": "Partner UI is the customer surface; Tinkr proxies messages back through partner-owned channels.",
  "partnerDocs.messagingCustomerBridgeTitle": "Customer bridge messaging",
  "partnerDocs.messagingCustomerBridgeBody": "Customers receive a Tinkr-hosted bridge for live print updates.",
  "partnerDocs.messagingDirectGuestTitle": "Direct Tinkr guest messaging",
  "partnerDocs.messagingDirectGuestBody": "Customers join Tinkr as guest accounts with direct provider messaging.",
  "partnerDocs.settlementInvoiceCreditTitle": "Invoice credit settlement",
  "partnerDocs.settlementInvoiceCreditBody": "Partner is invoiced post-period against an approved credit limit.",
  "partnerDocs.settlementExternalClaimTitle": "External payment claim",
  "partnerDocs.settlementExternalClaimBody": "Partner attests payment received through an approved external channel.",
  "partnerDocs.webhooksTitle": "Webhook subscriptions",
  "partnerDocs.webhooksBody": "Signed event delivery for jobs, settlement, and payouts.",
  "partnerDocs.quoteDisplayTitle": "External quote display",
  "partnerDocs.quoteDisplayBody": "Show locked quote/itemisation inside the partner surface before customer commit.",
  "adminOperations.title": "Outbox, slicer, and job health",
  "adminOperations.subtitle": "Inspect outbox, payouts, slicer runs, and job state. Requeue rows, force job transitions, or retry slicer runs with audit reasons.",
  "adminOperations.refresh": "Refresh operations health",
  "adminOperations.noSession": "Sign in with admin access to load operations health.",
  "adminOperations.loading": "Loading operations health.",
  "adminOperations.empty": "Load operations health to review current queues.",
  "adminOperations.destinationFilter": "Destination filter",
  "adminOperations.destinationPlaceholder": "stripe, resend, provider_webhook",
  "adminOperations.operationFilter": "Operation filter",
  "adminOperations.operationPlaceholder": "stripe_connect_transfer.create",
  "adminOperations.applyFilters": "Apply filters",
  "adminOperations.clearFilter": "Clear",
  "adminOperations.outboxLabel": "Outbox",
  "adminOperations.outboxHint": "Slot order: returned rows / dead-letter rows / stale processing locks.",
  "adminOperations.payoutsLabel": "Payouts",
  "adminOperations.payoutsHint": "Slot order: provider payout rows / failed rows / dead-letter rows.",
  "adminOperations.slicerRunsLabel": "Slicer runs",
  "adminOperations.slicerRunsHint": "Slot order: slicer rows / stale queued or running rows.",
  "adminOperations.jobsLabel": "Jobs",
  "adminOperations.jobsHint": "Slot order: job rows / stuck estimate rows / legitimate provider-waiting rows.",
  "adminOperations.overall": "Overall",
  "adminOperations.overallHint": "Worst current operations-health severity.",
  "adminOperations.metric.rows": "Rows returned",
  "adminOperations.metric.deadLetter": "Dead-letter",
  "adminOperations.metric.staleLock": "Stale lock",
  "adminOperations.metric.failed": "Failed",
  "adminOperations.metric.stale": "Stale",
  "adminOperations.metric.stuck": "Stuck",
  "adminOperations.metric.waiting": "Waiting",
  "adminOperations.metric.severity": "Worst severity",
  "adminOperations.count": "{count} rows",
  "adminOperations.noFlags": "No current operations flags.",
  "adminOperations.staleOutbox": "Stale outbox locks",
  "adminOperations.failedOutbox": "Failed outbox",
  "adminOperations.deadLetters": "Dead-letter outbox",
  "adminOperations.staleSlicers": "Stale slicer runs",
  "adminOperations.stuckJobs": "Stuck jobs",
  "adminOperations.waitingJobs": "Waiting for a provider",
  "adminOperations.requeueTitle": "Outbox requeue",
  "adminOperations.requeueSummary": "Before: a failed or dead-lettered outbox row is blocked. After: it is reset to pending with a fresh retry budget.",
  "adminOperations.outboxId": "Outbox row ID",
  "adminOperations.reason": "Reason",
  "adminOperations.confirmRequeue": "I have reviewed the row, external side effect risk, and audit reason.",
  "adminOperations.prepareRequeue": "Requeue",
  "adminOperations.requeueOutbox": "Requeue outbox",
  "adminOperations.requeueSaved": "Requeued {destination}.{operation}.",
  "adminOperations.requeueReasonDefault": "Manual requeue approved for {destination}.{operation}",
  "adminOperations.noStaleOutbox": "No stale outbox locks returned.",
  "adminOperations.noFailedOutbox": "No failed outbox rows returned.",
  "adminOperations.noDeadLetters": "No dead-letter rows returned.",
  "adminOperations.noStaleSlicers": "No stale slicer runs returned.",
  "adminOperations.noStuckJobs": "No stuck jobs returned.",
  "adminOperations.noWaitingJobs": "No orders have been waiting for a provider for over an hour.",
  "adminOperations.forceTransitionTitle": "Job recovery transition",
  "adminOperations.slicerRetryTitle": "Slicer retry",
  "adminOperations.jobId": "Job ID",
  "adminOperations.forceCommand": "Transition command",
  "adminOperations.errorCode": "Error code",
  "adminOperations.artifactId": "Artifact ID",
  "adminOperations.profileHash": "Profile hash",
  "adminOperations.slicerName": "Slicer",
  "adminOperations.slicerVersion": "Version",
  "adminOperations.containerDigest": "Container image digest",
  "adminOperations.confirmForceTransition": "I understand this changes job state for recovery.",
  "adminOperations.forceTransition": "Run transition",
  "adminOperations.retrySlicer": "Retry slicer",
  "adminOperations.saving": "Saving...",
  "adminOperations.recoverySaved": "Recovery action saved. Current state: {state}.",
  "adminOperations.jobCommand.customerRequestProvider": "Request provider offers",
  "adminOperations.jobCommand.cancelJob": "Cancel job",
  "adminOperations.jobCommand.cancelQuoteLocked": "Cancel quote-locked job",
  "adminOperations.jobCommand.markEstimateFailed": "Mark estimate failed",
  "adminSettlement.eyebrow": "Admin settlement",
  "adminSettlement.title": "Settlement and payout gates",
  "adminSettlement.tab.label": "Settlement sections",
  "adminSettlement.tab.claims": "Claims",
  "adminSettlement.tab.events": "Events",
  "adminSettlement.tab.payouts": "Payouts",
  "adminSettlement.subtitle.claims": "Verify or reject customer payment claims. Every action is audited.",
  // Partner attestation is retired (ADR-P11) and /api rejects it; the only
  // recordable event type left is manual_adjustment, so the copy must not
  // promise an attestation surface that no longer exists.
  "adminSettlement.subtitle.events": "Record manual settlement adjustments against a payable. Every action is audited.",
  "adminSettlement.subtitle.payouts": "Hold a settled payable before payout authorization, or release a held one back to the payout worker. Every action is audited.",
  "adminSettlement.noSession": "Sign in with admin access to manage settlement controls.",
  "adminSettlement.claimTitle": "Payment claim review",
  "adminSettlement.eventTitle": "Manual settlement event",
  "adminSettlement.payoutGateTitle": "Payout hold and release",
  "adminSettlement.payoutGateBody": "Hold only settled payables before payout authorization. Release returns a held payable to settled for the payout worker.",
  "adminSettlement.paymentClaimId": "Payment claim ID",
  "adminSettlement.payableId": "Payable ID",
  "adminSettlement.eventType": "Settlement event type",
  "adminSettlement.reason": "Audit reason",
  "adminSettlement.reasonMin": "At least 10 characters — recorded in the audit log.",
  "adminSettlement.errorCode": "Error code",
  "adminSettlement.evidenceJson": "Evidence JSON",
  "adminSettlement.relatedPartnerId": "Related partner ID",
  "adminSettlement.relatedBalanceTransactionId": "Related balance transaction ID",
  "adminSettlement.verifyClaim": "Verify claim",
  "adminSettlement.rejectClaim": "Reject claim",
  "adminSettlement.recordSettlement": "Record settlement",
  "adminSettlement.holdPayout": "Hold payout",
  "adminSettlement.releasePayout": "Release payout",
  "adminSettlement.saving": "Saving",
  "adminSettlement.saved": "{target} updated with status {status}.",
  "adminSettlement.confirmClaim": "I have checked the claim, payable, evidence, and audit reason.",
  "adminSettlement.confirmSettlement": "I have reconciled this external settlement evidence.",
  "adminSettlement.confirmPayoutGate": "I understand this changes provider payout eligibility for this payable.",
  "adminSettlement.summaryClaim": "Before: claim {claimId} is pending review. After: payable {payableId} may be settled or the claim rejected.",
  "adminSettlement.summarySettlement": "Before: payable {payableId} awaits settlement evidence. After: {eventType} may settle or advance the payable.",
  "adminSettlement.summaryPayoutGate": "Before: payable {payableId} is in its current payout state. After: payout may be held or released for worker evaluation.",
  "adminSettlement.error.evidenceJson": "Evidence must be valid JSON.",
  "adminSettlement.error.evidenceObject": "Evidence JSON must be an object.",
  "adminQuality.title": "Quality incidents",
  "adminQuality.subtitle": "Open new quality incidents, browse the queue with filters, and run audited resolve/void/reopen transitions.",
  "adminQuality.refresh": "Refresh quality incidents",
  "adminQuality.noSession": "Sign in with admin access to manage quality incidents.",
  "adminQuality.openTitle": "Open incident",
  "adminQuality.queueTitle": "Incident queue",
  "adminQuality.jobId": "Job ID",
  "adminQuality.jobIdHint":
    "Paste the order UUID (from Orders & support > Orders, or the provider's work queue).",
  "adminQuality.providerId": "Provider ID",
  "adminQuality.row.reasonCode": "Reason",
  "adminQuality.row.openedBy": "Opened by",
  "adminQuality.fulfillmentGroupId": "Fulfillment group ID",
  "adminQuality.payableId": "Payable ID",
  "adminQuality.severity": "Severity",
  "adminQuality.reasonCode": "Reason code",
  "adminQuality.summary": "Summary",
  "adminQuality.auditReason": "Audit reason",
  "adminQuality.openIncident": "Open incident",
  "adminQuality.statusFilter": "Status filter",
  "adminQuality.providerFilter": "Provider filter",
  "adminQuality.jobFilter": "Job filter",
  "adminQuality.applyFilters": "Apply filters",
  "adminQuality.loading": "Loading quality incidents.",
  "adminQuality.empty": "No quality incidents match the current filters.",
  "adminQuality.transitionReason": "Transition reason",
  "adminQuality.resolutionNote": "Resolution note",
  "adminQuality.resolve": "Resolve",
  "adminQuality.void": "Void",
  "adminQuality.reopen": "Reopen",
  "adminQuality.saving": "Saving",
  "adminQuality.saved": "Quality incident {incidentId} updated to {status}.",
  "adminQuality.reasonRequired": "Enter a transition reason.",
  "adminQuality.resolutionRequired": "Resolution note must be at least 8 characters.",
  "adminAnnouncements.title": "Announcements",
  "adminAnnouncements.subtitle": "Draft, publish, archive, and email-broadcast in-app announcements. Destructive actions require a recent admin reauth.",
  "adminAnnouncements.preview.label": "Announcement preview",
  "adminAnnouncements.preview.eyebrow": "LIVE PREVIEW",
  "adminAnnouncements.preview.placeholderTitle": "Your announcement title",
  "adminAnnouncements.preview.placeholderBody": "Body copy will appear here as you type.",
  "adminAnnouncements.preview.empty": "Start typing in the form to see how this will look.",
  "adminAnnouncements.preview.requiresAck": "Acknowledgment required",
  "adminAnnouncements.preview.metaDisplay": "Display",
  "adminAnnouncements.preview.metaSeverity": "Severity",
  "adminAnnouncements.preview.metaAudience": "Audience",
  "adminAnnouncements.preview.metaAudienceNone": "None selected",
  "adminAnnouncements.preview.metaWindow": "Window",
  "adminAnnouncements.preview.metaWindowNow": "Now",
  "adminAnnouncements.preview.metaWindowOpen": "Open-ended",
  "adminAnnouncements.refresh": "Refresh announcements",
  "adminAnnouncements.noSession": "Sign in with admin access to manage announcements.",
  "adminAnnouncements.createTitle": "Create announcement",
  "adminAnnouncements.listTitle": "Announcements",
  "adminAnnouncements.createButton": "Create draft",
  "adminAnnouncements.titleField": "Title",
  "adminAnnouncements.bodyField": "Body",
  "adminAnnouncements.audienceField": "Audiences",
  "adminAnnouncements.severityField": "Severity",
  "adminAnnouncements.displayModeField": "Display mode",
  "adminAnnouncements.requiresAckField": "Requires acknowledgement",
  "adminAnnouncements.defaultLocaleField": "Default locale",
  "adminAnnouncements.startsAtField": "Starts at",
  "adminAnnouncements.endsAtField": "Ends at",
  "adminAnnouncements.createReasonField": "Create reason",
  "adminAnnouncements.statusFilter": "Status filter",
  "adminAnnouncements.audienceFilter": "Audience filter",
  "adminAnnouncements.applyFilters": "Apply filters",
  "adminAnnouncements.loading": "Loading announcements.",
  "adminAnnouncements.empty": "No announcements match the current filters.",
  "adminAnnouncements.startsAtLabel": "Starts",
  "adminAnnouncements.endsAtLabel": "Ends",
  "adminAnnouncements.transitionAction.publish": "Publish",
  "adminAnnouncements.transitionAction.archive": "Archive",
  "adminAnnouncements.emailBroadcastButton": "Email broadcast",
  "adminAnnouncements.confirmReasonField": "Audit reason",
  "adminAnnouncements.confirmAcknowledge": "I confirm this action and accept it will be logged.",
  "adminAnnouncements.cancel": "Cancel",
  "adminAnnouncements.confirmTransition.title": "Confirm state change",
  "adminAnnouncements.confirmTransition.body": "About to {command} the announcement \"{title}\".",
  "adminAnnouncements.confirmTransition.action": "Confirm",
  "adminAnnouncements.confirmTransition.reauthWarning": "Admin actions require a recent reauth. If you have not signed in recently you will see a reauth prompt.",
  "adminAnnouncements.confirmBroadcast.title": "Confirm email broadcast",
  "adminAnnouncements.confirmBroadcast.body": "Request an email broadcast for \"{title}\". Eligible recipients are queued only where email is enabled; delivery is not guaranteed.",
  "adminAnnouncements.confirmBroadcast.action": "Send broadcast",
  "adminAnnouncements.confirmBroadcast.reauthWarning": "Email broadcasts are destructive and require a fresh admin reauth.",
  "adminAnnouncements.saving": "Saving",
  "adminAnnouncements.saved": "Announcement {announcementId} updated to {status}.",
  "adminAnnouncements.broadcastSaved": "Broadcast queued. Provider recipients: {providerCount}. Partner recipients: {partnerCount}.",
  "adminAnnouncements.error.reasonRequired": "Enter an audit reason (3 characters or more).",
  "adminAnnouncements.error.confirmRequired": "Tick the confirmation checkbox before submitting.",
  "adminAnnouncements.error.listFailed": "Could not load announcements.",
  "adminAnnouncements.error.createFailed": "Could not create the announcement.",
  "adminAnnouncements.error.transitionFailed": "Could not transition the announcement.",
  "adminAnnouncements.error.broadcastFailed": "Could not queue the email broadcast.",
  "adminNetworkLive.eyebrow": "Provider network announcement",
  "adminNetworkLive.title": "Provider network launch announcement",
  "adminNetworkLive.body": "When on, provider applicants (pending or approved) who have not been told yet get a one-time \"provider network is live\" email, and provider dashboards show \"Job routing open\" instead of \"Job routing paused\". This switch never starts or stops orders or offers.",
  "adminNetworkLive.refresh": "Refresh provider network announcement",
  "adminNetworkLive.noSession": "Sign in with admin access to manage the provider network announcement.",
  "adminNetworkLive.loading": "Loading provider network announcement…",
  "adminNetworkLive.statusLabel": "Launch announcement",
  "adminNetworkLive.statusLive": "On",
  "adminNetworkLive.statusPaused": "Off",
  "adminNetworkLive.updatedAt": "Last changed {at}",
  "adminNetworkLive.actionPause": "Turn off",
  "adminNetworkLive.actionResume": "Turn on",
  "adminNetworkLive.reason": "Reason (at least 10 chars; recorded in the audit log)",
  "adminNetworkLive.reasonPlaceholder": "Why are you changing the announcement? (e.g. \"Provider network launch\")",
  "adminNetworkLive.confirmPause": "Turn off the announcement? Provider dashboards show \"Job routing paused\". No email is sent and orders are not affected.",
  "adminNetworkLive.confirmResume": "Turn on the announcement? Every provider applicant not told yet is emailed once that the network is live, and provider dashboards show \"Job routing open\". Orders are not affected.",
  "adminNetworkLive.reasonTooShort": "Reason must be at least 10 characters.",
  "adminNetworkLive.confirmRequired": "Tick the confirmation checkbox before submitting.",
  "adminNetworkLive.feedback": "Announcement saved. Queued {count} provider email(s).",
  "adminMaintenance.title": "Platform maintenance",
  "adminMaintenance.subtitle": "Schedule, start, and end maintenance windows. Destructive actions require a recent admin reauth.",
  "adminMaintenance.refresh": "Refresh maintenance events",
  "adminMaintenance.noSession": "Sign in with admin access to manage maintenance.",
  "adminMaintenance.createTitle": "Schedule maintenance",
  "adminMaintenance.listTitle": "Maintenance events",
  "adminMaintenance.createButton": "Schedule maintenance",
  "adminMaintenance.publicTitleField": "Public title",
  "adminMaintenance.publicMessageField": "Public message",
  "adminMaintenance.maintenanceTypeField": "Maintenance type",
  "adminMaintenance.componentsField": "Affected components",
  "adminMaintenance.writeGateField": "Block writes on affected components",
  "adminMaintenance.startsAtField": "Starts at",
  "adminMaintenance.endsAtField": "Ends at",
  "adminMaintenance.operatorNotesField": "Operator notes (internal)",
  "adminMaintenance.createReasonField": "Create reason",
  "adminMaintenance.statusFilter": "Status filter",
  "adminMaintenance.applyFilters": "Apply filters",
  "adminMaintenance.loading": "Loading maintenance events.",
  "adminMaintenance.empty": "No maintenance events match the current filters.",
  "adminMaintenance.startsAtLabel": "Starts",
  "adminMaintenance.endsAtLabel": "Ends",
  "adminMaintenance.transitionAction.start": "Start now",
  "adminMaintenance.transitionAction.complete": "End maintenance",
  "adminMaintenance.transitionAction.cancel": "Cancel",
  "adminMaintenance.confirmReasonField": "Audit reason",
  "adminMaintenance.confirmAcknowledge": "I confirm this action and accept it will be logged.",
  "adminMaintenance.cancel": "Cancel",
  "adminMaintenance.confirmTransition.title": "Confirm maintenance change",
  "adminMaintenance.confirmTransition.body": "About to {command} the maintenance window \"{title}\".",
  "adminMaintenance.confirmTransition.action": "Confirm",
  "adminMaintenance.confirmTransition.reauthWarning": "Admin actions require a recent reauth. If you have not signed in recently you will see a reauth prompt.",
  "adminMaintenance.saving": "Saving",
  "adminMaintenance.saved": "Maintenance {maintenanceId} updated to {status}.",
  "adminMaintenance.error.reasonRequired": "Enter an audit reason (3 characters or more).",
  "adminMaintenance.error.confirmRequired": "Tick the confirmation checkbox before submitting.",
  "adminMaintenance.error.listFailed": "Could not load maintenance events.",
  "adminMaintenance.error.createFailed": "Could not schedule the maintenance window.",
  "adminMaintenance.error.transitionFailed": "Could not transition the maintenance window.",
  "adminEnforcement.title": "Enforcement actions",
  "adminEnforcement.subtitle": "Issue, resolve, and void enforcement actions against providers, partners, and API keys. Destructive actions require a recent admin reauth.",
  "adminEnforcement.refresh": "Refresh enforcement actions",
  "adminEnforcement.noSession": "Sign in with admin access to manage enforcement actions.",
  "adminEnforcement.createTitle": "Create enforcement action",
  "adminEnforcement.listTitle": "Enforcement actions",
  "adminEnforcement.createButton": "Create action",
  "adminEnforcement.targetTypeField": "Target type",
  "adminEnforcement.targetIdField": "Target ID",
  "adminEnforcement.actionTypeField": "Action type",
  "adminEnforcement.severityField": "Severity",
  "adminEnforcement.publicReasonField": "Public reason",
  "adminEnforcement.internalNotesField": "Internal notes",
  "adminEnforcement.expiresAtField": "Expires at",
  "adminEnforcement.createReasonField": "Create reason",
  "adminEnforcement.statusFilter": "Status filter",
  "adminEnforcement.targetTypeFilter": "Target type filter",
  "adminEnforcement.applyFilters": "Apply filters",
  "adminEnforcement.loading": "Loading enforcement actions.",
  "adminEnforcement.empty": "No enforcement actions match the current filters.",
  "adminEnforcement.cappedNotice":
    "Showing a full page of 50 matches — older actions may exist. Narrow the filters to check.",
  "adminEnforcement.effectiveAtLabel": "Effective",
  "adminEnforcement.expiresAtLabel": "Expires",
  "adminEnforcement.transitionAction.resolve": "Resolve",
  "adminEnforcement.transitionAction.void": "Void",
  "adminEnforcement.confirmReasonField": "Audit reason",
  "adminEnforcement.confirmAcknowledge": "I confirm this action and accept it will be logged.",
  "adminEnforcement.cancel": "Cancel",
  "adminEnforcement.confirmTransition.title": "Confirm enforcement change",
  "adminEnforcement.confirmTransition.body": "About to {command} the {actionType} action against {targetType} {targetId}.",
  "adminEnforcement.confirmTransition.action": "Confirm",
  "adminEnforcement.confirmTransition.reauthWarning": "Admin actions require a recent reauth. If you have not signed in recently you will see a reauth prompt.",
  "adminEnforcement.confirmCreate.title": "Confirm enforcement action",
  "adminEnforcement.confirmCreate.body": "About to create a {actionType} enforcement against {targetType} {targetId}. This mutates the target immediately — pause/resume change operational state, suspend changes provider/partner status, revoke_api_key revokes credentials.",
  "adminEnforcement.confirmCreate.action": "Create enforcement",
  "adminEnforcement.confirmCreate.reauthWarning": "Creating a destructive enforcement requires a recent admin reauth. If you have not signed in recently you will see a reauth prompt.",
  "adminEnforcement.saving": "Saving",
  "adminEnforcement.saved": "Enforcement action {actionId} updated to {status}.",
  "adminEnforcement.error.reasonRequired": "Enter an audit reason (3 characters or more).",
  "adminEnforcement.error.confirmRequired": "Tick the confirmation checkbox before submitting.",
  "adminEnforcement.error.listFailed": "Could not load enforcement actions.",
  "adminEnforcement.error.createFailed": "Could not create the enforcement action.",
  "adminEnforcement.error.transitionFailed": "Could not transition the enforcement action.",
  "onboarding.eyebrow": "Getting started",
  "onboarding.title": "Getting set up",
  "onboarding.refresh": "Refresh provider onboarding readiness",
  "onboarding.lockedTitle": "Onboarding locked",
  "onboarding.lockedBody": "Provider onboarding unlocks after the application is accepted. Central eligibility controls job routing after setup and approval.",
  "onboarding.noProfileReturned": "Provider profile was not returned after onboarding.",
  "onboarding.creatingLink": "Creating link",
  "onboarding.readyTitle": "Ready to create provider profile",
  "onboarding.legalName": "Legal business name",
  "onboarding.displayName": "Display name",
  "onboarding.accountCountry": "Business account country",
  "onboarding.accountCountryPlaceholder": "Select account country",
  "onboarding.taxId": "Tax ID / business number",
  "onboarding.taxCountry": "Tax country",
  "onboarding.businessWebsite": "Business website",
  "onboarding.creatingProvider": "Creating provider",
  "onboarding.createProvider": "Create provider profile",
  "onboarding.summary.provider": "Provider",
  "onboarding.summary.operationalStatus": "Operational status",
  "onboarding.summary.businessVerification": "Business verification",
  "onboarding.summary.acceptingJobs": "Accepting jobs",
  "onboarding.yes": "Yes",
  "onboarding.no": "No",
  "onboarding.readiness.businessVerification": "Business verification",
  "onboarding.readiness.businessVerificationGood": "Business verification is complete.",
  "onboarding.readiness.currentStatus": "Current status: {status}.",
  "onboarding.readiness.operationalReadiness": "Operational readiness",
  "onboarding.readiness.operationalReady": "Provider can be considered for jobs once capacity and rate cards are configured.",
  "onboarding.error.notFound": "Provider profile has not been created yet.",
  "onboarding.error.notAvailable": "Provider onboarding is not available for this account yet.",
  "onboarding.error.countryNotSupported": "Provider signup is not available in this country yet. Please check back later.",
  "onboarding.error.saveFailed": "Provider onboarding could not be completed. Please try again; if it repeats, contact support with the request reference.",
  "onboarding.error.statusRefreshFailed": "Verification status could not be refreshed. Please try again; if it repeats, contact support with the request reference.",
  "onboarding.error.withRequestId": "{message} Request ID: {requestId}.",
  "onboarding.websitePreview.verify": "Tinkr will display this as {hostname}. Confirm before saving.",
  "onboarding.websitePreview.openLink": "Open in a new tab",
  "onboarding.websitePreview.invalidScheme": "Use http:// or https:// — Tinkr can only display web addresses.",
  "onboarding.websitePreview.invalidShape": "That doesn't look like a website. Try example.com or https://example.com.",
  "onboarding.websitePreview.httpWarning": "http only — consider https",
  "providerSpecialties.eyebrow": "Provider profile",
  "providerSpecialties.title": "Best-fit job types",
  "providerSpecialties.help": "Choose the kinds of jobs you’re most comfortable taking. This helps Tinkr route better-fit orders to you.",
  "providerSpecialties.onboardingTitle": "What types of print jobs do you specialize in?",
  "providerSpecialties.save": "Save specialties",
  "providerSpecialties.saving": "Saving…",
  "providerSpecialties.saved": "Specialties updated.",
  "providerSpecialties.empty": "Specialist categories aren’t available right now. Please try again shortly.",
  "providerSpecialties.none": "No specialties selected yet.",
  "providerSpecialties.selectedCount": "{count} selected",
  "providerApps.eyebrow": "Order sources",
  "providerApps.title": "Apps you print for",
  "providerApps.onboardingTitle": "Which apps do you want orders from?",
  "providerApps.help": "Tinkr apps and partner apps send orders to makers like you. Every app is on by default. Turn off any launched app whose orders you don’t want, and change this whenever you like. Open an app to see who uses it, what its orders look like and what providers typically earn.",
  "providerApps.summary": "You receive orders from {on} of {total} launched apps.",
  "providerApps.receiveOrders": "Receive orders",
  "providerApps.comingSoonHint": "On when it launches. You can turn it off then.",
  "providerApps.savedOn": "You’ll receive orders from {app}.",
  "providerApps.savedOff": "You won’t receive new orders from {app}.",
  "providerApps.empty": "There are no apps to choose from yet.",
  "providerApps.profileMissing": "Finish your business details first, then choose the apps you print for.",
  "providerApps.error.notSelectable": "This app hasn’t launched yet, so its orders can’t be turned off.",
  "providerApps.status.comingSoon": "Coming soon",
  "providerApps.status.launchDate": "Launching {date}",
  "providerApps.status.paused": "Temporarily unavailable",
  "providerApps.status.openHere": "Open in your country",
  "providerApps.status.notOpenHere": "Not open in your country yet",
  "providerApps.status.live": "Live",
  "providerApps.ownerTinkr": "By Tinkr",
  "providerApps.ownerPartner": "By {partner}, a Tinkr partner",
  "providerApps.details.open": "Details",
  "providerApps.details.close": "Close app details",
  "providerApps.details.audience": "Who uses it",
  "providerApps.details.work": "What orders look like",
  "providerApps.details.pay": "How you’re paid",
  "providerApps.details.earnings": "Typical payout per order",
  "providerApps.details.earningsRange": "{low} to {high}, usually around {median}",
  "providerApps.details.earningsBasis": "Print and shipping paid to providers on {count} orders accepted in the last {days} days. Your payout follows your own rate card.",
  "providerApps.details.earningsNone": "Not enough recent orders to show a typical payout yet.",
  "providerApps.details.offersNote": "Turning an app off stops new offers from it. Offers you already have and orders you’ve accepted aren’t affected.",
  "providerApps.details.comingSoonNote": "You’ll receive its orders automatically when it launches. You can turn it off then.",
  "providerApps.details.visit": "Visit {app}",
  // Provider standard-color support (capability, not inventory). Provider-facing
  // copy: localized in every locale file. Color NAMES stay canonical English
  // (same precedent as materials/specialties — catalog display names are not
  // localized ahead of materials).
  "providerColors.eyebrow": "Provider profile",
  "providerColors.title": "Standard color support",
  "providerColors.onboardingTitle": "Which standard colors can you print?",
  "providerColors.help":
    "Tell customers which standard color families you can print. This is about capability, not stock on hand, so you can update it any time.",
  "providerColors.note.capability":
    "This is a capability signal, not inventory tracking. You are not committing specific filament rolls.",
  "providerColors.note.usuallyAvailable":
    "Mark a color \"usually available\" only if you can reliably fulfill it without delay.",
  "providerColors.note.canSource":
    "\"Can source\" means you can get it, but it may add lead time to the order.",
  "providerColors.note.shade":
    "Customers pick a color family. The exact shade can vary by material, brand, and provider.",
  "providerColors.status.usuallyAvailable": "Usually available",
  "providerColors.status.canSource": "Can source",
  "providerColors.status.notOffered": "Not offered",
  "providerColors.commonHint": "common",
  "providerColors.bulk.commonUsuallyAvailable": "Mark common colors usually available",
  "providerColors.bulk.resetAll": "Reset all to not offered",
  "providerColors.bulk.resetAllConfirm": "Click again to reset every color",
  "providerColors.save": "Save color support",
  "providerColors.saving": "Saving…",
  "providerColors.saved": "Color support updated.",
  "providerColors.empty":
    "Standard colors aren't available right now. Please try again shortly.",
  "operations.eyebrow": "Your shop",
  "operations.title": "Availability and controls",
  "operations.refresh": "Refresh provider operations",
  "operations.noProfile": "Create the provider profile before configuring operational controls.",
  "operations.availability": "Availability",
  "operations.acceptingJobs": "Accepting jobs",
  "operations.paused": "Paused",
  "operations.pauseJobs": "Pause jobs",
  "operations.acceptJobs": "Accept jobs",
  "operations.readyCore": "Core profile controls are ready for matching once rate cards and capacity are configured.",
  "operations.displayName": "Display name",
  "operations.fulfillmentMethods": "Fulfillment methods",
  "operations.fulfillment.pickup": "Pickup",
  "operations.fulfillment.localDelivery": "Local delivery",
  "operations.fulfillment.shipping": "Shipping",
  "operations.shippingCarriers": "Shipping carriers",
  "operations.shippingCarriersPlaceholder": "Royal Mail, DHL, FedEx",
  "operations.serviceCountries": "Service countries",
  "operations.serviceCountriesPlaceholder": "GB, US, SG",
  "operations.serviceCountriesHelp": "Use ISO country codes. Matching only considers destination countries listed here.",
  "operations.internationalShipping": "Accept international orders on enabled routes",
  "operations.minimumOrder": "Minimum order amount",
  "operations.minimumOrderEnable": "Only match jobs that meet a minimum payout to me",
  "operations.minimumOrderAmount": "Minimum payout ({currency})",
  "operations.minimumOrderAmountNoCurrency": "Minimum payout amount",
  "operations.minimumOrderPreview": "Jobs paying you less than {amount} will not be offered to you.",
  "operations.minimumOrderHelp": "Set the smallest payout you will accept for a job. Jobs whose payout to you falls below this are not offered to you at all. This is separate from your rate card's minimum order, which only raises a quote that is too small.",
  "operations.minimumOrderNeedsRateCard": "Set up your rate card first. Your minimum order amount is set in your payout currency, which comes from your active rate card.",
  "operations.multicolorJobs": "Multicolor jobs",
  "operations.multicolorJobsEnable": "Receive multicolor jobs",
  "operations.multicolorJobsHelp": "Multicolor prints change filament on many layers. Each change purges material and adds time, so one job can take a day or more of printer time. Turn this off to stop receiving multicolor offers; your printer settings and accepted orders are not affected.",
  "operations.error.minimumOrderAmount": "Enter a valid minimum payout amount, or turn off the minimum.",
  "operations.error.minimumOrderWhole": "This currency does not use decimals. Enter a whole amount.",
  "operations.shipping.domesticTitle": "Domestic shipping",
  "operations.shipping.domesticHelp": "You serve domestic orders in {country} by default.",
  "operations.shipping.internationalTitle": "International shipping",
  "operations.shipping.notEligible":
    "International shipping is not available for providers in your country yet. Domestic orders are still supported.",
  "operations.shipping.platformDomesticOnly":
    "Your profile is currently used for domestic fulfillment in your ship-from country. Tinkr is building local delivery bubbles country by country. International fulfillment is not enabled on the platform yet.",
  "operations.shipping.chooseDestinations": "Choose destination countries",
  "operations.shipping.moreSoon": "More destination countries may be added in the future.",
  "operations.shipping.dutiesNotice":
    "International orders may take longer to arrive, and any import duties, taxes, or customs fees are paid by the recipient on delivery.",
  "shippingSelector.selectAll": "Select all",
  "shippingSelector.clearAll": "Clear all",
  "shippingSelector.selectedCount": "{selected}/{total} selected",
  "shippingSelector.blockedTooltip": "This destination is not currently enabled by Tinkr.",
  "shippingSelector.empty": "No destination countries are available yet.",
  "shippingRegion.north_america": "North America",
  "shippingRegion.latin_america": "Latin America & Caribbean",
  "shippingRegion.europe": "Europe",
  "shippingRegion.east_asia": "East Asia",
  "shippingRegion.southeast_asia": "Southeast Asia",
  "shippingRegion.south_asia": "South Asia",
  "shippingRegion.central_asia": "Central Asia",
  "shippingRegion.middle_east": "Middle East",
  "shippingRegion.oceania": "Oceania",
  "shippingRegion.africa": "Africa",
  "shippingRegion.other": "Other regions",
  "operations.saving": "Saving",
  "operations.saveControls": "Save provider controls",
  "operations.error.carriers": "Enter up to 20 courier names, each up to 60 characters.",
  "operations.unsavedConfirm": "You have unsaved changes. Leave without saving?",
  "operations.unsavedChanges": "You have unsaved changes.",
  "operations.noChanges": "No changes to save.",
  "operations.saved": "Provider operations saved.",
  "operations.nowAccepting": "Provider marked as accepting jobs.",
  "operations.nowPaused": "Provider paused from new job matching.",
  "operations.error.selectFulfillment": "Select at least one fulfillment method before saving.",
  "operations.error.controlsUnavailable": "Provider controls are not available for this account.",
  "operations.warning.orderEligibility": "Order eligibility is blocked: {reason}.",
  "operations.warning.businessVerification": "Business verification is {status}.",
  "operations.warning.fulfillmentRequired": "At least one fulfillment method is required.",
  "operations.warning.shippingNotSet": "Shipping isn't set as your delivery method yet. Save these controls to turn it on.",
  "operations.warning.internationalDisabled": "International shipping is disabled; only domestic shipped jobs should match.",
  "operations.warning.serviceAreaRequired": "Add at least one service country so matching can route jobs to you.",
  "capabilities.eyebrow": "What you can print",
  "capabilities.title": "Printers and materials",
  "capabilities.refresh": "Refresh provider capabilities",
  "capabilities.locked": "Capability setup unlocks after provider onboarding creates your provider profile.",
  "capabilities.empty": "No printer capabilities yet. Add one so matching can consider your capacity.",
  "capabilities.printer": "Printer model",
  "capabilities.selectPrinter": "Select a printer",
  "capabilities.printerCount": "Printer count",
  "capabilities.concurrentJobs": "Concurrent jobs",
  "capabilities.concurrent": "concurrent",
  "capabilities.maxBuildX": "Build X (mm)",
  "capabilities.maxBuildY": "Build Y (mm)",
  "capabilities.maxBuildZ": "Build Z (mm)",
  "capabilities.layerHeight": "Min layer height (mm)",
  "capabilities.turnaround": "Typical turnaround (days)",
  "capabilities.rushAvailable": "Rush jobs available",
  "capabilities.catalogRequired": "Material catalog is required before adding capabilities. Retry refresh instead of entering non-canonical material IDs.",
  "capabilities.section.printer": "Printer",
  "capabilities.section.printerBody": "Which printer this capability covers and how many you run.",
  "capabilities.section.buildEnvelope": "Build envelope",
  "capabilities.section.buildEnvelopeBody": "Maximum part dimensions you can produce.",
  "capabilities.section.production": "Production",
  "capabilities.section.productionBody": "Layer height, turnaround, and rush availability.",
  "capabilities.section.multicolor": "Multi-color printing",
  "capabilities.section.multicolorBody": "Declare the spool system and simultaneous color capacity for this printer setup.",
  "capabilities.multicolorQuestion": "Does your printer support multi-color printing?",
  "capabilities.multicolorAms": "AMS (Bambu)",
  "capabilities.multicolorOther": "Other multi-material system",
  "capabilities.multicolorNone": "No multi-color system",
  "capabilities.multicolorSlots": "Simultaneous filament slots",
  "capabilities.multicolorSummary": "{system} · {slots} slots",
  "capabilities.section.materials": "Materials",
  "capabilities.section.materialsBody": "Pick everything you can print today; you can add more later.",
  "capabilities.status.operational.active": "Active",
  "capabilities.status.operational.paused": "Paused",
  "capabilities.status.operational.draft": "Draft",
  "capabilities.status.operational.disabled": "Disabled",
  "capabilities.rushYes": "Rush available",
  "capabilities.rushNo": "No rush",
  "capabilities.save": "Add capability",
  "capabilities.saving": "Saving capability",
  "capabilities.saved": "Capability saved.",
  "capabilities.update": "Save changes",
  "capabilities.updating": "Saving changes",
  "capabilities.updated": "Capability updated.",
  "capabilities.edit": "Edit capability",
  "capabilities.cancelEdit": "Cancel edit",
  "capabilities.delete": "Delete capability",
  "capabilities.deleteConfirm": "Click again to confirm delete",
  "capabilities.error.printer": "Select a printer model.",
  "capabilities.error.materials": "Select at least one material.",
  "capabilities.error.catalogUnavailable": "Material catalog is unavailable. Refresh before adding a capability.",
  "capabilities.error.counts": "Printer count and concurrent jobs must be greater than zero.",
  "capabilities.error.multicolorSlots": "Enter 2 to 32 simultaneous filament slots.",
  "capabilities.error.conflict": "This printer changed somewhere else. Refresh and try again.",
  "capabilities.error.unavailable": "Capability setup is available after provider profile creation.",
  "rateCard.eyebrow": "Your pricing",
  "rateCard.title": "Set your prices",
  "rateCard.appFit.title": "App order fit",
  "rateCard.appFit.explainer": "Some apps use fixed customer prices. Matching may skip a rate card when its unavoidable minimum order and delivery cost is above an app budget.",
  "rateCard.appFit.doodles": "Tinkr Doodles",
  "rateCard.appFit.limited": "{app} · {country}: some current fixed-price orders will skip this rate card.",
  "rateCard.appFit.blocked": "{app} · {country}: current fixed-price orders will skip this rate card.",
  "rateCard.appFit.shippingMissing": "{app} · {country}: add an active tracked domestic delivery rate to receive these orders.",
  "rateCard.appFit.currencyMismatch": "{app} · {country}: this rate card currency does not match the app country.",
  "rateCard.appFit.clear": "No app-fit warnings for your active rate cards. Matching still prices each complete job.",
  "rateCard.appFit.unavailable": "This guidance is temporarily unavailable. Your rate card still works normally.",
  "rateCard.appFit.privacy": "Set sustainable rates from your costs. Doodles shows its current payout ceilings; each order uses its locked price.",
  "rateCard.refresh": "Refresh provider rate cards",
  "rateCard.locked": "Rate-card setup unlocks after provider onboarding creates your provider profile.",
  "rateCard.currency": "Currency",
  "rateCard.minimumOrderMinor": "Minimum order amount",
  "rateCard.setupFeeMinor": "Setup fee",
  "rateCard.machineHourMinor": "Machine hour rate (optional)",
  "rateCard.machineHourHelp": "What you want to earn for each hour of estimated print time. Tinkr estimates print time for a Bambu Lab P2S (0.20 mm Standard profile with tree supports), whatever printer you use, so set this rate against that estimate. Leave blank if you are not pricing by machine time yet.",
  "rateCard.laborHourMinor": "Labor hour rate (optional)",
  "rateCard.laborHourHelp": "Pay for your own hands-on time, like setup and finishing work. Leave blank if you are not charging for labor separately yet.",
  "rateCard.materialMarkupPct": "Material markup (0.2 = 20%)",
  "rateCard.materialId": "Material ID",
  "rateCard.pricePerGramMinor": "Price per gram",
  "rateCard.pricePerCm3Minor": "Price per cm³",
  "rateCard.materialPriceBasis": "Material pricing unit",
  "rateCard.materialPriceBasis.per_gram": "Per gram",
  "rateCard.materialPriceBasis.per_cm3": "Per cm³",
  "rateCard.handlingFeeMinor": "Handling fee",
  "rateCard.origin": "Origin",
  "rateCard.destination": "Destination",
  "rateCard.serviceLevel": "Service level",
  "rateCard.shippingAmountMinor": "Shipping fee",
  "rateCard.shippingMaxWeightG": "Max shipping weight (g)",
  "rateCard.amountsNote": "Enter prices in your card's currency, like 12.50. Currencies without decimals, like JPY, take whole amounts. We convert and store exact values.",
  "rateCard.minimumScopeNote": "This starter setup saves one material rate and one shipping lane. You can come back and refine pricing any time.",
  "rateCard.amountPreview": "Equals {amount}",
  "rateCard.markupPreview": "Adds {percent}% on top of material cost",
  "rateCard.postProcessingNote": "Only the finishing services selected above are offered for matching. A job that needs another service will not be sent to you.",
  "rateCard.noDefaults": "No country pricing benchmark is available for this account yet. Enter your own currency and prices below. Starting simple is fine: a material price and minimum order alone make a valid rate card.",
  "rateCard.saving": "Saving rate card",
  "rateCard.saved": "Rate card saved.",
  "rateCard.addTitle": "Add rate card",
  "rateCard.editTitle": "Edit rate card",
  "rateCard.editorSubtitle": "Set the material price, shared card pricing, finishing, and shipping.",
  "rateCard.add": "Add rate card",
  "rateCard.update": "Save changes",
  "rateCard.cancel": "Cancel",
  "rateCard.close": "Close rate card editor",
  "rateCard.error.currency": "Enter a three-letter ISO currency code.",
  "rateCard.error.material": "Enter the material's short code in lowercase letters, numbers, hyphens, or underscores, such as pla or petg_cf.",
  "rateCard.error.minimumOrder": "Minimum order must be greater than zero.",
  "rateCard.error.setupFee": "Setup fee cannot be higher than the minimum order.",
  "rateCard.error.pricePerGram": "Material price must be greater than zero.",
  "rateCard.error.markup": "Material markup must be 5.0 or lower for this setup panel.",
  "rateCard.error.amount": "Enter amounts as plain numbers in {currency}, like 12.50.",
  "rateCard.error.wholeCurrency": "{currency} amounts have no decimal places. Enter a whole number.",
  "rateCard.error.ambiguousAmount": "Is that a thousands separator? Write {currency} amounts without one, for example 2000 or 2.50.",
  "rateCard.error.versionConflict": "These prices were changed somewhere else (another tab or an automatic update) after you opened them. Reload the latest prices, check your change and save again.",
  "rateCard.error.saveConflict": "Your prices couldn't be saved because something changed in the meantime. Refresh and try again.",
  "rateCard.error.guidedDropsMaterial": "This approach doesn't cover every material you price today. Use the full editor to keep those prices.",
  "rateCard.error.guidanceChanged": "The suggested prices were just updated. Review the new suggestion and save again.",
  "rateCard.error.cardRemoved": "These prices were removed somewhere else. Add them again if you still need them.",
  "rateCard.reloadLatest": "Reload latest prices",
  "rateCard.reloadedReview": "Latest prices loaded. Your changes are still in the form. Check them and save again.",
  "rateCard.discardConfirm": "Discard your unsaved price changes?",
  "rateCard.materialPricesLabel": "Material prices · {count}",
  "rateCard.addDisabled.loading": "Loading your prices…",
  "rateCard.addDisabled.noMaterials": "No materials are available for your country yet, so there is nothing to price.",
  "rateCard.approach.title": "Pricing approach",
  "rateCard.approach.body": "Your prices are set. Compare suggested prices or switch to a guided approach at any time.",
  "rateCard.approach.repick": "Re-pick approach",
  "rateCard.approach.hide": "Hide approaches",
  "rateCard.error.unavailable": "Rate-card setup is available after provider profile creation.",
  "rateCard.error.editCurrency": "An existing rate card's currency cannot be changed. Add a new rate card for another currency.",
  "rateCard.error.materialExists": "That material already has a rate card in this currency. Edit the existing rate card instead.",
  "rateCard.materialRemove": "Remove material rate",
  "rateCard.materialRemoveConfirm": "Click again to confirm removal",
  "rateCard.materialRemoveConfirmShort": "Confirm",
  "rateCard.materialRemoved": "Material rate removed.",
  "rateCard.error.materialRemoveLast": "This rate card needs at least one priced material. Add another material before removing this one.",
  "rateCard.postProcessingPricing": "Post-processing pricing",
  "rateCard.postProcessingPricingHint": "Check each finishing service you offer. Pricing appears when you select one.",
  "rateCard.postProcessingDoodlesHint": "Tinkr Doodles multicolor orders arrive as a color-mapped 3MF for your filament slots; no painting service is required.",
  "rateCard.postProcessing.service.support_removal": "Support removal",
  "rateCard.postProcessing.service.sanding": "Sanding",
  "rateCard.postProcessing.service.painting": "Painting",
  "rateCard.postProcessing.service.vapor_smoothing": "Vapor smoothing",
  "rateCard.postProcessing.service.dyeing": "Dyeing",
  "rateCard.postProcessing.service.epoxy_coating": "Epoxy coating",
  "rateCard.postProcessing.service.metal_plating": "Metal plating",
  "rateCard.postProcessing.service.threading_inserts": "Threaded inserts",
  "rateCard.postProcessing.service.heat_treatment": "Heat treatment",
  "rateCard.postProcessing.service.uv_curing": "UV curing",
  "rateCard.postProcessing.service.assembly": "Assembly",
  "rateCard.postProcessing.service.laser_engraving": "Laser engraving",
  "rateCard.postProcessing.service.other": "Other finishing service",
  "rateCard.postProcessingAmountMinor": "Amount",
  "rateCard.postProcessingMode": "Charged",
  "rateCard.postProcessingMode.perOrder": "Per order",
  "rateCard.postProcessingMode.perPart": "Per part",
  "rateCard.error.postProcessingCode": "Post-processing codes must be lowercase letters, digits, hyphens, or underscores (2-33 chars).",
  "rateCard.error.postProcessingDuplicate": "Post-processing codes must be unique.",
  "rateCard.error.postProcessingAmount": "Post-processing amount must be a non-negative whole number.",
  "rateCard.section.fundamentals": "Pricing fundamentals",
  "rateCard.section.fundamentalsBody": "Currency and the floor amounts every quote uses.",
  "rateCard.section.hourly": "Hourly rates",
  "rateCard.section.hourlyBody": "Machine and labor time used by the quote calculator.",
  "rateCard.section.material": "Material rate",
  "rateCard.section.materialBody": "Per-gram pricing for the material this rate card applies to.",
  "rateCard.section.shipping": "Shipping lane",
  "rateCard.section.shippingBody": "One origin → destination lane with service level and fee.",
  "shippingRates.eyebrow": "Fulfilment",
  "shippingRates.title": "Shipping rates",
  "shippingRates.body": "Add tracked domestic shipping by entering only the fee and maximum weight. Your verified account country and its currency are applied automatically.",
  "shippingRates.trackedDomestic": "Tracked domestic shipping",
  "shippingRates.feeIncludingTracking": "Shipping fee, including tracking",
  "shippingRates.maximumParcelWeight": "Maximum parcel weight",
  "shippingRates.trackingNote": "You’ll provide the carrier and tracking number when shipping.",
  "shippingRates.origin": "Ships from your verified account country: {country}",
  "shippingRates.refresh": "Refresh shipping rates",
  "shippingRates.count": "{count} shipping rates",
  "shippingRates.add": "Add shipping rate",
  "shippingRates.edit": "Edit shipping rate",
  "shippingRates.remove": "Remove shipping rate",
  "shippingRates.laneRoute": "{from} to {to}",
  "shippingRates.editLane": "Edit shipping rate: {lane}",
  "shippingRates.removeLane": "Remove shipping rate: {lane}",
  "shippingRates.empty": "No shipping rates yet. Add tracked domestic shipping to receive local orders.",
  "shippingRates.error.laneChanged": "This shipping rate was changed or removed somewhere else. Close this editor and try again.",
  "shippingRates.rulesSummary": "How shipping rates work",
  "shippingRates.saved": "Shipping rates saved.",
  "shippingRates.invalid": "Enter a valid shipping fee and a positive maximum weight.",
  "shippingRates.defaultsMissing": "We could not determine your account country and currency. Complete or refresh your provider setup first.",
  "shippingRates.error": "Could not load shipping rates.",
  "rateCard.serviceLevel.standard": "Standard",
  "rateCard.serviceLevel.tracked": "Tracked",
  "rateCard.serviceLevel.express": "Express",
  "quoteSimulator.eyebrow": "Estimate earnings",
  "quoteSimulator.title": "Estimate your payout",
  "quoteSimulator.lede":
    "See what an order pays on your own rate card. Start with a typical Doodles figurine, or enter a job of your own.",
  "quoteSimulator.doodles.heading": "Typical Doodles orders",
  "quoteSimulator.doodles.small": "Small figurine",
  "quoteSimulator.doodles.medium": "Medium figurine",
  "quoteSimulator.doodles.size": "Up to {cm} cm",
  "quoteSimulator.doodles.typical": "About {grams} g of PLA, {duration} printing",
  "quoteSimulator.doodles.estimate": "Estimate payout",
  "quoteSimulator.doodles.hint":
    "Typical figures from slicing sample figurines with Tinkr's print profile. Each real order is priced from its own slice, so busier drawings use more plastic and pay more.",
  "quoteSimulator.doodles.emptyHint": "Create a rate card to see what these orders pay you.",
  "quoteSimulator.doodles.needsPla":
    "Doodles figurines print in PLA. Add PLA to your rate card to estimate and receive them.",
  "quoteSimulator.doodles.payNote":
    "Doodles orders pay for plastic, print time and tracked shipping. Support removal and other hands-on work are not paid separately.",
  "quoteSimulator.doodles.needsTrackedShipping":
    "Doodles orders ship tracked within your country, so this estimate leaves shipping out. Add a tracked shipping rate for your country to receive them.",
  "quoteSimulator.duration": "{hours} h {minutes} min",
  "quoteSimulator.custom.heading": "Your own job",
  "quoteSimulator.custom.samples": "Fill in a sample job",
  "quoteSimulator.presets.phoneStand": "Phone stand",
  "quoteSimulator.presets.cableClip": "Cable clips",
  "quoteSimulator.presets.boardGameToken": "Board game tokens",
  "quoteSimulator.refresh": "Refresh provider rate cards",
  "quoteSimulator.locked": "Quote simulation unlocks after provider onboarding creates your provider profile.",
  "quoteSimulator.noRateCards": "Estimates use your own prices, so create a rate card first.",
  "quoteSimulator.emptyTitle": "Set your prices to estimate a payout",
  "quoteSimulator.emptyCta": "Create your rate card",
  "quoteSimulator.rateCard": "Rate card",
  "quoteSimulator.rateCardOption": "{currency}, version {version}",
  "quoteSimulator.rateCardUsing": "Priced on your active rate card ({currency}, version {version}).",
  "quoteSimulator.material": "Material",
  "quoteSimulator.quantity": "Quantity",
  "quoteSimulator.filamentG": "Estimated filament (g)",
  "quoteSimulator.printHours": "Print time (hours)",
  "quoteSimulator.laborMinutes": "Labor time (min)",
  "quoteSimulator.advanced": "More options",
  "quoteSimulator.priority": "Priority",
  "quoteSimulator.priority.price": "Price",
  "quoteSimulator.priority.speed": "Speed",
  "quoteSimulator.priority.balanced": "Balanced",
  "quoteSimulator.priority.quality": "Quality",
  "quoteSimulator.postProcessingPlaceholder": "sanding, painting",
  "quoteSimulator.postProcessingPicker.label": "Finishing services",
  "quoteSimulator.postProcessingPicker.empty":
    "No post-processing on this rate card. Configure pricing on the rate card first.",
  "quoteSimulator.includeShipping": "Include shipping",
  "quoteSimulator.origin": "Ships from",
  "quoteSimulator.destination": "Destination",
  "quoteSimulator.serviceLevel": "Service",
  "quoteSimulator.service.standard": "Standard",
  "quoteSimulator.service.tracked": "Tracked",
  "quoteSimulator.service.express": "Express",
  "quoteSimulator.packageWeightG": "Package weight (g)",
  "quoteSimulator.run": "Estimate my payout",
  "quoteSimulator.running": "Estimating…",
  "quoteSimulator.payoutTotal": "Your payout",
  "quoteSimulator.perPrintHour": "About {amount} per print hour",
  "quoteSimulator.nonBinding": "Estimate only. Nothing is created: no order, quote or payout.",
  "quoteSimulator.line.print": "Print",
  "quoteSimulator.line.shipping": "Shipping",
  "quoteSimulator.warning.provider_minimum_order_applied": "Provider minimum order applied",
  "quoteSimulator.warning.shipping_not_simulated": "Shipping was not included in this simulation",
  "quoteSimulator.warning.shipping_rate_not_found": "No matching shipping rate was found for this lane",
  "quoteSimulator.warning.international_shipping": "This is an international shipping lane",
  "quoteSimulator.warning.import_costs_excluded": "Import duties, taxes, and customs fees are excluded from this quote",
  "quoteSimulator.warning.postProcessingNotPriced": "{service} is not priced on this rate card",
  "quoteSimulator.excluded.import_duty": "Import duty",
  "quoteSimulator.excluded.import_vat_gst": "Import VAT/GST",
  "quoteSimulator.excluded.customs_fees": "Customs fees",
  "quoteSimulator.excluded.carrier_brokerage_fees": "Carrier brokerage fees",
  "quoteSimulator.error.quantity": "Enter a whole quantity from 1 to 10,000.",
  "quoteSimulator.error.filament": "Enter the filament weight in grams (0 or more).",
  "quoteSimulator.error.hours": "Enter the print time in hours (0 or more).",
  "quoteSimulator.error.minutes": "Enter the labor time in minutes (0 or more).",
  "quoteSimulator.error.origin": "Enter the two-letter code of the country you ship from.",
  "quoteSimulator.error.weight": "Enter the package weight in grams, or leave it empty.",
  "quoteSimulator.error.stale": "This rate card changed after the page loaded. The latest prices are loading; try again in a moment.",
  "quoteSimulator.error.unavailable": "Quote simulation is available after provider profile and rate-card setup.",
  "workQueue.eyebrow": "Your work",
  // Provider financial records live in the Payouts section, so the page
  // header is just the work queue.
  "workQueue.title": "Work queue",
  "workQueue.refresh": "Refresh provider work queue",
  "workQueue.locked": "Work queue unlocks after provider onboarding creates your provider profile.",
  "workQueue.pendingOffers": "Pending offers",
  "workQueue.acceptedJobs": "Accepted jobs",
  "workQueue.pendingPayout": "Pending payout",
  "workQueue.offerHint": "Accepting one offer assigns the fulfillment group to your provider account.",
  "workQueue.acceptedHint": "Accepted offers are your active job queue. Track each one here through delivery.",
  "workQueue.payoutStatus": "Payout status",
  "workQueue.payoutHint": "Provider-visible payout states only; platform margin and settlement details stay internal.",
  "workQueue.payoutLegend": "Status guide: Pending, the job is not finished or confirmed yet. Settled, payment is confirmed and your payout is being prepared. Payout queued, the transfer is on its way. Paid, the money was sent to your account. Held, paused while our support team reviews; contact support if this lasts more than a few days.",
  "workQueue.qualityIncidents": "Quality incidents",
  "workQueue.qualityHint": "Respond to quality holds or support reviews before payout release.",
  "workQueue.noPendingOffers": "No pending offers right now.",
  "workQueue.noAcceptedJobs": "No accepted jobs yet.",
  "workQueue.noPayables": "No provider payables yet.",
  "workQueue.noQualityIncidents": "No issues on your orders.",
  "workQueue.status.offer.pending": "Pending",
  "workQueue.status.offer.accepted": "Accepted",
  "workQueue.status.offer.declined": "Declined",
  "workQueue.status.offer.expired": "Expired",
  "workQueue.status.offer.reclaimed": "Reclaimed",
  "workQueue.status.payable.pending": "Pending",
  "workQueue.status.payable.eligible": "Eligible",
  "workQueue.status.payable.eligible_pending_funding": "Awaiting funding",
  "workQueue.status.payable.stripe_transfer_created": "In transit",
  "workQueue.status.payable.paid": "Paid",
  "workQueue.status.payable.reversed": "Reversed",
  "workQueue.status.payable.claimed": "Claimed",
  "workQueue.status.payable.settled": "Settled",
  "workQueue.status.payable.payout_queued": "Payout queued",
  "workQueue.status.payable.held": "Held",
  "workQueue.status.payable.voided": "Voided",
  "workQueue.status.payable.expired": "Expired",
  "workQueue.status.payable.refunded": "Refunded",
  "workQueue.status.incident.open": "Open",
  "workQueue.status.incident.acknowledged": "Acknowledged",
  "workQueue.status.incident.provider_responded": "Provider responded",
  "workQueue.status.incident.resolved": "Resolved",
  "workQueue.status.incident.voided": "Voided",
  "workQueue.severity.minor": "Minor",
  "workQueue.severity.major": "Major",
  "workQueue.severity.critical": "Critical",
  "workQueue.incidentAcknowledged": "Quality incident acknowledged. The queue has been refreshed.",
  "workQueue.incidentResponded": "Quality response submitted. The queue has been refreshed.",
  "workQueue.offerAccepted": "Offer accepted. The queue has been refreshed.",
  "workQueue.offerDeclined": "Offer declined. The queue has been refreshed.",
  "workQueue.expiringSoon": "Expiring in {hours}h. Review before the offer is automatically reclaimed.",
  "workQueue.expiredWarning": "This offer has expired or is about to be reclaimed.",
  "workQueue.expires": "Expires",
  "workQueue.priority": "Priority:",
  "workQueue.quality": "Quality:",
  "workQueue.shipTo": "Ship to:",
  "workQueue.notSpecified": "not specified",
  "workQueue.part": "Part",
  "workQueue.materialTbd": "material TBD",
  "workQueue.declineReason": "Decline reason",
  "workQueue.declineReasonPlaceholder": "Choose a reason",
  "workQueue.declineNote": "Decline note",
  "workQueue.declineNotePlaceholder": "Optional context for operators",
  "workQueue.decline.capacity": "Capacity full",
  "workQueue.decline.material": "Material unavailable",
  "workQueue.decline.deadline": "Cannot meet deadline",
  "workQueue.decline.quality": "Quality requirement needs review",
  "workQueue.decline.other": "Other operational reason",
  "workQueue.acceptConfirmBody": "By accepting, you commit to start printing within {productionHours} hours and hand the order to delivery within {shipHours} hours. Orders past their deadline can be reassigned to another provider, and repeated missed deadlines will pause your account from receiving new orders.",
  "workQueue.confirmAccept": "Confirm",
  "workQueue.cancel": "Cancel",
  "workQueue.printingDueBy": "Start printing by {date}",
  "workQueue.handOffDueBy": "Ship by {date}",
  "workQueue.overdueBadge": "Overdue",
  "workQueue.markingShipped": "Marking…",
  "workQueue.dialog.markShipped.title": "Mark as shipped?",
  "workQueue.dialog.markShipped.body": "The customer or partner is told the order is on its way and gets the tracking details. The order counts as delivered automatically {days} days after shipping unless the customer confirms sooner. You can still correct the tracking details afterwards.",
  "workQueue.dialog.markShipped.bodyNoWindow": "The customer or partner is told the order is on its way and gets the tracking details. The order counts as delivered automatically after the delivery window unless the customer confirms sooner. You can still correct the tracking details afterwards.",
  "workQueue.shippedPill": "Shipped",
  "workQueue.deliveredPill": "Delivered",
  "workQueue.completedPill": "Completed",
  "workQueue.cancelledPill": "Cancelled",
  "workQueue.cancelled.refundedTitle": "Cancelled and refunded",
  "workQueue.cancelled.refundPendingTitle": "Cancelled — refund not yet confirmed",
  "workQueue.cancelled.refundPendingBody": "Support cancelled this order. The buyer's refund is still processing or needs review. Do not continue fulfillment; no payout is shown while it is resolved.",
  "workQueue.cancelled.refundedBody": "Support cancelled this order and refunded the buyer. Nothing else is needed from you, and your account standing is not affected.",
  "workQueue.startPrinting": "Start printing",
  "workQueue.startingPrinting": "Starting…",
  "workQueue.dialog.startPrinting.title": "Start printing?",
  "workQueue.dialog.startPrinting.body": "The customer or partner is told that production has started. You must then hand the order to delivery within {shipHours} hours of accepting it.",
  "workQueue.dialog.startPrinting.bodyDueBy": "The customer or partner is told that production has started. You must then hand the order to delivery by {date}.",
  "workQueue.notStartedPill": "Not started",
  "workQueue.printingPill": "Printing",
  "workQueue.jobPrinting": "Job marked as printing.",
  "workQueue.shipment.title": "Shipment",
  "workQueue.shipment.hint": "Add the courier and tracking details, then mark the order shipped.",
  "workQueue.shipment.carrier": "Carrier",
  "workQueue.shipment.number": "Tracking number",
  "workQueue.shipment.url": "Tracking URL",
  "workQueue.shipment.record": "Mark shipped",
  "workQueue.shipment.track": "Track package",
  "workQueue.shipment.loading": "Loading shipment…",
  "workQueue.shipment.required": "Add a carrier and tracking number or URL.",
  "workQueue.shipment.edit": "Edit tracking",
  "workQueue.shipment.editHint": "Correct the carrier or tracking details. The customer's tracking view updates immediately.",
  "workQueue.shipment.saveChanges": "Save changes",
  "workQueue.shipment.cancelEdit": "Keep as recorded",
  "workQueue.shipment.changeCourier": "Change courier",
  "workQueue.shipment.savedCourier": "From your delivery settings",
  "workQueue.shipment.chooseCourier": "Choose a courier",
  "workQueue.shipment.savedCouriers": "Your saved couriers",
  "workQueue.shipment.countryCouriers": "Other local couriers",
  "workQueue.shipment.otherCourier": "Use another courier",
  "workQueue.shipment.courierName": "Courier name",
  "workQueue.shipment.courierNameTooLong": "Use a courier name of 80 characters or fewer.",
  "workQueue.shipment.courierScope": "This changes the courier for this shipment only.",
  "workQueue.shipment.couriersUnavailable": "We couldn’t load your couriers. You can enter the courier name manually.",
  "workQueue.shipment.couriersLoading": "Loading your couriers…",
  "workQueue.shipment.addTrackingUrl": "Add tracking link (optional)",
  "workQueue.shipment.optionalTrackingUrl": "Tracking URL (optional)",
  "packingCheck.eyebrow": "Before you ship",
  "packingCheck.title": "Quick packing check",
  "packingCheck.close": "Close",
  "packingCheck.step.inspect": "Inspect and clean",
  "packingCheck.step.wrap": "Wrap every face",
  "packingCheck.step.box": "Rigid box, 3 cm gap",
  "packingCheck.step.fill": "Fill the voids",
  "packingCheck.step.seal": "H-tape and label",
  "packingCheck.step.photo": "Photo before sealing",
  "packingCheck.dontShowAgain": "Don't show this again",
  "packingCheck.openGuide": "Open the full guide",
  "packingCheck.continue": "Got it, continue",
  "workQueue.evidence.title": "Print evidence",
  "workQueue.evidence.hint": "Add photos of the print. Buyers and support can see these as proof of work.",
  "workQueue.evidence.addPhotos": "Add photos",
  "workQueue.evidence.uploading": "Uploading…",
  "workQueue.evidence.verified": "Verified",
  "workQueue.evidence.photo": "Photo",
  "workQueue.evidence.none": "No photos yet.",
  "workQueue.evidence.remove": "Remove photo {n}",
  "workQueue.evidence.attachOnShip": "These photos are attached when you mark the order shipped.",
  "workQueue.evidence.uploadStaged": "Upload photos",
  "workQueue.evidence.limit": "An order can have up to {max} photos.",
  "workQueue.evidence.required": "Add at least one photo of the finished print. You need it to mark the order shipped.",
  "workQueue.saving": "Saving",
  "workQueue.accept": "Accept offer",
  "workQueue.decline": "Decline",
  "workQueue.jobPrefix": "Job",
  "workQueue.partsUnknown": "Parts not loaded",
  "workQueue.partsCount": "{count} part groups · {quantity} total units",
  "workQueue.mixedOrEmptyPayout": "Mixed",
  "workQueue.filter.acceptedLabel": "Show accepted jobs",
  "workQueue.filter.acceptedAll": "All accepted",
  "workQueue.filter.acceptedExpiringSoon": "Due within 48h",
  "workQueue.filter.acceptedScheduled": "Scheduled (>48h)",
  "workQueue.filter.payableLabel": "Filter payable status",
  "workQueue.filter.payableAll": "All payables",
  "workQueue.filter.payablePending": "Pending",
  "workQueue.filter.payableEligible": "Eligible",
  "workQueue.filter.payableInTransit": "In transit",
  "workQueue.filter.payablePaid": "Paid",
  "workQueue.filter.payableReversed": "Reversed",
  "workQueue.filter.payableSettled": "Settled",
  "workQueue.filter.payablePayoutQueued": "Payout queued",
  "workQueue.filter.payableHeld": "Held",
  "workQueue.filter.noAcceptedMatch": "No accepted jobs match this filter.",
  "workQueue.filter.noPayableMatch": "No payables match this filter.",
  "workQueue.filter.countShown": "Showing {shown} of {total}",
  "workQueue.error.unavailable": "Provider work queue is not available for this account yet.",
  "workQueue.error.declineReasonRequired": "Choose a decline reason before declining the offer.",
  "workQueue.error.incidentResponseRequired": "Add a response before submitting the quality incident update.",
  "workQueue.error.doodlesCurrencyMismatch": "This offer uses a different currency from the customer's fixed Doodles price. Ask support to review it.",
  "workQueue.error.doodlesQuoteOverPrice": "This provider quote is higher than the customer's fixed Doodles price, so it cannot be accepted. Ask support to send a corrected offer.",
  "workQueue.error.doodlesPayoutOverPrice": "The guaranteed payout is higher than the customer's fixed Doodles price, so this offer cannot be accepted. Ask support to send a corrected offer.",
  "workQueue.error.doodlesExcludedCosts": "This quote leaves required costs out of the fixed Doodles price, so it cannot be accepted. Ask support to correct it.",
  "workQueue.error.doodlesQuoteInvalid": "This offer contains invalid price details and cannot be accepted. Ask support to send a corrected offer.",
  "workQueue.error.doodlesPriceMissing": "The customer's fixed Doodles price is missing. Ask support to review this offer.",
  "workQueue.error.paymentNotReady": "Customer payment is not ready yet. Try again later or ask support to review the order.",
  "workQueue.error.offerExpired": "This offer has expired and can no longer be accepted.",
  "workQueue.error.doodlesNotAdmitted": "Your provider account is not approved for Doodles production. Contact support for a review.",
  "workQueue.error.doodlesPlanNotSupported": "Your current printer or color support does not cover this Doodles print plan. Review your capabilities or contact support.",
  "workQueue.error.premiumNotAdmitted": "This is a premium order. Only quality verified print providers can accept it.",
  "workQueue.error.quotePremiumMismatch": "This offer was priced before the order changed. Refresh your work queue to see the current offer.",
  "workQueue.premiumBadge": "Premium order",
  "workQueue.error.providerNotReady": "Your provider account is not currently eligible to accept this order. Review your account status or contact support.",
  "workQueue.error.agreementRequired": "To accept this order, first accept the current supplier agreement. The offer stays open until it expires.",
  "workQueue.action.reviewAgreement": "Review and accept the agreement",
  "workQueue.incidentPrefix": "Incident",
  "workQueue.incidentReason": "Reason:",
  "workQueue.incidentStatus": "Status:",
  "workQueue.incidentOpened": "Opened:",
  "workQueue.incidentCurrentResponse": "Current response:",
  "workQueue.incidentResolution": "Resolution:",
  "workQueue.incidentResponse": "Your response",
  "workQueue.incidentResponsePlaceholder": "Explain what happened, remake plan, evidence, or dispatch correction.",
  "workQueue.incidentReasonNote": "Note for Tinkr support (optional)",
  "workQueue.incidentReasonNotePlaceholder": "Optional short note for support",
  "workQueue.acknowledgeIncident": "Acknowledge",
  "workQueue.respondIncident": "Submit response",
  "workQueue.report.open": "Report a problem",
  "workQueue.report.hint": "Let us know if this job can't be finished as ordered. Our support team will step in to help sort it out.",
  "workQueue.report.reason": "What's the problem?",
  "workQueue.report.reasonPlaceholder": "Choose what happened",
  "workQueue.report.reason.print_failure": "The print failed",
  "workQueue.report.reason.material_unavailable": "I can't get the material",
  "workQueue.report.reason.spec_impossible": "This can't be printed as ordered",
  "workQueue.report.reason.customer_unreachable": "I can't reach the customer",
  "workQueue.report.reason.other": "Something else",
  "workQueue.report.summary": "What happened?",
  "workQueue.report.summaryPlaceholder": "Describe the problem so support can help, for example what failed and what you have tried.",
  "workQueue.report.minHint": "Add at least 10 characters.",
  "workQueue.report.submit": "Send report",
  "workQueue.report.success": "Problem reported. Our support team will follow up, and it now shows on the QA tab.",
  "workQueue.report.error.incomplete": "Choose a reason and describe the problem in at least 10 characters.",
  "workQueue.actuals.open": "Print took longer than expected? Let us know",
  "workQueue.actuals.title": "Print time vs estimate",
  "workQueue.actuals.hint": "This helps us tune future estimates. It never changes your payout for this order.",
  "workQueue.actuals.tookLonger": "The print took longer than expected",
  "workQueue.actuals.hours": "Actual print time in hours (optional)",
  "workQueue.actuals.grams": "Filament used in grams (optional)",
  "workQueue.actuals.submit": "Send",
  "workQueue.actuals.invalid": "Enter realistic positive values for hours and grams.",
  "workQueue.actuals.thanks": "Thanks! Your report helps us tune future estimates.",
  "workQueue.actuals.reportedLongerYes": "You told us this print took longer than estimated.",
  "workQueue.actuals.reportedLongerNo": "You told us this print finished within the estimate.",
  "workQueue.actuals.reportedLine": "Reported: {value}",
  "workQueue.actuals.estimatedLine": "Estimated: {value}",
  "workQueue.withdraw.open": "I can't fulfill this order",
  "workQueue.withdraw.warningTitle": "Returning this order records a strike",
  "workQueue.withdraw.warningBody": "The order goes back to matching so another provider can take it, and a fulfillment strike is recorded on your account. Strikes lower your matching priority, and reaching the strike limit pauses new orders. Payouts you have already earned on completed orders are never affected.",
  "workQueue.withdraw.reason": "Why can't you fulfill it?",
  "workQueue.withdraw.reasonPlaceholder": "e.g. printer failure, filament unavailable, unexpected downtime",
  "workQueue.withdraw.submit": "Return order (records a strike)",
  "workQueue.withdraw.submitting": "Returning order",
  "workQueue.withdraw.success": "The order was returned and is being rematched. A fulfillment strike was recorded on your account.",
  "workQueue.withdraw.error.reasonRequired": "Enter a short reason (at least 3 characters).",
  "workQueue.withdraw.error.paymentProcessed": "This order can't be returned automatically because its payment has already been processed. Contact support and we'll take it off your queue. No strike is recorded.",
  "workQueue.withdraw.error.stateChanged": "This order changed state since the page loaded, so it can't be returned from here. Refresh to see its current status.",
  "workQueue.withdraw.error.failed": "The order couldn't be returned. Try again, or contact support if it keeps failing.",
  "integrations.eyebrow": "Workshop integrations",
  "integrations.title": "Integrations",
  "integrations.intro": "Connect the tools you already run, like slicers, print-farm software, and order systems, directly to your Tinkr account. We're deciding what to build first and want your input.",
  "integrations.note": "Nothing here is required: everything works from this dashboard today. Integrations will be optional power tools.",
  "integrations.interestLabel": "Which tools would you want connected?",
  "integrations.placeholder": "e.g. OrcaSlicer, OctoPrint, my farm management software",
  "integrations.submit": "Request integrations",
  "integrations.sending": "Sending",
  "integrations.success": "Got it. We'll let you know when integrations for your tools are ready.",
  "integrations.error": "Could not send your request. Try again in a moment.",
  "integrations.signedOutNote": "Sign in to tell us which tools you'd connect.",
  "pluginKeys.eyebrow": "Provider plugin access",
  "pluginKeys.title": "API keys for local plugins",
  "pluginKeys.refresh": "Refresh provider plugin keys",
  "pluginKeys.locked": "Plugin keys unlock after the provider is active and business verification is complete.",
  "pluginKeys.intro": "Plugin keys connect optional workshop tools, such as a slicer integration, to your Tinkr account. If you do not use any local tools, you can ignore this section.",
  "pluginKeys.name": "Key name",
  "pluginKeys.namePlaceholder": "Workshop slicer bridge",
  "pluginKeys.expires": "Optional expiry date",
  "pluginKeys.scopes": "Scopes",
  "pluginKeys.create": "Create plugin key",
  "pluginKeys.creating": "Creating key",
  "pluginKeys.created": "Plugin key created. Store the plaintext now; it will not be shown again.",
  "pluginKeys.rotated": "Plugin key rotated. Store the new plaintext now; the old key is revoked.",
  "pluginKeys.revoked": "Plugin key revoked.",
  "pluginKeys.revealedTitle": "Plaintext key shown once",
  "pluginKeys.autoHide": "This key will hide automatically after 60 seconds.",
  "pluginKeys.copy": "Copy key",
  "pluginKeys.hide": "Hide",
  "pluginKeys.copied": "Copied key.",
  "pluginKeys.copyFailed": "Could not copy key automatically.",
  "pluginKeys.noKeys": "No active plugin keys yet.",
  "pluginKeys.activeKeys": "Active keys",
  "pluginKeys.lastUsed": "Last used",
  "pluginKeys.neverUsed": "Never used",
  "pluginKeys.expiresAt": "Expires",
  "pluginKeys.noExpiry": "No expiry",
  "pluginKeys.rotate": "Rotate",
  "pluginKeys.revoke": "Revoke",
  "pluginKeys.revokeConfirmTitle": "Revoke plugin key?",
  "pluginKeys.revokeConfirmBody": "Revoking this key immediately stops any plugin using it. Create a replacement key before confirming if the workshop still needs access.",
  "pluginKeys.cancel": "Cancel",
  "pluginKeys.confirmRevoke": "Confirm revoke",
  "pluginKeys.revokeReason": "Revocation reason",
  "pluginKeys.revokeReasonPlaceholder": "Replacing plugin installation",
  "pluginKeys.error.unavailable": "Provider plugin keys are available to active verified providers only.",
  "pluginKeys.error.nameRequired": "Enter a key name.",
  "pluginKeys.error.scopeRequired": "Select at least one scope.",
  "pluginKeys.scope.jobsRead": "Read jobs and offers",
  "pluginKeys.scope.jobsWrite": "Accept or update jobs",
  "pluginKeys.scope.offersRead": "Read offers",
  "pluginKeys.scope.offersWrite": "Respond to offers",
  "pluginKeys.scope.providersRead": "Read provider profile",
  "pluginKeys.scope.providersWrite": "Update provider settings",
  "pluginKeys.scope.inventoryRead": "Read inventory/catalog",
  "pluginKeys.scope.inventoryWrite": "Update inventory/catalog",
  "pluginKeys.scope.messagesRead": "Read messages",
  "pluginKeys.scope.messagesWrite": "Send messages",
  "pluginKeys.scope.payoutsRead": "Read payout status",
  "pluginKeys.scope.webhooksManage": "Manage webhooks",

  // Stitch control-plane chrome
  "stitch.notifications": "Notifications",
  "stitch.notifications.empty": "No unread notifications.",
  "partnerAnnouncements.loading": "Loading partner announcements…",
  "partnerAnnouncements.empty": "No partner announcements yet.",
  "partnerAnnouncements.refresh": "Refresh announcements",
  "partnerAnnouncements.error": "Could not load partner announcements. Try refreshing.",
  "partnerAnnouncements.ackFailed": "Could not acknowledge this announcement. Refresh announcements or try again if this notice is still available.",
  "partnerAnnouncements.docs": "Partner documentation",
  "stitch.notifications.bellWithCount": "Notifications ({count} unread)",
  "stitch.notifications.sectionOrders": "Order updates",
  "stitch.notifications.sectionAnnouncements": "Announcements",
  "stitch.notifications.markRead": "Mark as read",
  "stitch.notifications.markReadSaving": "Marking as read",
  "stitch.notifications.clear": "Clear",
  "stitch.notifications.clearing": "Clearing…",
  "stitch.notifications.clearOne": "Clear notification: {title}",
  "stitch.notifications.clearRead": "Clear read",
  "stitch.notifications.clearAll": "Clear all",
  "stitch.notifications.clearAllConfirm": "Clear every notification, including unread ones?",
  "stitch.notifications.clearAllConfirmAction": "Yes, clear all",
  "stitch.notifications.cancel": "Cancel",
  "stitch.notifications.markAllRead": "Mark all as read",
  "stitch.notifications.markingAllRead": "Marking all as read…",
  "stitch.notifications.refresh": "Refresh",
  "partnerAnnouncements.clearAcknowledged": "Clear acknowledged",
  "partnerAnnouncements.clearFailed": "Could not clear announcements. Refresh announcements and try again.",
  "workQueue.p2.tab.missed": "Missed · {count}",
  "workQueue.p2.tab.missedLabel": "Missed",
  "workQueue.p2.tab.completed": "Completed · {count}",
  "workQueue.p2.tab.completedLabel": "Completed",
  "workQueue.p2.status.completedOn": "Completed {date}",
  "workQueue.completed.empty": "No completed orders yet.",
  "workQueue.completed.emptyBody": "Orders move here once the customer confirms they received them, or automatically after the confirmation window.",
  "workQueue.missed.intro": "Offers from the last {days} days that closed before you accepted, and why. See what you were matched with so you can respond sooner next time.",
  "workQueue.missed.empty": "No missed offers in the last {days} days.",
  "workQueue.missed.summaryLabel": "Missed offers by reason",
  "workQueue.missed.col.closed": "Closed",
  "workQueue.missed.col.outcome": "Why",
  "workQueue.missed.outcome.timedOut": "Ran out of time",
  "workQueue.missed.outcome.taken": "Another provider accepted",
  "workQueue.missed.outcome.cancelled": "Order cancelled",
  "workQueue.missed.outcome.withdrawn": "Withdrawn",
  "workQueue.missed.explain.timedOut": "Your accept window ended before you responded.",
  "workQueue.missed.explain.taken": "This order was offered to more than one matching provider, and another provider accepted it before your window ended.",
  "workQueue.missed.explain.cancelled": "The order was cancelled before you accepted it.",
  "workQueue.missed.explain.withdrawn": "Tinkr withdrew this offer before your window ended, for example because the order changed or was reassigned.",
  "workQueue.missed.payout": "Payout offered",
  "workQueue.missed.offeredAt": "Offered",
  "workQueue.missed.respondBy": "Respond by",
  "workQueue.missed.tip": "Some offers ran out of time. Instant alerts notify you the moment a new offer arrives.",
  "workQueue.missed.tipAction": "Set up Instant alerts",
  "workQueue.missed.seeMissed": "See offers you missed ({count})",
  "stitch.openMenu": "Open navigation",
  "stitch.closeMenu": "Close navigation",
  "stitch.hub.provider": "Provider console",
  "stitch.hub.admin": "Control Room",
  "stitch.hub.partner": "Partner hub",
  "stitch.console.label": "Console",
  "stitch.console.provider": "Provider",
  "stitch.console.partner": "Partner",
  "stitch.console.admin": "Admin",
  "stitch.sidenav.documentation": "Documentation",
  "stitch.sidenav.support": "Support",

  // Provider nav
  "stitch.nav.provider.dashboard": "Dashboard",
  "stitch.nav.provider.onboarding": "Onboarding",
  "stitch.nav.provider.operations": "Operations",
  "stitch.nav.provider.workQueue": "Work queue",
  "stitch.nav.provider.capabilities": "Capabilities",
  "stitch.nav.provider.rateCards": "Rate cards",
  "stitch.nav.provider.apiKeys": "Integrations",
  "stitch.nav.provider.settlements": "Settlements",
  "stitch.nav.provider.payouts": "Payouts",

  // Warm-workshop sidebar group labels (2026-07 redesign)
  "stitch.nav.group.overview": "Overview",
  "stitch.nav.group.shopSetup": "Shop setup",
  "stitch.nav.group.money": "Money",
  "stitch.nav.group.developer": "Developer",
  "stitch.nav.group.account": "Account",

  // Warm-workshop topbar
  "warm.topbar.docs": "Docs",
  "warm.topbar.apiReference": "API reference",

  // Warm-workshop shop status chip (sidebar)
  "warm.chip.openForJobs": "Open for jobs",
  "warm.chip.paused": "Paused",
  "warm.chip.liveMode": "LIVE MODE",
  "warm.chip.sandbox": "SANDBOX",
  "warm.chip.inReview": "IN REVIEW",

  // ── WARM-WORKSHOP KEY ANCHOR: P1 provider home (keys land here) ──
  "warmHome.greetingNamed": "Welcome back, {name}",
  "warmHome.greeting": "Welcome back",
  "warmHome.subOpenOffersOne": "You're open for jobs. {offers} is waiting for a reply.",
  "warmHome.subOpenOffersMany": "You're open for jobs. {offers} are waiting for a reply.",
  "warmHome.subOffersOne": "1 offer",
  "warmHome.subOffersMany": "{count} offers",
  "warmHome.subOpenNoOffers": "You're open for jobs.",
  "warmHome.subPaused": "Your shop is paused. New offers stay on hold until you reopen.",
  "warmHome.legend.acceptingJobs": "Accepting jobs",
  "warmHome.legend.networkLive": "Network live",
  "warmHome.legend.payoutsVerified": "Payouts verified",
  "warmHome.legend.setup": "Setup {pct}%",
  "warmHome.legend.payoutsAction": "Payouts: action needed",
  "warmHome.legend.setupCaption": "How much of your go-live checklist is done.",
  "warmHome.legend.acceptingJobsCaption": "Your shop is open, so new offers can reach you.",
  "warmHome.legend.networkLiveCaption":
    "Tinkr's job routing is open and sending offers across the network.",
  "warmHome.legend.payoutsVerifiedCaption": "Your payout details are verified and ready.",
  "warmHome.legend.payoutsActionCaption": "Finish payout setup so you can be paid.",
  "warmHome.kpi.openOffers": "Open offers",
  "warmHome.kpi.firstExpires": "first expires in {time}",
  "warmHome.kpi.jobsInProgress": "Jobs in progress",
  "warmHome.kpi.dueToday": "{count} due today",
  "warmHome.kpi.earnedMonth": "Earned this month",
  "warmHome.kpi.fromJobsOne": "from 1 job",
  "warmHome.kpi.fromJobsMany": "from {count} jobs",
  "warmHome.kpi.alsoCurrencies": "also {amounts}",
  "warmHome.kpi.noPayablesMonth": "no payouts yet this month",
  "warmHome.kpi.capacityUsed": "Capacity used",
  "warmHome.offers.title": "Offers waiting for you",
  "warmHome.offers.hint": "respond before they expire",
  "warmHome.offers.openWorkQueue": "Open work queue",
  "warmHome.offers.colOrder": "Order",
  "warmHome.offers.colItem": "Item",
  "warmHome.offers.colMaterial": "Material",
  "warmHome.offers.colPayout": "Your payout",
  "warmHome.offers.colExpires": "Expires",
  "warmHome.offers.accept": "Accept",
  "warmHome.offers.confirmAccept": "Confirm",
  "warmHome.offers.decline": "Decline",
  "warmHome.offers.confirmDecline": "Decline offer",
  "warmHome.offers.moreParts": "+{count} more",
  "warmHome.offers.viewAll": "View all {count} offers",
  "warmHome.offers.empty": "No offers waiting right now.",
  "warmHome.offers.liveZeroTitle": "No offers waiting yet",
  "warmHome.offers.liveZeroBody":
    "Offers appear here as matching jobs come in across the network. While you wait, you can estimate your payout on a sample order or fine-tune your printers and materials.",
  "warmHome.offers.liveZeroEstimate": "Estimate your payout",
  "warmHome.offers.liveZeroCapabilities": "Review your capabilities",
  "warmHome.offers.expired": "expired",
  "warmHome.time.days": "{days}d {hours}h",
  "warmHome.time.hoursMinutes": "{hours}h {minutes}m",
  "warmHome.time.minutes": "{minutes}m",
  "warmHome.printing.title": "On the printers now",
  "warmHome.printing.allJobs": "All jobs",
  "warmHome.printing.due": "due {date}",
  "warmHome.printing.inProgress": "In progress",
  "warmHome.printing.notStarted": "Not started yet",
  "warmHome.printing.overdue": "Overdue",
  "warmHome.printing.shipped": "Shipped",
  "warmHome.printing.autoDeliver": "delivered automatically {date}",
  "warmHome.printing.awaitingQa": "Awaiting your QA response",
  "warmHome.printing.empty": "Nothing on the printers yet.",
  "warmHome.earnings.eligible": "Eligible for payout · {amount}",
  "warmHome.earnings.payouts": "Payouts",
  "warmHome.earnings.none": "No payouts recorded yet.",
  "warmHome.earnEstimate.title": "What would you earn?",
  "warmHome.earnEstimate.body":
    "Try a sample order to see the payout on a typical job, before any offers arrive.",
  "warmHome.earnEstimate.cta": "Estimate your payout",
  "warmHome.earnEstimate.noRateCardBody":
    "Setting your prices comes first. Once your rate card is live, you can estimate the payout on any sample job here.",
  "warmHome.earnEstimate.noRateCardCta": "Set your prices",
  "warmHome.printers.title": "Printers",
  "warmHome.printers.printing": "Printing",
  "warmHome.printers.idle": "Idle",
  "warmHome.printers.paused": "Paused",
  "warmHome.printers.unitCount": "{name} ×{count}",
  "warmHome.capabilities.title": "Current capabilities",
  "warmHome.capabilities.help":
    "These are the settings matching reads when deciding which jobs to offer you.",
  "warmHome.capabilities.notEnabled": "Not enabled",
  "warmHome.nudges.title": "Worth doing",
  "warmHome.nudges.qualityIncident": "Respond to the quality incident on {job}",
  "warmHome.nudges.offerExpiring": "Reply to an offer expiring in {time}",
  "warmHome.nudges.addRateCard": "Publish your first rate card",
  "warmHome.nudges.addCapabilities": "Add your printers and materials",
  "warmHome.nudges.verifyPayouts": "Finish payout verification",
  "warmHome.nudges.shopPaused": "Reopen your shop to receive offers",
  "warmHome.dayOne.greetingNamed": "Welcome, {name}",
  "warmHome.dayOne.greeting": "Welcome",
  "warmHome.dayOne.subStepsOne": "One step left before you can accept jobs.",
  "warmHome.dayOne.subStepsMany": "{count} steps left before you can accept jobs.",
  "warmHome.dayOne.subReady": "Setup complete. Your shop is almost ready.",
  "warmHome.checklist.title": "Go live checklist",
  "warmHome.checklist.progress": "{done} of {total} done",
  "warmHome.checklist.stepAccount": "Create your account",
  "warmHome.checklist.stepIdentity": "Verify your identity",
  "warmHome.checklist.stepBusinessReview": "Complete application review",
  "warmHome.checklist.stepAgreement": "Accept the provider agreement",
  "warmHome.checklist.stepCapabilities": "Add printers and materials",
  "warmHome.checklist.stepRateCard": "Set your first rate card",
  "warmHome.checklist.stepPayout": "Complete payout setup",
  "warmHome.checklist.stepGoLive": "Go live and accept jobs",
  "warmHome.checklist.done": "Done",
  "warmHome.checklist.verifyNow": "Verify now",
  "warmHome.checklist.continue": "Continue",
  "warmHome.checklist.locked": "Unlocks after payout setup",
  "warmHome.dayOne.offersTitle": "Offers",
  "warmHome.dayOne.zeroWaiting": "0 waiting",
  "warmHome.dayOne.waitingCount": "{count} waiting",
  "warmHome.dayOne.emptyTitle": "Your first offers land here",
  "warmHome.dayOne.emptyBody":
    "Once you're live, jobs that match your printers and prices appear here with a response timer.",
  "warmHome.payout.title": "Payout account",
  "warmHome.payout.notVerified": "Not verified yet",
  "warmHome.payout.verified": "Payout setup complete",
  "warmHome.payout.reviewSetup": "Review payout setup",
  "warmHome.payout.hint": "Payout setup will be completed before your first payout.",
  "warmHome.links.guide": "Read the provider guide",
  "warmHome.links.support": "Message support",

  // ── WARM-WORKSHOP KEY ANCHOR: P2 work queue (keys land here) ──
  "workQueue.p2.tabsLabel": "Work queue views",
  "workQueue.p2.tab.offers": "Offers · {count}",
  "workQueue.p2.tab.accepted": "Accepted · {count}",
  "workQueue.p2.tab.qa": "Issues · {count}",
  // Count-less tab names, shown while a count is unknown or its read failed.
  "workQueue.p2.tab.offersLabel": "Offers",
  "workQueue.p2.tab.acceptedLabel": "Accepted",
  "workQueue.p2.tab.qaLabel": "Issues",
  "workQueue.p2.col.order": "Order",
  "workQueue.p2.col.item": "Item",
  "workQueue.p2.col.material": "Material",
  "workQueue.p2.col.expires": "Expires",
  "workQueue.p2.col.accepted": "Accepted",
  "workQueue.p2.col.status": "Status",
  "workQueue.p2.timeHm": "{hours}h {minutes}m",
  "workQueue.p2.expired": "Expired",
  "workQueue.p2.expiresIn": "Expires {time}",
  // Accepted-row status sub-line: names the deadline the countdown/date is for.
  "workQueue.p2.status.startWithin": "Start within {time}",
  "workQueue.p2.status.startBy": "Start by {date}",
  "workQueue.p2.status.handOffWithin": "Ship within {time}",
  "workQueue.p2.status.startOverdue": "Start was due {date}",
  "workQueue.p2.status.handOffOverdue": "Shipping was due {date}",
  "workQueue.p2.status.autoDeliverBy": "Delivered automatically by {date} unless the customer confirms sooner",
  "workQueue.p2.itemLabel": "{name} ×{quantity}",
  "workQueue.p2.itemMore": "+ {count} more",
  "workQueue.badge.express": "Express",
  "providerLabels.orderRef": "Order {ref}",
  "workQueue.incident.reason.dimensional_accuracy": "Size or fit is off",
  "workQueue.incident.reason.surface_quality": "Surface quality",
  "workQueue.incident.reason.wrong_material": "Wrong material",
  "workQueue.incident.reason.wrong_color": "Wrong color",
  "workQueue.incident.reason.missing_part": "Missing part",
  "workQueue.incident.reason.late_dispatch": "Shipped late",
  "workQueue.incident.reason.damaged_in_transit": "Damaged in transit",
  "workQueue.incident.reason.missing_evidence": "Print photos missing",
  "workQueue.incident.reason.spec_mismatch": "Doesn't match the order",
  "workQueue.incident.reason.customer_reported": "Reported by the customer",
  "workQueue.badge.multicolor": "Multicolor ×{count}",
  "workQueue.badge.doodles": "Doodles {size}",
  "workQueue.accepted.todo": "To do · {count}",
  "workQueue.accepted.done": "Done · {count}",
  "workQueue.accepted.nothingToDo": "Nothing to do right now. Orders you accept stay here until you mark them shipped.",
  "workQueue.p2.col.payout": "Payout",
  "workQueue.evidence.error.type": "{name} can't be used. Choose a JPEG, PNG, WebP, GIF or HEIC photo.",
  "workQueue.evidence.error.size": "{name} is larger than {max}. Choose a smaller photo.",
  "workQueue.evidence.error.empty": "{name} couldn't be read. Choose it again.",
  "workQueue.evidence.error.uploadFailed": "Photo {n} couldn't be uploaded. Remove it or try again.",
  "workQueue.evidence.limits": "JPEG, PNG, WebP, GIF or HEIC, up to {max} each.",
  "workQueue.incident.updateResponse": "Update your response",
  "workQueue.tryAgain": "Try again",
  "workQueue.p2.parts": "Parts",
  "workQueue.multicolor.title": "Multicolor filament slots",
  "workQueue.multicolor.hint": "Load these exact stocked filaments before slicing the 3MF.",
  "workQueue.multicolor.slot": "Slot {slot}: {name} · {hex} · ≈{coverage}% of surface",
  "workQueue.p2.spec.size": "Size",
  "workQueue.p2.spec.color": "Color",
  "workQueue.p2.spec.material": "Material",
  "workQueue.p2.spec.quantity": "Quantity",
  "workQueue.p2.spec.layer": "Layer",
  "workQueue.p2.spec.infill": "Infill",
  "workQueue.p2.spec.priority": "Priority",
  "workQueue.p2.priorityPreset.speed": "Speed",
  "workQueue.p2.priorityPreset.balanced": "Balanced",
  "workQueue.p2.priorityPreset.quality": "Quality",
  "workQueue.p2.priorityPreset.price": "Price",
  "workQueue.p2.qualityLevel.standard": "Standard",
  "workQueue.p2.qualityLevel.documented": "Documented",
  "workQueue.p2.qualityLevel.certified_provider": "Certified provider",
  "workQueue.p2.qualityLevel.regulated_manual_review": "Regulated (manual review)",
  "workQueue.p2.spec.quality": "Quality",
  "workQueue.p2.spec.shipping": "Shipping",
  "workQueue.p2.spec.shipTo": "Ship to",
  "workQueue.p2.spec.deadline": "Deadline",
  "workQueue.p2.spec.filament": "Filament",
  "workQueue.p2.spec.printTime": "Print time",
  "workQueue.p2.spec.payout": "Payout total",
  "workQueue.p2.spec.manufacturingPayout": "Manufacturing payout",
  "workQueue.p2.spec.shippingReimbursement": "Shipping reimbursement",
  "workQueue.p2.printTimeHm": "{hours}h {minutes}m",
  "workQueue.p2.printTimeH": "{hours}h",
  "workQueue.p2.printTimeM": "{minutes}m",
  "workQueue.model.showPreview": "Preview model",
  "workQueue.model.hidePreview": "Hide preview",
  "workQueue.model.canvasLabel": "3D model preview",
  "workQueue.model.loading": "Loading model…",
  "workQueue.model.error": "Could not load the model.",
  "workQueue.model.retry": "Retry",
  "workQueue.model.downloadStl": "Download STL",
  "workQueue.model.downloadPrintPlan": "Print plan (3MF)",
  "workQueue.model.preparing": "Preparing…",
  "workQueue.parts.title": "Complex model remediation",
  "workQueue.parts.originalRetained":
    "Use this only when the original cannot print as one piece. The customer has already paid, Tinkr covers one regeneration attempt, and the original STL stays available. The result contains separate shells for your slicer's Split to Objects tool.",
  "workQueue.parts.request": "Regenerate as printable parts",
  "workQueue.parts.confirm":
    "This is the single provider regeneration allowed for this model. Generate the multipart alternative now?",
  "workQueue.parts.confirmAction": "Generate parts",
  "workQueue.parts.requesting": "Queuing…",
  "workQueue.parts.generating": "Generating printable parts…",
  "workQueue.parts.download": "Download multipart STL",
  "workQueue.parts.failed": "The parts attempt failed. The original STL is unchanged; contact support for review.",
  "workQueue.parts.error": "Could not update the printable-parts tool.",
  "workQueue.address.show": "Shipping address",
  "workQueue.address.hide": "Hide address",
  "workQueue.address.loading": "Loading address…",
  "workQueue.address.error": "Could not load the address.",
  "workQueue.address.countryOnly": "Ships to {country}. Full address available after dispatch prep.",
  "workQueue.address.none": "No shipping address on file.",
  "workQueue.p2.customerNotes": "Customer notes",
  "workQueue.p2.declineEllipsis": "Decline…",
  "workQueue.p2.footer": "Offers match your printers, materials, and rate cards.",
  "workQueue.p2.tuneMatching": "Improve which offers you get",
  "workQueue.p2.qa.awaitingResponse": "Awaiting your response",
  "workQueue.copyOrderId": "Copy full order number",
  "workQueue.orderIdCopied": "Copied",
  "workQueue.adminHelp": "Ask Tinkr support for help with this order",
  "workQueue.adminHelpSubject": "Help needed with order {orderId}",
  "workQueue.section.specs": "Specifications",
  "workQueue.section.model": "3D model",
  "workQueue.section.payout": "Your payout",
  "workQueue.address.title": "Shipping address",
  "workQueue.address.reveal": "Show address",

  // ── WARM-WORKSHOP KEY ANCHOR: P3 capabilities & rates (keys land here) ──
  "warmCap.printersLabel": "Printers · {count}",
  "warmCap.rateCardsLabel": "Rate cards · {count}",
  "warmCap.addPrinter": "Add printer",
  "warmCap.toggleLabel": "Accepting jobs on this printer",
  "warmCap.capabilityPaused": "Printer paused. It will not be matched with new offers.",
  "warmCap.capabilityResumed": "Printer active. It can be matched with offers again.",
  "warmCap.specEnclosed": "enclosed",
  "warmCap.specPaused": "paused",
  "warmCap.toggleLabelNamed": "Accepting jobs on {printer}",
  "warmCap.toggleOn": "On",
  "warmCap.toggleOff": "Off",
  "warmCap.status.suspended": "Suspended by Tinkr",
  "warmCap.status.removed": "Removed",
  "warmCap.edit": "Edit",
  "warmCap.delete": "Delete",
  "warmCap.printerEditorAdd": "Add a printer",
  "warmCap.printerEditorEdit": "Edit printer",
  "warmCap.rateEditorLabel": "Rate card editor",
  "warmCap.perGram": "/g",
  "warmCap.perCm3": "/cm³",
  "warmCap.minOrder": "min {amount}",
  "warmCap.addRateMaterial": "Add a rate card for another material",
  "warmCap.addRateCard": "Add rate card",
  "warmCap.noRates": "No rate cards yet. Add pricing so offers can match you.",

  // ── WARM-WORKSHOP KEY ANCHOR: P4 provider payouts (keys land here) ──
  "providerPayouts.eyebrow": "Get paid",
  "providerPayouts.title": "Payouts",
  "providerPayouts.locked": "Payouts unlock once your provider profile is created and approved.",
  "providerPayouts.settlements.approved": "Approved payable",
  "providerPayouts.settlements.count": "{count} settlements",
  "providerPayouts.settlements.countOne": "1 settlement",
  "providerPayouts.settlements.paid": "Paid",
  "providerPayouts.settlements.paidSub": "Explicitly confirmed payments",
  "providerPayouts.settlements.partial": "Showing your latest {count} settlements. The totals above include only these.",
  "providerPayouts.settlements.approvedUnpaidOne": "1 settlement approved, not yet paid",
  "providerPayouts.settlements.approvedUnpaid": "{count} settlements approved, not yet paid",
  "providerPayouts.settlements.title": "Settlements",
  "providerPayouts.settlements.body": "Each settlement is an amount Tinkr owes you for completed supplier work. Payout attempts are tracked separately.",
  "providerPayouts.settlements.empty": "No settlements yet.",
  "providerPayouts.settlements.period": "Period",
  "providerPayouts.settlements.reference": "Reference",
  "providerPayouts.settlements.net": "Net payable",
  "providerPayouts.settlements.status": "Status",
  "providerPayouts.setup.currencyChecking": "Checking which currency you earn in…",
  "providerPayouts.setup.currencyAllowedOnly": "You can be paid in GBP, USD or your local currency.",
  "providerPayouts.setup.currencyNotAllowed": "Payouts can only be set up in {currencies}.",
  "providerPayouts.setup.currencyLoadError": "We couldn't check which currency you earn in.",
  "providerPayouts.setup.confirmTitle": "Register for payouts in {currency}?",
  "providerPayouts.setup.confirmBody": "Wise will pay you in {currency}. Earnings in another currency are converted into {currency} when they're paid, and Wise takes its conversion fee from them.",
  "providerPayouts.setup.confirm": "Confirm and register",
  "providerPayouts.setup.back": "Back",
  "providerPayouts.setup.checkStatus": "Check status",
  "providerPayouts.setup.changeOpen": "Change payout currency",
  "providerPayouts.setup.changeLabel": "New payout currency",
  "providerPayouts.setup.changeSubmit": "Change currency",
  "providerPayouts.setup.changeConfirmTitle": "Change your payout currency to {currency}?",
  "providerPayouts.setup.changeConfirmBody": "We'll register you with Wise in {currency}. Payouts that start after this are paid in {currency}: earnings in another currency are converted when they're paid, and Wise takes its conversion fee from them. What you earn doesn't change.",
  "providerPayouts.setup.changeConfirm": "Confirm change",
  "providerPayouts.setup.changeSuccess": "Your payout currency is now {currency}.",
  "providerPayouts.setup.changeUnavailable": "Changing your payout currency isn't available here yet. Contact support to change it.",
  "providerPayouts.setup.currencyMissing": "Earnings in {currencies} can't be paid to your current payout currency. Change your payout currency to receive them.",
  "providerPayouts.setup.recipientMissing": "Your payout setup isn't finished, so payouts can't be sent yet. Choose your payout currency to register with Wise.",
  "providerPayouts.setup.conversionNote": "Earnings in {currencies} are converted into {currency} when they're paid. What you earn doesn't change.",
  "providerPayouts.setup.changePending": "Your payout currency is changing to {currency}. This usually takes a moment.",
  "providerPayouts.setup.changeFailed": "Your payout currency couldn't be changed to {currency}. Try again, or contact support if it keeps failing.",
  "providerPayouts.setup.changeBlockedBatch": "A payout to you is being prepared or sent. You can change your payout currency after it completes.",
  "providerPayouts.setup.changeUnchanged": "This is already your payout currency.",
  "providerPayouts.setup.changeInProgress": "A payout currency change is already in progress. Check its status in a moment.",
  "providerPayouts.setup.changePasswordHint": "Enter your password to confirm the change.",
  "providerPayouts.setup.changeReauthExpired": "Confirm your password again to change your payout currency.",
  "providerPayouts.setup.title": "Payout setup",
  "providerPayouts.setup.configured": "Your payout method is configured. Tinkr never asks you to enter bank details on this page.",
  "providerPayouts.setup.placeholder": "Payout setup will be completed before your first payout. Do not enter bank details into Tinkr.",
  "providerPayouts.setup.selfIntro": "Tinkr pays you through Wise, our payout partner. Registering takes one click. When your first payout is sent, Wise emails you a secure link to add your bank details directly with them.",
  "providerPayouts.setup.emailLabel": "Payout email",
  "providerPayouts.setup.currencyLabel": "Payout currency",
  "providerPayouts.setup.currencyPlaceholder": "Select payout currency",
  "providerPayouts.setup.submit": "Register with Wise",
  "providerPayouts.setup.submitting": "Registering with Wise…",
  "providerPayouts.setup.success": "Payout setup complete. Wise will email you to collect your bank details when your first payout is sent.",
  "providerPayouts.setup.hint": "Never enter bank details into Tinkr. We share only your legal name, account email and payout currency with Wise. Wise collects your bank details directly from you.",
  "providerPayouts.setup.pendingNote": "Setup is finishing in the background. Refresh in a moment to see the result.",
  "providerPayouts.setup.step1Title": "We register you with Wise",
  "providerPayouts.setup.step1Body": "Tinkr sends your legal name, account email and payout currency to Wise, our payout partner. Nothing else.",
  "providerPayouts.setup.step2Title": "Your first approved settlement triggers a payout",
  "providerPayouts.setup.step2Body": "Tinkr sends payouts in batches through Wise.",
  "providerPayouts.setup.step3Title": "Wise emails you a secure link for your bank details",
  "providerPayouts.setup.step3Body": "Add them directly with Wise. Tinkr never sees or stores them. The money reaches your account once you do.",
  "providerPayouts.setup.currencyFact": "The currency your settlements are paid in.",
  "providerPayouts.setup.done.registered": "Registered with Wise",
  "providerPayouts.setup.done.claimEmail": "Wise then emails {email} a secure link",
  "providerPayouts.setup.done.claimEmailBody": "Add your bank details with Wise through that link. The payout reaches your bank account once you do.",
  "providerPayouts.setup.done.payoutsStart": "Payouts start after your first approved settlement",
  "providerPayouts.setup.done.noEmail": "Payout sent but no email from Wise? Check spam, then contact Wise support.",
  "providerPayouts.setup.done.noEmailLink": "Wise help",
  "providerPayouts.eligibility.title": "Payout eligibility",
  "providerPayouts.eligibility.ready": "Ready to receive payouts",
  "providerPayouts.eligibility.blocked": "Action is still required",
  "providerPayouts.retry": "Try again",
  "providerPayouts.pending.loadError": "We couldn't load your pending earnings.",
  "providerPayouts.pending.staleError": "We couldn't refresh your pending earnings, so these may be out of date.",
  "providerPayouts.pending.partial": "Showing your latest {count} orders. Older pending orders aren't included in these totals.",
  "providerPayouts.pending.title": "Pending earnings",
  "providerPayouts.pending.body":
    "What you have earned on accepted and completed work, before it settles into a payout.",
  "providerPayouts.pending.empty": "No pending earnings yet. Accept an order to start earning.",
  "providerPayouts.pending.count": "{count} orders",
  "providerPayouts.pending.countOne": "1 order",
  "providerPayouts.pending.ordersHeading": "By order",
  "providerPayouts.pending.order": "Order {ref}",
  "providerPayouts.pending.statusOwed": "Awaiting payout",
  "providerPayouts.pending.statusPaid": "Paid",
  "providerPayouts.pending.statusHold": "On completion hold",
  "providerPayouts.standing.title": "Fulfillment standing",
  "providerPayouts.standing.count": "{count} of {threshold} strikes",
  "providerPayouts.standing.window":
    "Strikes in the last {days} days. A strike is recorded when an accepted order is reclaimed for a missed production or delivery deadline, or when you withdraw from an accepted order.",
  "providerPayouts.standing.clear":
    "No strikes on your record. Nice work keeping orders on track.",
  "providerPayouts.standing.recentHeading": "Recent strikes",
  "providerPayouts.standing.kindProduction": "Missed production deadline",
  "providerPayouts.standing.kindDelivery": "Missed delivery deadline",
  "providerPayouts.standing.kindWithdrew": "Withdrew from an accepted order",
  "providerPayouts.standing.kindOther": "Other fulfillment issue",
  "providerPayouts.standing.pausedTitle": "New orders are paused",
  "providerPayouts.standing.pausedBody":
    "You reached the strike limit, so new orders are paused. Orders you already accepted are not affected, and you will still be paid for them.",
  "providerPayouts.standing.pausedReason": "Reason: {reason}",
  "providerPayouts.standing.pausedContact":
    "Contact support to review your account and restore new orders.",
  "providerGovernance.status.notStarted": "Not started",
  "providerGovernance.status.requiresInput": "Action required",
  "providerGovernance.status.processing": "Processing",
  "providerGovernance.status.verified": "Verified",
  "providerGovernance.status.failed": "Failed",
  "providerGovernance.status.cancelled": "Cancelled",
  "providerGovernance.status.expired": "Expired",
  "providerGovernance.status.notSubmitted": "Not submitted",
  "providerGovernance.status.pending": "Pending",
  "providerGovernance.status.approved": "Approved",
  "providerGovernance.status.rejected": "Rejected",
  "providerGovernance.status.moreInformationRequired": "More information required",
  "providerGovernance.status.notAccepted": "Not accepted",
  "providerGovernance.status.accepted": "Accepted",
  "providerGovernance.status.configured": "Configured",
  "providerGovernance.status.disabled": "Disabled",
  "providerGovernance.status.draft": "Draft",
  "providerGovernance.status.awaitingApproval": "Awaiting approval",
  "providerGovernance.status.paid": "Paid",
  "providerGovernance.status.awaitingFunding": "Awaiting funding",
  "providerGovernance.status.queued": "Queued",
  "providerGovernance.status.returned": "Returned",
  "providerGovernance.blockingReason.identityNotVerified": "Identity is not verified",
  "providerGovernance.blockingReason.businessNotApproved": "Application review is not approved",
  "providerGovernance.blockingReason.providerAgreementNotAccepted": "Provider agreement is not accepted",
  "providerGovernance.blockingReason.providerNotApproved": "Provider is not approved",
  "providerGovernance.blockingReason.profileIncomplete": "Provider profile is incomplete",
  "providerGovernance.blockingReason.countryNotEnabled": "Country not open for orders yet",
  "providerGovernance.blockingReason.countryClosed": "Country is not accepting new orders",
  "providerGovernance.blockingReason.providerNotAcceptingOrders": "Provider is not accepting orders",
  "providerGovernance.blockingReason.payoutMethodNotConfigured": "Payout method is not configured",
  "providerGovernance.blockingReason.payoutCountryUnsupported": "Provider country is not supported for payouts",
  "providerGovernance.payoutProvider.manual": "Manual payout",
  "providerGovernance.payoutProvider.external": "External payout provider",

  // ── WARM-WORKSHOP KEY ANCHOR: B1 partner overview (keys land here) ──
  "warm.b1.subtitle.sandbox": "Sandbox access · v1 API",
  "warm.b1.subtitle.production": "Production access · v1 API",
  "warm.b1.legend.sandboxKeys": "Sandbox keys {count}",
  "warm.b1.legend.liveKeys": "Live keys {count}",
  "warm.b1.orders.title": "Orders placed via API",
  "warm.b1.orders.caption": "all time",
  "warm.b1.orders.statOrders": "orders placed",
  "warm.b1.orders.statPaid": "paid orders",
  "warm.b1.orders.statVolume": "paid volume",
  "warm.b1.orders.empty": "No orders through the API yet. Your first order will show up here.",
  "warm.b1.orders.error": "We could not load your order analytics.",
  "warm.b1.retry": "Retry",
  "warm.b1.quickstart.title.sandbox": "Quickstart · Create a sandbox order",
  "warm.b1.quickstart.title.production": "Quickstart · Create an order",
  "warm.b1.quickstart.copy": "Copy",
  "warm.b1.quickstart.copied": "Copied",
  "warm.b1.quickstart.copyFailed": "Copy failed",
  "warm.b1.keys.title": "API keys",
  "warm.b1.keys.badge.live": "Live",
  "warm.b1.keys.badge.test": "Test",
  "warm.b1.keys.manage": "Manage keys",
  "warm.b1.keys.empty": "No API keys yet. Create one from the API keys section.",
  "warm.b1.keys.error": "We could not load your API keys.",
  "warm.b1.recent.title": "Recent orders",
  "warm.b1.recent.empty": "No orders yet.",
  "warm.b1.recent.error": "We could not load recent orders.",
  "warm.b1.links.apiReference": "API reference",
  "warm.b1.links.integrationBrief": "Integration brief",

  // ── WARM-WORKSHOP KEY ANCHOR: B2 partner keys & webhooks (keys land here) ──
  "warm.b2.keys.title": "API keys",
  "warm.b2.keys.sub": "Mint, rotate, and revoke the keys your integration uses to call the partner API.",
  "warm.b2.keys.create": "Create key",
  "warm.b2.col.nameKey": "Name / key",
  "warm.b2.col.environment": "Environment",
  "warm.b2.col.created": "Created",
  "warm.b2.col.lastUsed": "Last used",
  "warm.b2.env.live": "LIVE",
  "warm.b2.env.test": "TEST",
  "warm.b2.never": "never",
  "warm.b2.reveal.created":
    "Secrets are shown once at creation. Copy the full key below now. From now on it will read {prefix}.",
  "warm.b2.reveal.rotated":
    "Secrets are shown once at rotation. Copy the full key below now. From now on it will read {prefix}, and the old key keeps working until the grace window ends.",
  "warm.b2.webhooks.title": "Webhook endpoints",
  "warm.b2.webhooks.activeDelivery": "Active · last delivery {when}",
  "warm.b2.webhooks.noDeliveries": "No deliveries yet",
  "warm.b2.webhooks.attempts": "up to {n} delivery attempts",
  "warm.b2.webhooks.added": "added {date}",

  // ── WARM-WORKSHOP KEY ANCHOR: SPLASH (keys land here) ──
  // Flow hero (scroll-scrubbed particle story, "Tinkr Splash Flow")
  "splash.heroEyebrow": "A network of 3D print workshops, rolling out in stages",
  "splash.heroLine1": "ideas",
  "splash.heroLine2": "made real.",
  "splash.heroSub":
    "Ready-to-print orders land in your queue: files checked, prices yours. You keep the printers running; Tinkr brings the work.",
  "splash.storySkip": "Skip animation",
  "splash.storyReplay": "Replay animation",
  "splash.skipToContent": "Skip to content",
  "splash.readout.forming": "FORMING",
  "splash.readout.waterIn": "WATER IN",
  "splash.readout.brewing": "BREWING",
  "splash.readout.pouringFuel": "POURING FUEL",
  "splash.readout.ignition": "IGNITION",
  "splash.readout.liftOff": "LIFT-OFF",
  // How it works (four steps + progress line)
  "splash.hiwEyebrow": "How it works",
  "splash.hiwTitle": "Four steps from upload to finished print.",
  "splash.hiwStep1Title": "Order lands in your queue",
  "splash.hiwStep1Body": "Files arrive pre-checked, sliced, and print-ready.",
  "splash.hiwStep2Title": "Accept on your terms",
  "splash.hiwStep2Body": "Your materials, your prices, your capacity.",
  "splash.hiwStep3Title": "Print and hand off",
  "splash.hiwStep3Body": "Labels, tracking, and customer updates are handled.",
  "splash.hiwStep4Title": "Follow every job",
  "splash.hiwStep4Body": "Status and history for each order stay in one place.",
  "splash.hiwRateNote": "your rate card set the price",
  // Console preview (three cards + docs link)
  "splash.consoleTitle": "One console runs the whole shift.",
  "splash.consoleBody":
    "Offers, printing, QA and shipping: one place to run every job.",
  "splash.consoleLink": "See the full provider console",
  // Network rail (marquee of illustrative sample jobs)
  "splash.railEyebrow": "Sample jobs, for illustration",
  // Shared sample-card copy (how-it-works, console preview, rail).
  // Quantities/prices/machine names stay literal in the markup: they are
  // sample-data motifs from the design, not sentences.
  "splash.simNewOffer": "New offer",
  "splash.simAccept": "Accept",
  "splash.simDecline": "Decline",
  "splash.simAccepted": "Accepted",
  "splash.simPrinting": "Printing",
  "splash.tagOffer": "Offer",
  "splash.tagQaPassed": "QA passed",
  "splash.tagShipped": "Shipped",
  "splash.tagLayer": "layer 30/44",
  "splash.tagTracking": "tracking shared with customer",
  "splash.tagGearHousing": "gear housing ×12",
  "splash.itemBracket": "Enclosure bracket ×4",
  "splash.itemDroneArm": "Drone arm ×8",
  "splash.itemJigSet": "Jig set ×6",
  "splash.itemCameraMount": "Camera mount ×2",
  // Teal closer ("the deal is simple")
  "splash.closerEyebrow": "The deal is simple",
  "splash.closerLine1": "you print.",
  "splash.closerLine2": "we route the work.",
  "splash.closerLine3": "you stay in control.",

  // Partner nav
  "stitch.nav.partner.overview": "Overview",
  "stitch.nav.partner.billing": "Balance",
  "stitch.nav.partner.secrets": "API keys",
  "stitch.nav.partner.webhooks": "Webhooks",
  "stitch.nav.partner.sandbox": "Sandbox",
  "stitch.nav.partner.settlements": "Settlements",
  "stitch.nav.partner.docs": "Docs",
  "stitch.nav.partner.payouts": "Payouts",
  "stitch.nav.partner.orders": "Orders",
  "stitch.nav.partner.shipping": "Shipping",

  // Partner Shipping Coverage (WS-SHIP PR5, read-only). Aggregate shipping
  // eligibility by country; fully localized like the rest of the partner platform.
  "partnerShipping.title": "Shipping coverage",
  "partnerShipping.subtitle":
    "Where Tinkr can make and deliver orders for your customers. Domestic shipping is the default; international is available to destinations Tinkr has enabled.",
  "partnerShipping.dutiesNotice":
    "International orders may take longer to arrive, and any import duties, taxes, or customs fees are paid by the recipient on delivery.",
  "partnerShipping.platformDomesticOnly":
    "Tinkr routes orders to local providers where available. For launch, Tinkr Checkout supports domestic fulfillment only: the customer delivery country must match the provider ship-from country.",
  "partnerShipping.loading": "Loading shipping coverage…",
  "partnerShipping.loadError": "We could not load shipping coverage. Please try again.",
  "partnerShipping.retry": "Retry",
  "partnerShipping.empty": "No shipping destinations are configured yet.",
  "partnerShipping.lockedBody":
    "Shipping coverage is available once your partner application is approved.",
  "partnerShipping.lockedCta": "View application status",
  "partnerShipping.table.country": "Country",
  "partnerShipping.table.status": "Status",
  "partnerShipping.table.domestic": "Domestic",
  "partnerShipping.table.international": "International",
  "partnerShipping.table.networks": "Networks",
  "partnerShipping.status.available": "Available",
  "partnerShipping.status.comingSoon": "Coming soon",
  "partnerShipping.status.blocked": "Unavailable",
  "partnerShipping.yes": "Yes",
  "partnerShipping.no": "No",
  "partnerShipping.none": "—",

  // Partner commissions and payout setup. Registration and payment remain
  // separate steps; partner copy is localized in every supported locale.
  "partnerPayouts.title": "Payouts",
  "partnerPayouts.subtitle":
    "Your referral earnings and payout history. Tinkr reviews eligible commissions and funds approved payouts.",
  "partnerPayouts.loading": "Loading your payouts…",
  "partnerPayouts.loadError": "We couldn't load your payouts. Please try again.",
  "partnerPayouts.retry": "Retry",
  "partnerPayouts.lockedBody":
    "Payouts appear here once your partner application is approved.",
  "partnerPayouts.lockedCta": "Go to application",
  "partnerPayouts.autoNote":
    "Commissions are held under your agreement. After the hold, Tinkr reviews eligible amounts and funds approved payouts through Wise. No payout request is needed.",
  "partnerPayouts.verificationRequired": "Identity verification and Tinkr business approval are required before a commission can be paid. Your API access and earnings history remain available.",
  "partnerPayouts.openOverview": "Review verification steps",
  "partnerPayouts.setup.title": "Set up Wise payouts",
  "partnerPayouts.setup.intro": "Register a payout currency now or add another currency later. Setup does not affect your API access.",
  "partnerPayouts.setup.notStarted": "Not started",
  "partnerPayouts.setup.pending": "Registration pending",
  "partnerPayouts.setup.configured": "Registered",
  "partnerPayouts.setup.disabled": "Unavailable",
  "partnerPayouts.setup.stepRegister": "Choose a currency. Tinkr shares your account email, legal or business name, and currency with Wise to register a payout recipient.",
  "partnerPayouts.setup.stepReview": "After the commission hold, Tinkr reviews eligible amounts and funds an approved payout through Wise.",
  "partnerPayouts.setup.stepClaim": "When a payment is sent, Wise emails a secure link to your account email so you can provide bank details directly to Wise.",
  "partnerPayouts.setup.registered": "Registered currencies:",
  "partnerPayouts.setup.currency": "Payout currency",
  "partnerPayouts.setup.chooseCurrency": "Select a currency",
  "partnerPayouts.setup.submit": "Register with Wise",
  "partnerPayouts.setup.addCurrency": "Add currency",
  "partnerPayouts.setup.submitting": "Registering…",
  "partnerPayouts.setup.error": "We couldn't register this currency. Please try again.",
  "partnerPayouts.setup.success": "Currency registered. A bank-details email comes when a payment is sent.",
  "partnerPayouts.setup.pendingNotice": "Registration is processing. Refresh to check its status.",
  "partnerPayouts.setup.checkStatus": "Check registration status",
  "partnerPayouts.setup.disabledHelp": "Payout setup is unavailable. Contact Tinkr support.",
  "partnerPayouts.setup.noBankDetails": "Never enter bank details in Tinkr. Wise collects them through its secure email link when a payment is sent.",
  "partnerPayouts.connect.label": "Payout account",
  "partnerPayouts.connect.notStarted": "Not set up",
  "partnerPayouts.connect.setUp": "Setup in progress",
  "partnerPayouts.connect.ready": "Ready",
  "partnerPayouts.connect.actionNeeded": "Action needed",
  "partnerPayouts.balances.empty": "No commissions yet.",
  "partnerPayouts.balances.held": "Held",
  "partnerPayouts.balances.eligible": "Eligible",
  "partnerPayouts.balances.paid": "Paid out",
  "partnerPayouts.balances.accrued": "Total earned",
  "partnerPayouts.minimum.label": "Payout minimum",
  "partnerPayouts.history.title": "Payout history",
  "partnerPayouts.history.empty": "No payouts yet.",
  "partnerPayouts.history.date": "Date",
  "partnerPayouts.history.amount": "Amount",
  // Read-only commission terms (GET /v1/partner/referral-fee-summary).
  // Rail-agnostic copy: it describes the agreement, not the payout rail.
  // Partner platform is fully localized (founder direction).
  "partnerPayouts.fees.title": "Commission terms",
  "partnerPayouts.fees.commission": "Referral commission",
  "partnerPayouts.fees.none": "No referral commission",
  "partnerPayouts.fees.fixedPerOrder": "{amount} per order",
  "partnerPayouts.fees.cappedPercent": "{percent}, capped at {amount}",
  "partnerPayouts.fees.hold": "Commission hold",
  "partnerPayouts.fees.holdDays": "{days} days",
  "partnerPayouts.fees.effectiveFrom": "Effective from",
  "partnerPayouts.fees.agreement": "Agreement reference",
  "partnerPayouts.fees.empty": "Your commission terms haven't been set yet. They appear here once Tinkr configures your agreement.",
  "partnerPayouts.fees.loadError": "We couldn't load your commission terms. Please try again.",

  // Partner Orders & Analytics (WS4, read-only). The partner sees their own
  // orders plus simple roll-ups; amounts are grouped public totals only.
  // Localized in every supported locale (partner platform is fully localized).
  "partnerOrders.title": "Orders & analytics",
  "partnerOrders.subtitle":
    "Your orders and a quick overview of their status and paid volume.",
  "partnerOrders.loading": "Loading your orders…",
  "partnerOrders.loadError": "We couldn't load your orders. Please try again.",
  "partnerOrders.retry": "Retry",
  "partnerOrders.lockedBody":
    "Your orders appear here once your partner application is approved.",
  "partnerOrders.lockedCta": "Go to application",
  "partnerOrders.empty": "No orders yet.",
  "partnerOrders.analytics.title": "Overview",
  "partnerOrders.analytics.totalOrders": "Total orders",
  "partnerOrders.analytics.paidOrders": "Paid orders",
  "partnerOrders.analytics.paidVolume": "Paid volume",
  "partnerOrders.table.reference": "Reference",
  "partnerOrders.table.status": "Status",
  "partnerOrders.table.created": "Created",
  "partnerOrders.table.total": "Total",
  "partnerOrders.premium.tag": "Premium order",
  "partnerOrders.premium.unavailable": "Premium not available, standard price applied",
  "partnerOrders.status.draft": "Draft",
  "partnerOrders.status.estimating": "Estimating",
  "partnerOrders.status.failed": "Estimate failed",
  "partnerOrders.status.quoted": "Quoted",
  "partnerOrders.status.matching": "Matching provider",
  "partnerOrders.status.noMatch": "No provider found",
  "partnerOrders.status.awaitingPayment": "Awaiting payment",
  "partnerOrders.status.paid": "Paid",
  "partnerOrders.status.inProduction": "In production",
  "partnerOrders.status.shipped": "Shipped",
  "partnerOrders.status.delivered": "Delivered",
  "partnerOrders.status.completed": "Completed",
  "partnerOrders.status.cancelled": "Cancelled",
  "partnerOrders.pager.prev": "Previous",
  "partnerOrders.pager.next": "Next",
  "partnerOrders.pager.showing": "Showing {count} of {total}",

  // Partner provider-matching preference (ADR-P10 §5). Partner-facing copy,
  // localized in every supported locale. Neutral framing: individual makers
  // pass the same verification and quality checks as registered businesses.
  "partnerAppCoverage.eyebrow": "Provider network",
  "partnerAppCoverage.title": "Providers taking your app’s orders",
  "partnerAppCoverage.subtitle": "Providers can choose which apps they print for. Every app is on for them by default, and some may turn yours off. This shows how widely your orders can still be offered.",
  "partnerAppCoverage.share": "{percent}% of ready providers take your orders",
  "partnerAppCoverage.band.good": "Good",
  "partnerAppCoverage.band.limited": "Limited",
  "partnerAppCoverage.band.none": "None yet",
  "partnerAppCoverage.bandHelp": "Good means several ready providers take your orders there, Limited means one or two, and None yet means none do. Exact provider numbers are not shown, to protect individual providers.",
  "partnerAppCoverage.country": "Country",
  "partnerAppCoverage.coverage": "Coverage",
  "partnerAppCoverage.unlisted": "Your integration isn’t listed as an app providers can choose, so every eligible provider can receive your orders.",
  "partnerAppCoverage.loading": "Loading provider coverage…",
  "partnerAppCoverage.loadError": "Provider coverage couldn’t be loaded.",
  "partnerAppCoverage.retry": "Try again",
  "partnerMatching.eyebrow": "Order routing",
  "partnerMatching.title": "Provider matching",
  "partnerMatching.subtitle":
    "Choose which provider divisions can fulfill your orders. Registered businesses and individual makers pass the same verification and quality checks.",
  "partnerMatching.option.both": "Both",
  "partnerMatching.option.both.help": "Your orders reach registered businesses and individual makers.",
  "partnerMatching.option.business_only": "Businesses only",
  "partnerMatching.option.business_only.help": "Your orders reach registered business providers only.",
  "partnerMatching.save": "Save preference",
  "partnerMatching.saving": "Saving",
  "partnerMatching.saved": "Matching preference saved.",
  "partnerMatching.loading": "Loading preference…",
  "partnerMatching.loadError": "Couldn't load your matching preference. Please try again.",
  "partnerMatching.retry": "Try again",
  // Partner payment notification preferences (v1: the `payment` category
  // only; in-app alerts are always on). The preferredLocale select controls
  // the language payment EMAILS are sent in, not the dashboard language.
  // Partner platform is fully localized (founder direction).
  "partnerNotifications.eyebrow": "Notifications",
  "partnerNotifications.title": "Payment notifications",
  "partnerNotifications.subtitle": "Choose how you hear about payment activity on your partner account.",
  "partnerNotifications.inAppNote": "In-app alerts for payment activity are always on.",
  "partnerNotifications.channelsLegend": "Email channel",
  "partnerNotifications.emailLabel": "Email me about payment activity",
  "partnerNotifications.localeLabel": "Email language",
  "partnerNotifications.localeHelp": "Payment emails are sent in this language.",
  "partnerNotifications.save": "Save preferences",
  "partnerNotifications.saving": "Saving",
  "partnerNotifications.saved": "Notification preferences saved.",
  "partnerNotifications.loading": "Loading preferences…",
  "partnerNotifications.loadError": "Couldn't load your notification preferences. Please try again.",
  "partnerNotifications.retry": "Try again",
  "providerPush.eyebrow": "Notifications",
  "providerPush.title": "Instant alerts",
  "providerPush.valueProp":
    "Get an instant alert on this device the moment a new print offer arrives, so you can accept it before it expires.",
  "providerPush.enable": "Enable instant alerts",
  "providerPush.enabling": "Enabling",
  "providerPush.enabledNote": "Instant alerts are on for this device.",
  "providerPush.disable": "Turn off",
  "providerPush.disabling": "Turning off",
  "providerPush.deniedNote":
    "Notifications are blocked. Turn them on for this site in your browser settings, then reload the page.",
  "providerPush.iosInstallNote": "On iPhone and iPad, alerts work only from the Home Screen: tap Share, choose Add to Home Screen, then open Tinkr from the new icon and turn on instant alerts here.",
  "providerPush.unclaimedNote": "This browser already receives instant alerts that may belong to another account. Turn them on to get this account's alerts here.",
  "providerPush.error": "We couldn't update instant alerts. Please try again.",

  // Page headers per console
  "stitch.page.provider.eyebrow": "Provider command center",
  "stitch.page.provider.title": "System overview",
  "stitch.page.provider.noticeLabel": "Network notice",
  "stitch.page.provider.notice": "Provider setup open · Job routing controlled",
  "stitch.page.partner.eyebrow": "Developer hub",
  "stitch.page.partner.title": "Partner integrations",

  // Metrics
  "stitch.metric.openOffers": "Open offers",
  "stitch.metric.openOffers.delta": "▲ 4.2%",
  "stitch.metric.activeJobs": "Active jobs",
  "stitch.metric.activeJobs.delta": "System ready",
  "stitch.metric.monthlyPayout": "Monthly payout",
  "stitch.metric.monthlyPayout.delta": "Verified",
  "stitch.metric.capacityUsed": "Capacity used",
  "stitch.metric.activeProviders": "Active providers",
  "stitch.metric.activeProviders.delta": "Verified",
  "stitch.metric.pendingApps": "Pending applications",
  "stitch.metric.pendingApps.delta": "Review queue",
  "stitch.metric.inFlightJobs": "In-flight jobs",
  "stitch.metric.inFlightJobs.delta": "Live",
  "stitch.metric.auditedWrites": "Audited writes (24h)",
  "stitch.metric.auditedWrites.delta": "Reason logged",
  "stitch.metric.netSettlements": "Net settlements",
  "stitch.metric.netSettlements.delta": "▲ 12.4%",
  "stitch.metric.pendingClearing": "Pending clearing",
  "stitch.metric.processingFee": "Processing fee",
  "stitch.metric.processingFee.delta": "Tier 3",
  "stitch.metric.disputeRate": "Dispute rate",
  "stitch.metric.disputeRate.delta": "Optimised",

  // Partner page-header titles. The account-manager-era placeholder copy
  // (stitch.partner.placeholder.* / .secrets table / .banner / .webhooks
  // inputs) was deleted once the self-serve panels replaced every
  // placeholder surface — only these two nav titleKeys remain in use.
  "stitch.partner.secrets.title": "API keys",
  "stitch.partner.webhooks.title": "Webhooks",

  // Footer
  "stitch.footer.brand": "Tinkr",
  "stitch.demoBadge": "Demo data",
  "stitch.loading": "Loading…",
  "stitch.error.title": "Something went wrong",
  "stitch.error.body": "The {panel} panel failed to load. Reload it without losing the rest of the dashboard.",
  "stitch.error.retry": "Reload panel",
  "stitch.error.requestId": "Request ID",
  "stitch.signedOut.title": "Signed out",
  "stitch.signedOut.body": "Your session ended. Sign back in to continue.",

  // Provider onboarding wizard
  "onboardingWizard.hero.eyebrow": "Provider onboarding",
  "onboardingWizard.hero.welcomeTitle": "Welcome — let's get you set up to receive jobs.",
  "onboardingWizard.hero.welcomeBody": "Tinkr routes structured print jobs to providers who can deliver. Walk through these steps and you'll be ready for review. Take a break anytime; we'll save your progress.",
  "onboardingWizard.hero.inProgressTitle": "A few steps to go.",
  "onboardingWizard.hero.inProgressBody": "Each step saves automatically. Step away whenever you need to and come back here — we'll remember exactly where you left off.",
  "onboardingWizard.hero.reviewTitle": "Thanks — your account is in review.",
  "onboardingWizard.hero.reviewBody": "Our operator team is checking your setup. You'll get an email and an in-dashboard notification as soon as jobs can route to you.",
  "onboardingWizard.hero.liveTitle": "You're live — jobs can route to you.",
  "onboardingWizard.hero.liveBody": "Capabilities, rate cards, and the work queue are now your daily tools. Keep your availability and pricing fresh and you'll see more offers.",
  "onboardingWizard.hero.agreementTitle": "One thing to do: accept the updated provider agreement.",
  "onboardingWizard.hero.agreementBody": "Your account is approved. A new version of the Tinkr Provider Supplier Agreement is in effect, so job routing and payouts are paused until you accept it below.",
  "onboardingWizard.hero.metaLabel": "Onboarding meta",
  "onboardingWizard.hero.refresh": "Refresh onboarding status",
  "onboardingWizard.progress.review": "In review",
  "onboardingWizard.progress.agreement": "Action needed",
  "onboardingWizard.progress.live": "Live",
  "onboardingWizard.progress.approved": "Approved",
  "onboardingWizard.steps.label": "Onboarding steps",
  "onboardingWizard.steps.welcome": "Welcome",
  "onboardingWizard.steps.entity": "You or your business",
  "onboardingWizard.steps.business": "Your details",
  "onboardingWizard.steps.individualDetails": "Your details",
  "onboardingWizard.steps.verification": "Verify it's you",
  "onboardingWizard.steps.capabilities": "What you print",
  "onboardingWizard.steps.apps": "Apps",
  "onboardingWizard.apps.why": "Choose which apps can send you orders. Everything is on to start, so you can simply continue.",
  "onboardingWizard.steps.serviceArea": "Where you ship",
  "onboardingWizard.steps.portfolio": "Show your work",
  "onboardingWizard.steps.review": "Check and send",
  "onboardingWizard.steps.pending": "In review",
  "onboardingWizard.actions.back": "Back",
  "onboardingWizard.actions.continue": "Continue",
  "onboardingWizard.actions.checkingSetup": "Checking setup",
  "onboardingWizard.welcome.title": "Here's what we'll do together.",
  "onboardingWizard.welcome.body": "Two parts are on you, one is on us. Here's the whole journey before you begin, no surprises.",
  "onboardingWizard.welcome.bulletBusiness": "Tell us who will receive payouts: a registered business or you as an individual maker.",
  "onboardingWizard.welcome.bulletIdentity": "Verify your identity on a secure hosted page.",
  "onboardingWizard.welcome.bulletCapabilities": "Add the printers, materials, and post-processing you offer.",
  "onboardingWizard.welcome.bulletService": "Set where you ship and how you fulfill.",
  "onboardingWizard.welcome.cta": "Let's go",
  "onboardingWizard.welcome.privateBetaNote": "We review every provider before opening live job routing.",
  // Entity chooser step (ADR-P10). Neutral framing: both lanes get equal
  // weight and neither is labelled as recommended.
  "onboardingWizard.entity.title": "How will you join Tinkr?",
  "onboardingWizard.entity.body": "Pick the account type that matches how you operate. Both receive the same jobs, tools, and dashboard.",
  "onboardingWizard.entity.businessTitle": "Registered business",
  "onboardingWizard.entity.businessBody": "A registered company with official registration documents.",
  "onboardingWizard.entity.individualTitle": "Individual maker",
  "onboardingWizard.entity.individualBody": "A person with one or more printers. No registered business required.",
  "onboardingWizard.business.title": "Tell us about your business.",
  "onboardingWizard.business.body": "We'll use these details to create your provider profile. Identity verification and application review are separate steps.",
  "onboardingWizard.business.companyName": "Business name",
  "onboardingWizard.business.contactEmail": "Operations email",
  "onboardingWizard.business.legalName": "Legal business name",
  "onboardingWizard.business.displayName": "Display name (shown to customers)",
  "onboardingWizard.business.taxId": "Tax ID / business registration number",
  "onboardingWizard.business.taxIdCountry": "Tax country (ISO-2)",
  "onboardingWizard.business.accountCountry": "Business account country",
  "onboardingWizard.business.accountCountryPlaceholder": "Select account country",
  "onboardingWizard.business.website": "Business website",
  "onboardingWizard.business.legalHint": "Use the legal name that matches your business registration. Tinkr reviews business information separately from identity verification.",
  "onboardingWizard.business.reviewHint": "Tinkr reviews your provider information separately from identity verification.",
  "onboardingWizard.business.accountCountryHelp": "This is the country recorded at signup and cannot be changed here. If it is incorrect, create a new account with your correct country. If your country is not supported yet, please check back later.",
  "onboardingWizard.business.submit": "Save and continue",
  "onboardingWizard.business.submitting": "Saving...",
  // Individual maker lane (ADR-P10). Same step position as the business
  // form, with person-specific fields and the normalized identity flow.
  "onboardingWizard.individual.title": "Tell us about yourself.",
  "onboardingWizard.individual.body": "We'll use these details to create your provider profile. Identity verification happens securely in the next step.",
  "onboardingWizard.individual.contactEmail": "Contact email",
  "onboardingWizard.individual.legalName": "Legal full name",
  "onboardingWizard.individual.legalNameHelp": "Your legal name is used only for verification and payouts. It is never shown publicly.",
  "onboardingWizard.individual.displayName": "Maker name (shown to customers)",
  "onboardingWizard.individual.accountCountry": "Account country",
  "onboardingWizard.individual.accountCountryHelp": "This is the country recorded at signup and cannot be changed here. If it is incorrect, create a new account with your correct country. If your country is not supported yet, please check back later.",
  "onboardingWizard.individual.showcaseUrl": "Showcase URL (optional)",
  "onboardingWizard.individual.showcaseUrlHelp": "Optional: link your work if you have it online, like a portfolio site, a MakerWorld or Printables profile, or a social page. No link yet? No problem. You will show your work with photos in the Portfolio step.",
  "onboardingWizard.review.showcaseNone": "No link. Your portfolio photos will show your work.",
  "onboardingWizard.individual.identityHint": "Identity verification happens on a secure hosted page. Tinkr does not store identity documents.",
  "onboardingWizard.error.incompleteIndividual": "Fill in every required field before continuing.",
  "onboardingWizard.error.showcaseUrlInvalid": "Enter a full https:// link to your work, for example https://makerworld.com/@yourname.",
  "onboardingWizard.verification.title": "Verify your identity.",
  "onboardingWizard.verification.body": "Identity verification confirms the person representing this provider. Tinkr reviews the business separately.",
  "onboardingWizard.verification.bodyIndividual": "Complete identity verification on the secure hosted page, then return here to continue.",
  "onboardingWizard.verification.identity": "Identity verification",
  "onboardingWizard.verification.businessReview": "Application review",
  "onboardingWizard.verification.agreement": "Provider agreement",
  "onboardingWizard.verification.payoutSetup": "Payout setup",
  "onboardingWizard.verification.providerApproval": "Provider approval",
  "onboardingWizard.verification.allGood": "Identity verified — you can move on.",
  "onboardingWizard.verification.checkingResult": "Welcome back. We're checking your verification result. If you submitted your document, you don't need to start again.",
  "onboardingWizard.verification.resultDelayed": "No updated result yet. If you submitted your document, check again later. If you didn't finish, continue verification. Contact Tinkr support if the result stays unchanged.",
  "onboardingWizard.verification.processing": "Your identity check is under review. This page updates automatically.",
  "onboardingWizard.verification.unavailable": "We could not open identity verification. Try again. If this continues, contact Tinkr support.",
  "onboardingWizard.verification.retryBody": "This verification did not complete. Start again when you are ready.",
  "onboardingWizard.verification.startFailed": "Identity verification could not be started. Please try again.",
  "onboardingWizard.verification.checkingShort": "Checking",
  "onboardingWizard.verification.hint": "The secure verification page will return you to this wizard. Have a photo ID ready.",
  "onboardingWizard.verification.hintIndividual": "The secure verification page will return you to this wizard. Have a photo ID ready.",
  "onboardingWizard.verification.start": "Start identity verification",
  "onboardingWizard.verification.resume": "Continue identity verification",
  "onboardingWizard.verification.opening": "Opening secure verification",
  "onboardingWizard.verification.checkAgain": "Check again",
  "onboardingWizard.agreement.cardTitle": "Provider agreement",
  "onboardingWizard.agreement.cardBody": "Review and accept the Tinkr Provider Supplier Agreement. Acceptance is required before your account can go live and receive jobs.",
  "onboardingWizard.agreement.documentName": "Tinkr Provider Supplier Agreement",
  "onboardingWizard.agreement.versionLabel": "Version {version}",
  "onboardingWizard.agreement.readDocument": "Read the provider agreement",
  "onboardingWizard.agreement.checkbox": "I have read and agree to the Tinkr Provider Supplier Agreement.",
  "onboardingWizard.agreement.accept": "Accept agreement",
  "onboardingWizard.agreement.accepting": "Recording",
  "onboardingWizard.agreement.accepted": "Accepted on {date} (version {version}).",
  "onboardingWizard.agreement.error": "We could not record your acceptance. Please try again.",
  "onboardingWizard.agreement.unavailable": "The provider agreement is not available to accept yet. You can continue with onboarding. We will ask you to accept it before your account goes live.",
  "onboardingWizard.agreement.loadError": "We could not check the provider agreement right now. Please try again.",
  "onboardingWizard.agreement.retry": "Try again",
  "onboardingWizard.agreement.errorUnpublished": "The provider agreement is not available to accept yet. Please check back soon.",
  "onboardingWizard.agreement.errorVersionMismatch": "A newer provider agreement has been published. Refresh this page and review the current version before accepting.",
  "onboardingWizard.agreement.docsOutdated": "A newer provider agreement is being prepared. Acceptance will reopen when the updated document is available to read.",
  "onboardingWizard.capabilities.title": "What can you print?",
  "onboardingWizard.capabilities.body": "Add your printers, materials, and post-processing options. The capabilities and rate cards panels go deeper — open them in another tab and come back to confirm.",
  "onboardingWizard.capabilities.linkCapabilitiesTitle": "Set capabilities",
  "onboardingWizard.capabilities.linkCapabilitiesBody": "Printers, materials, colors, finishes.",
  "onboardingWizard.capabilities.linkRateCardsTitle": "Build rate cards",
  "onboardingWizard.capabilities.linkRateCardsBody": "Price per gram, machine hour, setup fee, currency.",
  "onboardingWizard.capabilities.partialOk": "It's fine to start with one printer and one material. You can grow your offering after launch.",
  "onboardingWizard.error.setupIncomplete": "Save at least one active printer capability, one active rate card, and one shipping lane before submitting for review.",
  "onboardingWizard.error.portfolioIncomplete": "Complete the portfolio step (photos of your work, including the verification photo) before submitting for review.",
  "onboardingWizard.error.serviceAreaIncomplete": "Save your service area, fulfillment method, and shipping carriers before submitting for review.",
  "onboardingWizard.serviceArea.title": "Where will you serve?",
  "onboardingWizard.serviceArea.body": "Choose your fulfillment methods, service area, and shipping carriers. The operations panel has the full controls.",
  "onboardingWizard.serviceArea.linkOperationsTitle": "Open operations",
  "onboardingWizard.serviceArea.linkOperationsBody": "Fulfillment methods, shipping carriers, international toggle, service-area countries.",
  "onboardingWizard.serviceArea.partialOk": "Start with what you know you can deliver this week. Expanding later is one toggle.",
  "onboardingWizard.portfolio.title": "Show us your work.",
  "onboardingWizard.portfolio.body": "Upload photos of prints you have made so our review team can see your quality before activating your account. The photos are only used for review and are never shown publicly.",
  "onboardingWizard.portfolio.codeTitle": "Your verification code",
  "onboardingWizard.portfolio.codeStep1": "Write this code on a piece of paper.",
  "onboardingWizard.portfolio.codeStep2": "Place the note next to a print you made. Including your printer in the frame helps.",
  "onboardingWizard.portfolio.codeStep3": "Take a photo and upload it as your verification photo.",
  "onboardingWizard.portfolio.codeWhy": "The handwritten code proves the photos show your own work, not images found online.",
  "onboardingWizard.portfolio.uploadVerification": "Upload verification photo",
  "onboardingWizard.portfolio.uploadMore": "Add portfolio photo",
  "onboardingWizard.portfolio.uploading": "Uploading",
  "onboardingWizard.portfolio.verificationBadge": "Verification photo",
  "onboardingWizard.portfolio.photoAlt": "Portfolio photo",
  "onboardingWizard.portfolio.delete": "Remove photo",
  "onboardingWizard.portfolio.limit": "{count} / {max} photos",
  "onboardingWizard.portfolio.adminOnlyNote": "Photos are visible to the Tinkr review team only.",
  "onboardingWizard.portfolio.requirementIndividual": "Upload at least {min} photos, including the verification photo.",
  "onboardingWizard.portfolio.requirementBusinessWithWebsite": "Optional for registered businesses with a website, but photos strengthen your application.",
  "onboardingWizard.portfolio.requirementBusinessNoWebsite": "Without a company website, upload at least {min} photos including the verification photo.",
  "onboardingWizard.portfolio.verificationMissing": "One of your photos must be the verification photo with the handwritten code.",
  "onboardingWizard.portfolio.uploadFailed": "The photo upload failed. Please try again.",
  "onboardingWizard.review.title": "Submit for review.",
  "onboardingWizard.review.body": "We'll review your business details separately from identity verification before opening job routing to your account.",
  "onboardingWizard.review.portfolio": "Portfolio",
  "onboardingWizard.review.portfolioGood": "Ready ({count} photos)",
  "onboardingWizard.review.portfolioMissing": "Not complete",
  "onboardingWizard.review.identity": "Identity verification",
  "onboardingWizard.review.identityGood": "Verified",
  "onboardingWizard.review.identityMissing": "Not verified",
  "onboardingWizard.review.businessReview": "Application review",
  "onboardingWizard.review.agreement": "Provider agreement",
  "onboardingWizard.review.payoutSetup": "Payout setup",
  "onboardingWizard.review.payoutConfigured": "Configured",
  "onboardingWizard.review.payoutPending": "Will be completed before your first payout",
  "onboardingWizard.review.identityRequired": "Complete identity verification before submitting your application for review.",
  "onboardingWizard.review.agreementRequired": "Accept the provider agreement before submitting for review.",
  "onboardingWizard.review.businessReviewRequired": "Your application review must be approved before your account can go live.",
  "onboardingWizard.review.allGood": "Everything looks good. Submit when you're ready.",
  "onboardingWizard.review.submit": "Submit for review",
  "onboardingWizard.review.submitting": "Submitting",
  "onboardingWizard.pending.title": "We're reviewing your account.",
  "onboardingWizard.pending.body": "Our operator team reviews business information separately from identity verification before opening job routing. Expect a response within one business day in most regions.",
  "onboardingWizard.pending.bodyIndividual": "Our operator team reviews your profile, showcase link, and identity status before opening job routing. Expect a response within one business day in most regions.",
  "onboardingWizard.pending.submittedAt": "Submitted {when}.",
  "onboardingWizard.pending.bulletEmail": "We'll email the operations address you provided.",
  "onboardingWizard.pending.bulletEta": "Most reviews land within one business day.",
  "onboardingWizard.pending.liveTitle": "Approved — you're ready for jobs.",
  "onboardingWizard.pending.liveBody": "Job offers will start routing to you as long as you have active capabilities, a rate card, and your availability is on.",
  "onboardingWizard.pending.liveBulletWork": "Check the work queue for incoming offers.",
  "onboardingWizard.pending.liveBulletRateCards": "Keep rate cards and availability current — it directly affects ranking.",
  "onboardingWizard.pending.agreementTitle": "Approved — accept the updated agreement to go live again.",
  "onboardingWizard.pending.agreementBody": "Nothing else is outstanding. Review and accept the current Tinkr Provider Supplier Agreement and job routing and payouts resume right away.",
  "onboardingWizard.pending.agreementBodyPayoutPending": "Accept the current Tinkr Provider Supplier Agreement and job routing resumes right away. Payouts also need your payout setup, which you can finish from the Payouts tab.",
  "onboardingWizard.pending.agreementRequired": "Accept the current Tinkr Provider Supplier Agreement to continue. Job routing and payouts stay paused until you do.",
  "onboardingWizard.pending.approvedTitle": "Approved. Turn on job intake to go live.",
  "onboardingWizard.pending.approvedBodyIntakeOff": "Your account passed review. Job offers only route to you while you are accepting jobs, and that switch is currently off.",
  "onboardingWizard.pending.approvedBodyBlocked": "Your account passed review. A few things still block job routing. Clear them below and offers will start arriving.",
  "onboardingWizard.pending.intakeTitle": "Job intake",
  "onboardingWizard.pending.intakeBody": "Turn this on when you are ready for offers. You can pause it any time from Operations.",
  "onboardingWizard.pending.startAcceptingJobs": "Start accepting jobs",
  "onboardingWizard.pending.startAcceptingJobsBusy": "Turning on",
  "onboardingWizard.pending.remainingTitle": "Still blocking job routing",
  "onboardingWizard.error.incompleteBusiness": "Fill in every required business field before continuing.",
  "onboardingWizard.adminRejection.eyebrow": "Application needs changes",
  "onboardingWizard.adminRejection.title": "An admin sent your application back",
  "onboardingWizard.adminRejection.reasonLabel": "Reason",
  "onboardingWizard.adminRejection.noteHeader": "Note from admin (original language)",
  "onboardingWizard.adminRejection.noteDisclaimer": "The note above is shown exactly as the admin wrote it and isn't translated.",
  "onboardingWizard.adminRejection.rejectedAt": "Sent back on {when}.",
  "onboardingWizard.adminRejection.callToAction": "Update the affected step below and submit for review again. Your saved business, identity, capabilities, and rate-card data are preserved.",
  "onboardingWizard.adminRejection.reason.businessDocumentsUnclear": "Business documents are unclear or unverifiable.",
  "onboardingWizard.adminRejection.reason.payoutCountryUnsupported": "Your country isn't supported by our payout rail yet.",
  "onboardingWizard.adminRejection.reason.serviceAreaMisaligned": "Your service area doesn't match your operations.",
  "onboardingWizard.adminRejection.reason.capabilitySpecsUnverifiable": "Your listed printer capabilities couldn't be verified.",
  "onboardingWizard.adminRejection.reason.pricingUnrealistic": "Your rate-card pricing is outside the reasonable range.",
  "onboardingWizard.adminRejection.reason.portfolioInsufficient": "Your portfolio photos were not enough to verify the quality or authenticity of your work.",
  "onboardingWizard.adminRejection.reason.otherSeeMessage": "Other — see the note from the admin below.",
  "onboardingWizard.statusBanner.review.title": "Onboarding is in review",
  "onboardingWizard.statusBanner.review.body": "You can keep configuring capabilities and rate cards. Job routing opens once the operator team approves you.",
  "onboardingWizard.statusBanner.incomplete.title": "Finish onboarding to receive jobs",
  "onboardingWizard.statusBanner.incomplete.body": "Continue the guided setup to open job routing for your account.",
  "onboardingWizard.statusBanner.cta": "Continue onboarding",

  // Legacy account-rejection copy retained for historical records. New
  // onboarding uses normalized identity and business-review states.
  "onboardingWizard.rejection.title": "Account verification was declined.",
  "onboardingWizard.rejection.bodyDefault": "We couldn't enable payouts on this account. Please contact our team to continue setup.",
  "onboardingWizard.rejection.reason.rejectedFraud": "Account verification was declined during fraud checks. We can't enable payouts on this account at this time.",
  "onboardingWizard.rejection.reason.rejectedTerms": "This account was declined because it doesn't fit our acceptable-use or payments partner terms.",
  "onboardingWizard.rejection.reason.rejectedListed": "This account or one of its representatives appears on a regulatory or sanctions list, so payouts can't be enabled.",
  "onboardingWizard.rejection.reason.rejectedOther": "Account verification was declined. We can't enable payouts on this account at this time.",
  "onboardingWizard.rejection.reason.requirementsPastDue": "Required business information is overdue, so payouts are paused. Submit the missing details to resume verification.",
  "onboardingWizard.rejection.errorsTitle": "Specific issues",
  "onboardingWizard.rejection.currentlyDueTitle": "Information still needed",
  "onboardingWizard.rejection.contactSupport": "Contact support",
  "onboardingWizard.rejection.refresh": "Refresh status",
  "onboardingWizard.rejection.supportSubject": "Provider verification declined",
  "onboardingWizard.rejection.supportHint": "Our team can review your account, explain next steps, and resubmit verification details on your behalf where possible.",

  // Onboarding wizard rework (founder directive 2026-07-24): first-impression
  // journey copy, per-step "why we ask" lines, live setup checklists, the
  // act-now / after-you-submit split on verification, and the step progress
  // label. Wizard-internal keys only — section-header eyebrows are owned
  // elsewhere.
  "onboardingWizard.progress.stepOf": "Step {n} of {total}",
  "onboardingWizard.checklist.title": "Your progress so far",
  "onboardingWizard.welcome.phase1Title": "You set up",
  "onboardingWizard.welcome.phase1Body": "Account basics take about ten minutes. Setting up your shop, your printers, prices, and shipping, takes as long as you like, and we save your progress.",
  "onboardingWizard.welcome.phase2Title": "We review",
  "onboardingWizard.welcome.phase2Body": "A real person checks your setup and confirms your identity before anything goes live. No instant bots.",
  "onboardingWizard.welcome.phase3Title": "You go live",
  "onboardingWizard.welcome.phase3Body": "Once you're approved, print jobs start routing to you and this dashboard becomes your daily workspace.",
  "onboardingWizard.business.why": "This is who gets paid when you complete a job.",
  "onboardingWizard.capabilities.why": "This is how we match you with jobs you can actually print.",
  "onboardingWizard.capabilities.checkPrinter": "Printer and materials saved",
  "onboardingWizard.capabilities.checkPrices": "Prices saved",
  "onboardingWizard.capabilities.checkShipping": "Shipping rate saved",
  "onboardingWizard.capabilities.missingPrinter": "a printer and materials",
  "onboardingWizard.capabilities.missingPrices": "your prices",
  "onboardingWizard.capabilities.missingShipping": "a shipping rate",
  "onboardingWizard.error.setupMissing": "Still needed before you continue: {items}.",
  "onboardingWizard.serviceArea.why": "This is how we know which orders can reach your customers.",
  "onboardingWizard.serviceArea.gateArea": "Service area set",
  "onboardingWizard.serviceArea.gateFulfillment": "Fulfillment method chosen",
  "onboardingWizard.serviceArea.gateCarriers": "Shipping carriers set",
  "onboardingWizard.portfolio.why": "A few photos of real prints show buyers the work is yours.",
  "onboardingWizard.verification.why": "One quick identity check keeps your payouts safe. You only do this once.",
  "onboardingWizard.verification.actNowTitle": "Do this now",
  "onboardingWizard.verification.afterSubmitTitle": "After you submit",
  "onboardingWizard.verification.afterSubmitBody": "Our team reviews your application and activates your provider account. Payout registration is a separate step after approval.",
  "onboardingWizard.verification.businessReviewWhat": "We check your provider details and print portfolio before you can receive orders.",
  "onboardingWizard.verification.payoutSetupWhat": "After approval, register with Wise in Payouts using your legal name, account email and payout currency. Wise collects bank details with your first payout; this setup does not block approval or jobs.",
  "onboardingWizard.verification.providerApprovalWhat": "After approval, turn on job availability in your dashboard. Receiving orders also depends on availability in your country.",
  "onboardingWizard.review.why": "Take one last look, then send your shop to our team.",
  "onboardingWizard.review.afterSubmit": "Happens after you submit",
  "onboardingWizard.pending.bulletReview": "We check your shop details, identity, and portfolio.",
  "onboardingWizard.pending.meanwhileTitle": "While you wait",
  "onboardingWizard.pending.bulletPolish": "Polish your capabilities and add more materials or finishes.",
  "onboardingWizard.pending.bulletEstimate": "Try the payout estimate in Rate Cards to see what a job could earn.",

  // Pre-launch teaser splash. Replaces the entire app surface when
  // VITE_TEASER_MODE=true. Brand name "Tinkr" is never translated; keep the
  // X brand as Latin script in every locale.
  // Translators: place the literal "Tinkr" inside teaser.headline where it
  // reads naturally; the splash highlights every occurrence of "Tinkr" in
  // the headline with the brand colour.
  "teaser.launchEyebrow": "Rolling out in stages, country by country",
  "teaser.headline": "Grow your 3D print business with Tinkr.",
  "teaser.lede": "Tinkr is a provider network designed to help print shops and independent makers receive more qualified customer requests.",
  "teaser.bullet1": "Reach customers looking for trusted 3D printing",
  "teaser.bullet2": "Keep control over the jobs you accept",
  "teaser.bullet3": "No coding or API setup required",
  "teaser.cta": "Follow on",
  "teaser.ctaLabel": "Follow on X",
  "teaser.heroAlt": "A 3D print maker behind a desktop printer in a workshop full of finished prints",
  "teaser.brandLogoAlt": "Tinkr logo",

  // ─── Admin terminal (Stitch redesign) ─────────────────────────────
  // Admin chrome is locked to English (App.tsx adminT) — these keys are
  // only added to the English dictionary and NOT included in
  // requiredLocaleKeys.

  "admin.tab.home": "Home",
  "admin.tab.orders": "Orders & support",
  "admin.tab.providers": "Providers",
  "admin.tab.partners": "Partners",
  "admin.tab.money": "Money",
  "admin.tab.countries": "Countries & shipping",
  "admin.tab.apps": "Apps & matching",
  "admin.tab.system": "System",
  "admin.home.subtab.switches": "On/off switches",
  "admin.system.subtab.dataRetention": "Guest data retention",
  "admin.topnav.label": "Admin sections",
  "admin.embedded.loading": "Loading panel",

  // WS-SHIP admin Shipping governance section (English-only admin surface).
  "admin.shipping.subtab.coverage": "Coverage",
  "admin.shipping.subtab.policy": "International shipping",
  "admin.shipping.subtab.bubbles": "Shipping groups",
  "admin.shipping.subtab.lanes": "Blocked routes",
  "admin.shipping.noSession": "Sign in as an admin to manage shipping governance.",
  "admin.shipping.cancel": "Cancel",
  "admin.shipping.field.reason": "Reason (audited)",
  "admin.shipping.confirm.reauthWarning":
    "This sensitive action requires a fresh admin sign-in and is recorded in the audit log.",
  "admin.shipping.confirm.acknowledge": "I understand this change is recorded and takes effect immediately.",
  "admin.shipping.row.none": "—",

  // Coverage dashboard
  "admin.shipping.coverage.title": "Shipping coverage",
  "admin.shipping.coverage.retry": "Retry",
  "admin.shipping.coverage.loading": "Loading shipping coverage…",
  "admin.shipping.coverage.empty": "No countries with supply, demand, policy, or bubble membership yet.",
  "admin.shipping.coverage.error": "Could not load shipping coverage.",
  "admin.shipping.coverage.generatedAt": "Generated {when}.",
  "admin.shipping.coverage.summary.countries": "Countries",

  // Country policy CRUD
  "admin.shipping.policy.title": "Country permissions",
  "admin.shipping.policy.subtitle": "Choose which countries may send or receive international orders. All countries start with international shipping off.",
  "admin.shipping.policy.openCreate": "Configure a country",
  "admin.shipping.policy.loading": "Loading policies…",
  "admin.shipping.policy.empty": "No international country permissions configured. Domestic availability follows Country Launch.",
  "admin.shipping.policy.error": "Could not load country policies.",
  "admin.shipping.policy.saved": "Saved policy for {country}.",
  "admin.shipping.policy.deleted": "Removed policy for {country}.",
  "admin.shipping.policy.col.country": "Country",
  "admin.shipping.policy.col.intlOrigin": "May send abroad",
  "admin.shipping.policy.col.intlDest": "May receive from abroad",
  "admin.shipping.policy.col.blocked": "International pause",
  "admin.shipping.policy.col.actions": "Actions",
  "admin.shipping.policy.yes": "On",
  "admin.shipping.policy.no": "Off",
  "admin.shipping.policy.edit": "Edit",
  "admin.shipping.policy.delete": "Delete",
  "admin.shipping.policy.save": "Save permissions",
  "admin.shipping.policy.editTitle": "International shipping: {country}",
  "admin.shipping.policy.createTitle": "Configure international shipping",
  "admin.shipping.policy.field.country": "Country",
  "admin.shipping.policy.field.intlOrigin": "Allow providers here to ship abroad",
  "admin.shipping.policy.field.intlDest": "Allow deliveries here from abroad",
  "admin.shipping.policy.field.blocked": "Pause international shipping to and from this country",
  "admin.shipping.policy.field.customerNotice": "Delivery notice for this country (shown to customers)",
  "admin.shipping.policy.field.adminNotes": "Admin notes (internal)",
  "admin.shipping.policy.confirmDelete.title": "Remove international permissions",
  "admin.shipping.policy.confirmDelete.body": "Remove the saved international permissions for {country}? Sending and receiving go back to Off and any pause is cleared, so new cross-border routes to and from it are refused until you set it up again. Committed deliveries continue. Domestic ordering still follows Country Launch.",

  // Shipping bubbles CRUD
  "admin.shipping.bubbles.title": "Shipping groups (bubbles)",
  "admin.shipping.bubbles.subtitle": "A directional group permits providers in its origin countries to deliver to its destination countries. It only takes effect when the group, international shipping and each country permission are all on. Groups do not set delivery prices.",
  "admin.shipping.bubbles.openCreate": "Create shipping group",
  "admin.shipping.bubbles.loading": "Loading bubbles…",
  "admin.shipping.bubbles.empty": "No shipping bubbles yet.",
  "admin.shipping.bubbles.error": "Could not load shipping bubbles.",
  "admin.shipping.bubbles.saved": "Saved bubble {name}.",
  "admin.shipping.bubbles.deleted": "Deleted bubble {name}.",
  "admin.shipping.bubbles.enabled": "On",
  "admin.shipping.bubbles.disabled": "Off",
  "admin.shipping.bubbles.col.name": "Name",
  "admin.shipping.bubbles.col.slug": "Slug",
  "admin.shipping.bubbles.col.type": "Type",
  "admin.shipping.bubbles.col.origins": "Origins",
  "admin.shipping.bubbles.col.destinations": "Destinations",
  "admin.shipping.bubbles.col.actions": "Actions",
  "admin.shipping.bubbles.edit": "Edit",
  "admin.shipping.bubbles.delete": "Delete",
  "admin.shipping.bubbles.save": "Save shipping group",
  "admin.shipping.bubbles.editTitle": "Edit shipping group — {name}",
  "admin.shipping.bubbles.createTitle": "New shipping group",
  "admin.shipping.bubbles.field.slug": "Reference ID (lowercase letters and dashes; cannot be changed)",
  "admin.shipping.bubbles.field.name": "Name",
  "admin.shipping.bubbles.field.description": "Description",
  "admin.shipping.bubbles.field.type": "Type",
  "admin.shipping.bubbles.field.enabled": "Group is on",
  "admin.shipping.bubbles.field.enabledHelp": "When off, this group permits no routes, so a route covered only by it is refused. Its countries are kept. Turning it on needs at least one origin and one destination country, and international shipping must also be on.",
  "admin.shipping.bubbles.field.customerNotice": "Customer notice",
  "admin.shipping.bubbles.field.adminNotes": "Admin notes (internal)",
  "admin.shipping.bubbles.type.regional": "Regional",
  "admin.shipping.bubbles.type.international": "International",
  "admin.shipping.bubbles.confirmDelete.title": "Delete shipping group",
  "admin.shipping.bubbles.confirmDelete.body": "Delete the shipping group {name}? Its origin and destination countries are removed, and a route covered only by this group is refused from now on. Committed deliveries continue.",

  // Blocked lanes
  "admin.shipping.lanes.title": "Blocked shipping lanes",
  "admin.shipping.lanes.subtitle":
    "Admin bans on international lanes or destinations. These override provider preferences and bubble membership.",
  "admin.shipping.lanes.openCreate": "Block a lane",
  "admin.shipping.lanes.loading": "Loading blocked lanes…",
  "admin.shipping.lanes.empty": "No blocked lanes.",
  "admin.shipping.lanes.error": "Could not load blocked lanes.",
  "admin.shipping.lanes.saved": "Blocked shipping to {to}.",
  "admin.shipping.lanes.deleted": "Removed block on {to}.",
  "admin.shipping.lanes.allOrigins": "All origins",
  "admin.shipping.lanes.platformWide": "Platform-wide",
  "admin.shipping.lanes.col.from": "From",
  "admin.shipping.lanes.col.to": "To",
  "admin.shipping.lanes.col.scope": "Scope",
  "admin.shipping.lanes.col.reason": "Reason",
  "admin.shipping.lanes.col.actions": "Actions",
  "admin.shipping.lanes.remove": "Remove",
  "admin.shipping.lanes.create": "Block lane",
  "admin.shipping.lanes.createTitle": "Block a shipping lane",
  "admin.shipping.lanes.field.from": "From country (ISO-2; blank = all origins)",
  "admin.shipping.lanes.field.to": "To country (ISO-2)",
  "admin.shipping.lanes.field.scope": "App key scope (blank = platform-wide)",
  "admin.shipping.lanes.field.adminNotes": "Admin notes (internal)",
  "admin.shipping.lanes.confirmDelete.title": "Remove blocked lane",
  "admin.shipping.lanes.confirmDelete.body": "Remove the block on {from} → {to}? International shipping on this lane becomes possible again (subject to provider and policy gates).",
  "admin.actions.retry": "Retry",
  "admin.actions.cancel": "Cancel",
  "admin.actions.back": "Back to list",
  "admin.field.empty": "—",

  // Providers section
  "admin.providers.subtab.directory": "Directory",
  "admin.providers.subtab.awaiting": "Awaiting Activation",
  "admin.providers.subtab.quality": "Quality Incidents",
  "admin.providers.subtab.operations": "Limits & bulk actions",
  "admin.providers.subtab.payouts": "Provider settlements",
  "admin.providers.subtab.wisePayouts": "Wise batches",
  "admin.providers.subtab.payoutRuns": "Scheduled payouts",
  "admin.providers.searchPlaceholder": "Search providers…",
  "admin.providers.filter.all": "All",
  "admin.providers.filter.new": "New",
  "admin.providers.filter.active": "Active",
  "admin.providers.filter.banned": "Banned",
  "admin.providers.empty": "No providers match the current filters.",
  "admin.providers.loading": "Loading providers",
  "admin.providers.loadMore": "Load more",
  "admin.providers.loadingMore": "Loading more…",
  "admin.providers.directory.truncatedNotice":
    "More providers exist beyond the loaded set. The material, process, and capacity filters (and their options) apply only to the loaded set — load more below, search, or narrow the country, entity type, or submitted-date filters for complete results.",
  "admin.providers.error.fallback": "Couldn't load providers.",
  "admin.providers.error.reasonTooShort": "Reason must be at least 10 characters.",
  "admin.providers.error.confirmRequired": "Tick the confirmation checkbox before submitting.",
  "admin.providers.error.transitionRejected": "Transition rejected: {reason}",
  "admin.providers.error.unknown": "unknown",
  "admin.providers.feedback.transitionApplied": "Provider state updated.",
  "admin.providers.detail.placeholderTitle": "Select a provider",
  "admin.providers.detail.placeholderBody":
    "Pick a row from the list to view detail, run state transitions, and surface enforcement controls.",
  "admin.providers.detail.loading": "Loading provider detail",
  "admin.providers.detail.joined": "Joined",
  "admin.providers.detail.openInOperations": "Open in Operations",
  "admin.providers.detail.viewPayouts": "View payouts",
  "admin.providers.action.warn": "Warn",
  "admin.providers.action.suspend": "Suspend",
  "admin.providers.action.reinstate": "Reinstate",
  "admin.providers.action.ban": "Ban Provider",
  "admin.providers.action.deactivate": "Deactivate",
  "admin.providers.transition.title": "Submit state transition",
  "admin.providers.transition.subtitle":
    "Command: {command}. Reason is recorded in the audit log.",
  "admin.providers.transition.reasonLabel": "Reason (≥ 10 characters)",
  "admin.providers.transition.reasonPlaceholder":
    "Why is this action being taken? Recorded in the audit log.",
  "admin.providers.transition.confirmLabel":
    "I understand this is a destructive admin action.",
  "admin.providers.transition.submit": "Submit",
  "admin.providers.transition.submitting": "Submitting…",
  "admin.providers.section.capabilities": "Technical Capabilities",
  "admin.providers.section.recentOrders": "Recent Order Fulfillment",
  "admin.providers.section.danger": "Administrative Actions",
  "admin.providers.field.primaryEmail": "Primary Email",
  "admin.providers.field.emailPending":
    "No email on file for this provider owner.",
  "admin.providers.field.legalName": "Legal Name",
  "admin.providers.field.country": "Country",
  "admin.providers.payoutRail.manual": "Manual workflow",
  "admin.providers.payoutRail.wise": "Wise batch",
  "admin.providers.payoutRail.none": "Not set",
  "admin.providers.capabilities.empty":
    "No printer capabilities registered yet.",
  "admin.providers.capabilities.count":
    "{count} capability record(s) registered. Full editor in the provider's own dashboard.",
  "admin.providers.recentOrders.filter.ongoing": "Ongoing",
  "admin.providers.recentOrders.filter.completed": "Completed",
  "admin.providers.recentOrders.col.id": "Job ID",
  "admin.providers.recentOrders.col.state": "State",
  "admin.providers.recentOrders.col.channel": "Channel",
  "admin.providers.recentOrders.col.customer": "Customer",
  "admin.providers.recentOrders.col.updated": "Updated",
  "admin.providers.recentOrders.loading": "Loading recent jobs…",
  "admin.providers.recentOrders.loadFailed": "Could not load this provider's recent jobs.",
  "admin.providers.recentOrders.emptyOngoing":
    "No ongoing jobs assigned to this provider.",
  "admin.providers.recentOrders.emptyCompleted":
    "No completed or cancelled jobs in this provider's recent history.",
  // Providers > Directory > Fulfillment strikes (admin-only, English chrome).
  "admin.providers.section.strikes": "Fulfillment Strikes",
  "admin.providers.strikes.loading": "Loading fulfillment strikes…",
  "admin.providers.strikes.loadFailed":
    "Could not load this provider's fulfillment strikes.",
  "admin.providers.strikes.empty": "No strikes",
  "admin.providers.strikes.emptyBody":
    "This provider has never had an accepted engagement reclaimed or withdrawn.",
  "admin.providers.strikes.summary":
    "{inWindow} of {threshold} strikes inside the current {window}-day window. {total} recorded in total.",
  "admin.providers.strikes.retentionNote":
    "Strikes are never deleted. They stop counting once they fall outside the window, and resuming fulfillment clears the pause but not the history.",
  "admin.providers.strikes.col.kind": "Kind",
  "admin.providers.strikes.col.recorded": "Recorded",
  "admin.providers.strikes.col.order": "Order",
  "admin.providers.strikes.col.reason": "Reason",
  "admin.providers.strikes.col.window": "Counts now",
  "admin.providers.strikes.inWindow": "Yes",
  "admin.providers.strikes.outOfWindow": "Aged out",
  "admin.providers.strikes.kind.productionOverdue": "Production overdue, reclaimed",
  "admin.providers.strikes.kind.deliveryOverdue": "Hand-off overdue, reclaimed",
  "admin.providers.strikes.kind.providerWithdrew": "Provider withdrew",
  "admin.providers.strikes.openOrder": "Open order {id}",
  // Providers > Directory > Customer ratings (admin-only, English chrome).
  // Internal data: providers never see ratings or feedback.
  "admin.providers.section.ratings": "Customer Ratings",
  "admin.providers.ratings.loading": "Loading customer ratings…",
  "admin.providers.ratings.loadFailed": "Could not load this provider's customer ratings.",
  "admin.providers.ratings.empty": "No ratings yet",
  "admin.providers.ratings.emptyBody":
    "No customer has rated a delivered order from this provider yet.",
  "admin.providers.ratings.average": "Average rating",
  "admin.providers.ratings.noAverage": "No average yet",
  "admin.providers.ratings.count": "Ratings",
  "admin.providers.ratings.lowCount": "Rated 3 stars or below",
  "admin.providers.ratings.lastRated": "Last rated {date}",
  "admin.providers.ratings.distribution": "Rating distribution",
  "admin.providers.ratings.distributionRow": "{stars} ★",
  "admin.providers.ratings.starsLabel": "{stars} of 5 stars",
  "admin.providers.ratings.col.rated": "Rated",
  "admin.providers.ratings.col.stars": "Stars",
  "admin.providers.ratings.col.order": "Order",
  "admin.providers.ratings.col.channel": "Channel",
  "admin.providers.ratings.col.feedback": "Feedback",
  "admin.providers.ratings.channelUnknown": "Unknown",
  "admin.providers.ratings.noFeedback": "No comment",
  "admin.providers.ratings.openOrder": "Open order {id}",
  "admin.providers.ratings.loadMore": "Load more ratings",
  "admin.providers.ratings.loadingMore": "Loading…",
  "admin.providers.ratings.internalNote":
    "Internal only. Providers never see ratings or feedback and are never notified when an order is rated.",
  "admin.providers.ratings.rowSummary": "★ {average} · {count} ratings",
  "admin.providers.ratings.rowSummaryOne": "★ {average} · 1 rating",
  "admin.providers.ratings.rowNone": "No ratings yet",
  "admin.providers.danger.footnote":
    "Destructive admin actions are reauth-gated and logged. Earned payouts on completed jobs are never withheld by these controls.",

  // Providers > Directory richer filters (WS6)
  "admin.providers.filters.title": "Filters",
  "admin.providers.filters.entityType": "Provider type",
  "admin.providers.filters.entityType.all": "All types",
  "admin.providers.filters.entityType.business": "Business",
  "admin.providers.filters.entityType.individual": "Individual",
  "admin.providers.filters.country": "Country",
  "admin.providers.filters.countryPlaceholder": "ISO code, e.g. GB",
  "admin.providers.filters.material": "Material",
  "admin.providers.filters.materialAll": "Any material",
  "admin.providers.filters.process": "Print process",
  "admin.providers.filters.processAll": "Any process",
  "admin.providers.filters.businessReview": "Application review",
  "admin.providers.filters.businessReviewAll": "Any application review",
  "admin.providers.filters.businessReview.notSubmitted": "Not submitted",
  "admin.providers.filters.businessReview.pending": "Pending",
  "admin.providers.filters.businessReview.approved": "Approved",
  "admin.providers.filters.businessReview.moreInformation": "More information required",
  "admin.providers.filters.businessReview.rejected": "Rejected",
  "admin.providers.filters.identity": "Identity verification",
  "admin.providers.filters.identityAll": "Any identity status",
  "admin.providers.filters.identity.notStarted": "Not started",
  "admin.providers.filters.identity.requiresInput": "Requires input",
  "admin.providers.filters.identity.processing": "Processing",
  "admin.providers.filters.identity.verified": "Verified",
  "admin.providers.filters.identity.failed": "Failed",
  "admin.providers.filters.identity.cancelled": "Cancelled",
  "admin.providers.filters.identity.expired": "Expired",
  "admin.providers.filters.payoutSetup": "Payout setup",
  "admin.providers.filters.payoutSetupAll": "Any payout setup",
  "admin.providers.filters.payoutSetup.notStarted": "Not started",
  "admin.providers.filters.payoutSetup.pending": "Pending",
  "admin.providers.filters.payoutSetup.configured": "Configured",
  "admin.providers.filters.payoutSetup.disabled": "Disabled",
  "admin.providers.filters.capacityMin": "Min capacity",
  "admin.providers.filters.maxAverageRating": "Average rating at most",
  "admin.providers.filters.maxAverageRatingAny": "Any rating",
  "admin.providers.filters.maxAverageRatingOption": "{value} ★ or below",
  "admin.providers.filters.minRatingCount": "Minimum ratings",
  "admin.providers.filters.sortLowestRating": "Sort: lowest rating first",
  "admin.providers.filters.submittedFrom": "Submitted from",
  "admin.providers.filters.submittedTo": "Submitted to",
  "admin.providers.filters.apply": "Apply",
  "admin.providers.filters.reset": "Reset",
  "admin.providers.row.materials": "Materials",
  "admin.providers.row.process": "Process",
  "admin.providers.row.capacity": "Capacity",
  "admin.providers.row.capacityUnlimited": "No limit",
  "admin.providers.row.none": "—",
  "admin.providers.flag.missingCapabilities": "No capabilities",
  "admin.providers.flag.missingCountry": "No country",
  "admin.providers.flag.businessReviewPending": "Application review pending",
  "admin.providers.flag.payoutsDisabled": "Payouts off",
  "admin.providers.flag.fulfillmentPaused": "Fulfillment paused",
  "admin.providers.flag.lowRating": "Low rating",

  // Providers > Coverage view (WS6)
  "admin.providers.coverage.title": "Marketplace coverage by country",
  "admin.providers.coverage.subtitle":
    "Active provider supply against trailing-{days}-day order demand, keyed by account country. Low and no-coverage countries are highlighted.",
  "admin.providers.coverage.loading": "Loading coverage",
  "admin.providers.coverage.error": "Couldn't load coverage.",
  "admin.providers.coverage.empty":
    "No providers or recent orders yet, so there is no coverage to summarize.",
  "admin.providers.coverage.retry": "Retry",
  "admin.providers.coverage.generatedAt": "As of {when}",
  "admin.providers.coverage.summary.countries": "Countries",
  "admin.providers.coverage.summary.activeProviders": "Active providers",
  "admin.providers.coverage.summary.gaps": "Coverage gaps",
  "admin.providers.coverage.summary.gapsHint":
    "Countries with recent demand but no active provider supply.",
  "admin.providers.coverage.col.country": "Country",
  "admin.providers.coverage.col.active": "Active",
  "admin.providers.coverage.col.inactive": "Onboarding",
  "admin.providers.coverage.col.materials": "Materials",
  "admin.providers.coverage.col.processes": "Processes",
  "admin.providers.coverage.col.capacity": "Capacity",
  "admin.providers.coverage.col.recentOrders": "Orders ({days}d)",
  "admin.providers.coverage.gap.none": "No coverage",
  "admin.providers.coverage.gap.low": "Low coverage",
  "admin.providers.coverage.gap.ok": "Covered",
  "admin.providers.coverage.legend":
    "No coverage = recent orders with zero active providers. Low coverage = a single active provider.",

  // Partners section
  "admin.partners.subtab.directory": "Directory",
  "admin.partners.businessReview.title": "Identity and business review",
  "admin.partners.businessReview.decisionError": "Could not record this decision.",
  "admin.partners.businessReview.pendingHelp": "Review the partner's application and legal details before deciding. This decision is separate from Wise payout registration.",
  "admin.partners.businessReview.openDecision": "Review business decision",
  "admin.partners.businessReview.drawerTitle": "Record partner business decision",
  "admin.partners.businessReview.drawerSubtitle": "Review the declared business details in the partner record before making this audited decision.",
  "admin.partners.businessReview.messageOptional": "Message to partner (optional)",
  "admin.partners.businessReview.messageRequired": "Message to partner (required)",
  "admin.partners.businessReview.messageHelp": "Shown to the partner on their onboarding screen. Give actionable next steps for a rejection or information request. If provided, use at least 10 characters.",
  "admin.partners.businessReview.confirm": "I confirm this business review decision: {decision}.",
  "admin.partners.subtab.applications": "Access requests",
  "admin.partners.subtab.controls": "Partner settings",
  "admin.partners.subtab.referralFees": "Referral Fees",
  "admin.partners.searchPlaceholder": "Search partners…",
  "admin.partners.filter.all": "All",
  "admin.partners.filter.pending": "Pending",
  "admin.partners.filter.active": "Active",
  "admin.partners.filter.suspended": "Suspended",
  "admin.partners.filter.terminated": "Terminated",
  "admin.partners.empty": "No partners match the current filters.",
  "admin.partners.loading": "Loading partners",
  "admin.partners.loadMore": "Load more",
  "admin.partners.loadingMore": "Loading more…",
  "admin.partners.error.fallback": "Couldn't load partners.",
  "admin.partners.error.reasonTooShort": "Reason must be at least 10 characters.",
  "admin.partners.error.confirmRequired": "Tick the confirmation checkbox before submitting.",
  "admin.partners.error.transitionRejected": "Transition rejected: {reason}",
  "admin.partners.error.unknown": "unknown",
  "admin.partners.feedback.transitionApplied": "Partner state updated.",
  "admin.partners.detail.placeholderTitle": "Select a partner",
  "admin.partners.detail.placeholderBody":
    "Pick a row from the list to view API keys, webhooks, settlement posture, and run partner transitions.",
  "admin.partners.detail.loading": "Loading partner detail",
  "admin.partners.detail.joined": "Joined",
  "admin.partners.action.approve": "Approve Partner",
  "admin.partners.action.approveConfigure": "Configure & Approve",
  "admin.partners.action.reject": "Reject",
  "admin.partners.action.suspend": "Suspend Partner",
  "admin.partners.action.reactivate": "Reactivate",
  "admin.partners.action.reactivateConfigure": "Configure & Reactivate",
  "admin.partners.action.terminate": "Terminate Partner",
  "admin.partners.transition.title": "Submit partner transition",
  "admin.partners.transition.subtitle":
    "Command: {command}. Reason is recorded in the audit log.",
  "admin.partners.transition.reasonLabel": "Reason (≥ 10 characters)",
  "admin.partners.transition.reasonPlaceholder":
    "Why is this action being taken? Recorded in the audit log.",
  "admin.partners.transition.confirmLabel":
    "I understand this is a destructive admin action.",
  "admin.partners.transition.submit": "Submit",
  "admin.partners.transition.submitting": "Submitting…",
  "admin.partners.section.company": "Company & Contact",
  "admin.partners.section.controls": "Controls (summary)",
  "admin.partners.section.controlsEdit": "Edit in Controls →",
  "admin.partners.section.controlsFootnote":
    "These are admin-set: settlement mode, enabled tiers, messaging modes, and flow flags. Click \"Edit in Controls\" above to change them — the Controls sub-tab carries the full editor with reauth-gated transitions.",
  "admin.partners.section.apiKeys": "API Keys",
  "admin.partners.section.webhooks": "Webhook Subscriptions",
  "admin.partners.section.settlement": "Settlement & Payouts",
  "admin.partners.section.danger": "Danger Zone",
  "admin.partners.field.contactEmail": "Contact Email",
  "admin.partners.field.legalName": "Legal Name",
  "admin.partners.field.slug": "Slug",
  "admin.partners.field.settlementMode": "Settlement Mode",
  "admin.partners.field.enabledTiers": "Enabled Tiers",
  "admin.partners.field.enabledMessagingModes": "Enabled Messaging Modes",
  "admin.partners.field.enabledFlowFlags": "Enabled Flow Flags",
  "admin.partners.field.creditLimit": "Credit Limit (minor units)",
  "admin.partners.field.reserveRequirement": "Reserve Requirement (minor units)",
  "admin.partners.field.payoutHoldPolicy": "Payout Hold Policy",
  "admin.partners.apiKeys.empty":
    "No API keys yet. Issue one via the Controls sub-tab so the partner can authenticate.",
  "admin.partners.apiKeys.name": "Key name",
  "admin.partners.apiKeys.scopes": "Scopes",
  "admin.partners.apiKeys.rateTier": "Rate tier",
  "admin.partners.apiKeys.lastUsed": "Last used",
  "admin.partners.apiKeys.status": "Status",
  "admin.partners.apiKeys.footnote":
    "Mint, rotate, and revoke API keys from the Controls sub-tab.",
  "admin.partners.webhooks.empty":
    "No webhook subscriptions configured.",
  "admin.partners.webhooks.endpoint": "Endpoint URL",
  "admin.partners.webhooks.events": "Event types",
  "admin.partners.webhooks.lastDelivery": "Last delivery",
  "admin.partners.webhooks.enabled": "Status",
  "admin.partners.webhooks.footnote":
    "Manage webhook subscriptions and quote-display QA from the Controls sub-tab.",
  "admin.partners.danger.footnote":
    "Terminating a partner is irreversible. Use Suspend for temporary holds; terminate only when the relationship is permanently ended.",

  // Retired General tab: these admin.general.subtab.* label keys keep the
  // old name as naming debt; the pages now live in the 8-section layout.
  "admin.general.subtab.overview": "Overview",
  "admin.general.subtab.orders": "Orders",
  "admin.general.subtab.maintenance": "Maintenance",
  "admin.general.subtab.announcements": "Announcements",
  "admin.general.subtab.health": "Operations Health",
  "admin.general.subtab.appStatus": "Configuration check",
  "admin.general.subtab.storageCleanup": "Storage Cleanup",
  "admin.general.subtab.audit": "Audit Log",
  "admin.general.subtab.feedback": "Feedback",
  "admin.general.subtab.guestSupport": "Guest support",
  "admin.general.subtab.doodlesSupport": "Doodles support",
  "admin.general.subtab.bulkOrders": "Bulk orders",
  "admin.general.subtab.settlement": "Payment claims & holds",
  "admin.general.subtab.pricing": "Pricing",
  "admin.general.subtab.modelSimulator": "Model simulator",
  "admin.general.subtab.fees": "Fees",
  "admin.general.subtab.coupons": "Coupons",
  "admin.general.subtab.legalDocuments": "Legal Documents",
  "admin.general.subtab.forex": "Currency fees",
  "admin.general.subtab.countryLaunch": "Countries",
  "admin.general.subtab.taxReport": "Tax Collected",
  "admin.general.subtab.matchingConfig": "Matching",
  "admin.general.subtab.matchingWeights": "Matching weights",
  "admin.general.subtab.standardColors": "Standard Colors",
  "admin.general.subtab.firstPartyKeys": "App API keys",
  "admin.general.subtab.enforcement": "Enforcement",
  "admin.general.subtab.traffic": "Order channels",
  "admin.general.subtab.fulfillment": "Late & stuck orders",
  "admin.general.attention.queue.orderLiveness": "Orders requiring recovery",
  "adminTraffic.title": "Order channels",
  "adminTraffic.subtitle": "Each channel is a place orders come from: a first-party app or the partner API. Turn a channel off to refuse its new orders. To stop one partner only, use Enforcement.",
  "adminTraffic.refresh": "Refresh channels",
  "adminTraffic.loading": "Loading channels…",
  "adminTraffic.empty": "No order channels registered.",
  "adminTraffic.noSession": "Sign in with an admin account to view order channels.",
  "adminTraffic.volume": "Jobs: {total} total · {h24} last 24h · {d7} last 7d",
  "adminTraffic.pausedNote": "Turned off {at}: {reason}",
  "adminTraffic.state.on": "On",
  "adminTraffic.state.off": "Off",
  "adminTraffic.whenOff": "When off, new orders and new payment links from this channel are refused. Orders already placed are not cancelled.",
  "adminTraffic.whenOffPartner": "When off, partners cannot create jobs or send existing drafts to providers. Jobs already with providers are not cancelled.",
  "adminTraffic.pause": "Turn off",
  "adminTraffic.resume": "Turn on",
  "adminTraffic.drawerPauseTitle": "Turn off order channel",
  "adminTraffic.drawerResumeTitle": "Turn on order channel",
  "adminTraffic.pauseBody": "Turn off {name}? New orders from it are refused (503 channel_paused) and no new payment links are issued until you turn it back on. Orders already paid or in progress are not affected.",
  "adminTraffic.pauseBodyPartner": "Turn off {name}? Partners cannot create new jobs or send existing drafts to providers (503 channel_paused) until you turn it back on. Jobs already with providers are not affected. To stop one partner only, use Enforcement.",
  "adminTraffic.resumeBody": "Turn on {name}? It accepts new orders again straight away.",
  "adminTraffic.reasonField": "Reason (audited)",
  "adminTraffic.acknowledge": "I understand this changes live order handling.",
  "adminTraffic.cancel": "Cancel",
  "adminTraffic.confirmPause": "Turn off",
  "adminTraffic.confirmResume": "Turn on",
  "adminTraffic.saving": "Saving…",
  "adminTraffic.saved": "Channel {key} is now {status}.",
  "adminTraffic.reauthWarning": "This action requires recent admin reauthentication.",
  "adminTraffic.dashboardLocked": "Always on. The core dashboard channel cannot be turned off.",
  "adminTraffic.error.listFailed": "Could not load order channels.",
  "adminTraffic.error.updateFailed": "Could not update the channel.",
  "adminTraffic.error.reasonRequired": "A reason of at least 3 characters is required.",
  "admin.nav.group.payouts": "Payouts",
  "admin.nav.group.pricingFees": "Pricing & fees",
  "admin.nav.group.reports": "Reports",
  "admin.nav.group.monitoring": "Monitoring",
  "admin.nav.group.platform": "Platform",
  "admin.nav.group.accessData": "Access & data",
  "admin.nav.search.label": "Search admin pages",
  "admin.nav.search.placeholder": "Search admin…",
  "admin.nav.search.noResults": "No matching admin pages",
  "admin.general.overview.title": "Platform Overview",
  "admin.general.overview.subtitle":
    "What is waiting on you, plus platform-wide metrics.",
  "admin.general.metric.totalProviders": "Total Providers",
  "admin.general.metric.activePartners": "Active Partners",
  "admin.general.metric.monthlyOrders": "Monthly Orders",
  "admin.general.metric.avgFulfillmentHrs": "Avg Fulfillment Time (hrs)",
  "admin.general.metric.unavailable": "Metric unavailable",
  // "Waiting on you" card (Home > Overview). Admin chrome is English-only,
  // so these keys live in the en dict and are never added to
  // publicLocalizedMessageKeys. Queue labels are looked up through a typed
  // key map in AdminGeneralOverview; an unrecognised queue key from the API
  // falls back to a humanized label instead of a missing-key crash.
  "admin.general.attention.title": "Waiting on you",
  "admin.general.attention.subtitle":
    "Queues with work sitting in them right now. Open one to work it down.",
  "admin.general.attention.loading": "Checking the queues…",
  "admin.general.attention.allClear": "All clear. No queue is waiting on an admin.",
  "admin.general.attention.unavailable":
    "Queue counts are unavailable right now. Open the queues directly to check.",
  "admin.general.attention.oldest": "oldest: {age}",
  "admin.general.attention.open": "Open this queue",
  "admin.general.attention.queue.providerBusinessReview": "Provider business review",
  "admin.general.attention.queue.providersAwaitingActivation":
    "Providers awaiting activation",
  "admin.general.attention.queue.orderRefundsManualReview": "Refunds in manual review",
  "admin.general.attention.queue.settlementsAwaitingApproval":
    "Settlements awaiting approval",
  "admin.general.attention.queue.payoutsInFlight": "Payouts in flight",
  "admin.general.attention.queue.wiseBatchesOpen": "Open Wise batches",
  "admin.general.attention.queue.paymentClaimsPending": "Pending payment claims",
  "admin.general.attention.queue.qualityIncidentsOpen": "Open quality incidents",
  "admin.general.attention.queue.feedbackNew": "New feedback",
  "admin.general.attention.queue.guestIssuesOpen": "Open guest issues",
  "admin.general.attention.queue.doodlesSupportOpen": "Doodles support requests",
  "admin.general.attention.queue.partnerApplicationsPending":
    "Pending partner applications",
  "admin.general.attention.queue.partnerProductionApplicationsPending":
    "Pending production access reviews",
  "admin.general.attention.queue.fulfillmentOverdue": "Overdue fulfillment",
  "admin.general.attention.queue.countryPrivacyReviewsDue": "Country privacy reviews due",
  "admin.general.attention.queue.guestStorageCleanupDeadLetter": "Guest storage deletions needing repair",
  "admin.general.attention.queue.doodlesStorageCleanupDeadLetter": "Doodles storage deletions needing repair",
  "admin.general.attention.queue.premium_reviews_pending": "Quality verification requests",
  // Audit log (admin-native rewrite — replaces embedded AdminActivityFeedPanel)
  "admin.feedback.title": "Feedback",
  "admin.feedback.subtitle":
    "In-app feedback from providers and partners: bug reports, feature requests, and integration interest. Transitions require a reason and are recorded in the audit log.",
  "admin.feedback.listTitle": "Triage queue",
  "admin.feedback.listSubtitle":
    "Newest first. Expand a row for the full message and the reporter's UI context (including the request id of their most recent failed API call).",
  "admin.feedback.filter.status": "Status",
  "admin.feedback.filter.category": "Category",
  "admin.feedback.filter.all": "All",
  "admin.feedback.status.new": "New",
  "admin.feedback.status.reviewed": "Reviewed",
  "admin.feedback.status.closed": "Closed",
  "admin.feedback.category.bug": "Bug",
  "admin.feedback.category.feature": "Feature request",
  "admin.feedback.category.general": "General",
  "admin.feedback.category.integration": "Integration interest",
  "admin.feedback.refresh": "Refresh feedback",
  "admin.feedback.loading": "Loading feedback…",
  "admin.feedback.empty": "No feedback matches the current filters.",
  "admin.feedback.noSession": "Sign in with admin access to triage feedback.",
  "admin.feedback.loadMore": "Load more",
  "admin.feedback.field.user": "Reporter user ID",
  "admin.feedback.field.context": "UI context",
  "admin.feedback.action.markReviewed": "Mark reviewed",
  "admin.feedback.action.close": "Close",
  "admin.feedback.action.reopen": "Reopen",
  "admin.feedback.transitionSaved": "Feedback updated to {status}.",
  "admin.feedback.drawer.subtitle": "The reason is recorded in the audit log.",
  "admin.feedback.drawer.reason": "Reason (3+ characters)",
  "admin.feedback.drawer.reasonPlaceholder": "e.g. Reproduced and filed internally; closing the report.",
  "admin.feedback.drawer.reauthNote":
    "Admin actions require a recent reauth. If you have not signed in recently you will see a password prompt.",
  "admin.audit.title": "Audit Log",
  "admin.audit.subtitle":
    "Compliance and forensics audit trail. Every admin write action is recorded with the before/after state and the reason supplied.",
  "admin.audit.refresh": "Refresh",
  "admin.audit.loading": "Loading audit entries…",
  "admin.audit.loadingMore": "Loading more…",
  "admin.audit.loadMore": "Load more",
  "admin.audit.filter.title": "Filters",
  "admin.audit.filter.clear": "Clear filters",
  "admin.audit.filter.adminUser": "Admin user ID",
  "admin.audit.filter.adminUserPlaceholder": "00000000-0000-0000-0000-000000000000",
  "admin.audit.filter.targetType": "Target type",
  "admin.audit.filter.targetTypePlaceholder": "Any type, or pick a suggestion",
  "admin.audit.filter.targetId": "Target ID",
  "admin.audit.filter.targetIdPlaceholder": "Specific row UUID or aggregate label",
  "admin.audit.filter.from": "From",
  "admin.audit.filter.to": "To",
  "admin.audit.list.title": "Activity",
  "admin.audit.list.count": "{count} entries",
  "admin.audit.empty.unfiltered":
    "No audit entries recorded yet.",
  "admin.audit.empty.filtered": "No entries match the current filters.",
  "admin.audit.empty.filteredBody":
    "Adjust the filters or clear them to see all recent admin activity.",
  "admin.audit.col.occurredAt": "Occurred at",
  "admin.audit.col.admin": "Admin user",
  "admin.audit.col.action": "Action",
  "admin.audit.col.target": "Target",
  "admin.audit.col.summary": "Summary",
  "admin.audit.detail.reason": "Reason",
  "admin.audit.detail.before": "Before",
  "admin.audit.detail.after": "After",
  "admin.audit.row.expand": "Expand audit row",
  "admin.audit.row.collapse": "Collapse audit row",
  "admin.audit.exportCsv": "Export CSV",
  "admin.audit.exporting": "Preparing CSV…",
  "admin.audit.exportLimit":
    "More than 10,000 entries match. Narrow the filters and export again.",

  // Drawer titles for the partner-applications review drawer (moved
  // out of the inline form into AdminDrawer).
  "adminPartnerApplications.drawer.approveTitle": "Approve partner application",
  "adminPartnerApplications.drawer.rejectTitle": "Reject partner application",
  "adminMaintenance.banner.activeLabel": "Maintenance in progress:",
  "adminMaintenance.banner.moreActive": "+ {count} more active",
  "adminQuality.openIncidentTrigger": "New incident",
  "adminMaintenance.openCreate": "Schedule new",
  "adminAnnouncements.openCreate": "Compose announcement",
  "adminEnforcement.openCreate": "New enforcement",

  // Single-recipient notification composer (AdminNotifyUserDrawer).
  // Shared provider/partner typeahead chrome (AdminProviderPicker /
  // AdminPartnerPicker). English-only, like the rest of the admin terminal —
  // the picker resolves these through translate("en", …) rather than taking a
  // Translate prop, because several host panels carry no `t`.
  "admin.picker.placeholder": "Search by name, or paste an ID",
  "admin.picker.selected": "Selected:",
  "admin.picker.clear": "Clear selection",
  "admin.picker.useTypedId": "Use ID as typed",
  "admin.picker.loadFailed": "Could not load the directory.",
  "admin.picker.manualFallback": "You can still paste an ID directly.",
  "admin.picker.provider.loading": "Loading providers…",
  "admin.picker.provider.empty": "No providers match. Paste a provider ID instead.",
  // Worded to stay true both while the page is unfiltered and once the typed
  // needle is searched server-side: either way a full page means there may be
  // more behind it, and the fix is the same (type more, or paste the ID).
  "admin.picker.provider.truncated":
    "Showing the first 100 results. Add more of the name to narrow the search, or paste the provider ID if it is missing.",
  "admin.picker.partner.loading": "Loading partners…",
  "admin.picker.partner.empty": "No partners match. Paste a partner ID instead.",
  "admin.picker.partner.truncated":
    "Showing the first 100 results. Add more of the name to narrow the search, or paste the partner ID if it is missing.",
  // English-only admin surface — content localization happens per-recipient
  // via the drawer's own translation rows, not via these chrome keys.
  "admin.notify.action": "Notify",
  "admin.notify.open": "Notify user",
  "admin.notify.drawerTitle": "Send notification",
  "admin.notify.drawerSubtitle":
    "Deliver an in-app notification to a single provider or partner. Use Announcements for broadcasts.",
  "admin.notify.recipientLabel": "Recipient",
  "admin.notify.recipientTypeField": "Recipient type",
  "admin.notify.recipientIdField": "Recipient ID",
  "admin.notify.recipientIdHint":
    "UUID of the provider or partner to notify (from the directory or an audit entry).",
  "admin.notify.categoryField": "Category",
  "admin.notify.severityField": "Severity",
  "admin.notify.defaultLocaleField": "Default locale",
  "admin.notify.titleField": "Title",
  "admin.notify.bodyField": "Body",
  "admin.notify.translationsLegend": "Translations",
  "admin.notify.translationsHint":
    "Optional. Recipients see the version matching their locale; everyone else falls back to the default locale.",
  "admin.notify.addTranslation": "Add translation",
  "admin.notify.removeTranslation": "Remove translation",
  "admin.notify.translationLocaleField": "Locale",
  "admin.notify.actionUrlField": "Action URL (optional)",
  "admin.notify.actionUrlHint":
    "For provider recipients, use a dashboard section path such as /provider/work-queue so the notification deep-links into that section.",
  "admin.notify.reasonField": "Reason (audit log)",
  "admin.notify.send": "Send notification",
  "admin.notify.sending": "Sending",
  "admin.notify.sent": "Notification sent ({notificationId}).",
  "admin.notify.cancel": "Cancel",
  "admin.notify.error.recipientId": "Enter a valid UUID for the recipient.",
  "admin.notify.error.translationDuplicate":
    "Each translation locale must be unique and different from the default locale.",
  "admin.notify.error.sendFailed": "Could not send the notification.",

  "onboarding.countryAvailability.title": "Country availability",
  "onboarding.countryAvailability.off": "Opening soon",
  "onboarding.countryAvailability.test": "Opening soon",
  "onboarding.countryAvailability.live": "Live",
  "onboarding.countryAvailability.closed": "Closed",
  "onboarding.orderEligibility.title": "Order eligibility",
  "onboarding.orderEligibility.notEligible": "Waiting for country launch",
  "onboarding.orderEligibility.testOnly": "Waiting for country launch",
  "onboarding.orderEligibility.eligible": "Eligible for live orders",
  "onboarding.orderEligibility.closed": "No new orders",
  "onboarding.countryCopy.off":
    "Real orders aren't open in {country} yet. Finish your setup now and we'll email you the moment orders go live.",
  "onboarding.countryCopy.test":
    "Real orders aren't open in {country} yet. Finish your setup now and we'll email you the moment orders go live.",
  "onboarding.countryCopy.live":
    "Your country is live. Eligible providers can receive real customer orders.",
  "onboarding.countryCopy.closed": "Tinkr has stopped taking new orders in {country}. Paid orders, payouts and disputes continue. We'll email you if it reopens.",
  // Standard colors admin governance panel. Admin surfaces are English-only by
  // product decision (operator surface, not customer-facing) — no locale
  // overrides, en fallback is intentional.
  "admin.colors.title": "Standard colors",
  "admin.colors.subtitle":
    "Curate the customer-facing color families providers declare support for. Archiving keeps history intact; it only stops a color being selectable in new flows.",
  "admin.colors.openCreate": "Add color",
  "admin.colors.noSession": "Sign in as an admin to manage standard colors.",
  "admin.colors.loading": "Loading standard colors…",
  "admin.colors.empty": "No standard colors yet. Add the first color family.",
  "admin.colors.error": "Could not load standard colors.",
  "admin.colors.error.slugConflict": "A color with that slug already exists.",
  "admin.colors.col.swatch": "",
  "admin.colors.col.name": "Name",
  "admin.colors.col.slug": "Slug",
  "admin.colors.col.hex": "Hex",
  "admin.colors.col.state": "Status",
  "admin.colors.col.flags": "Flags",
  "admin.colors.col.sortOrder": "Order",
  "admin.colors.col.support": "Usual / Source",
  "admin.colors.col.updated": "Updated",
  "admin.colors.col.actions": "Actions",
  "admin.colors.state.active": "Active",
  "admin.colors.state.archived": "Archived",
  "admin.colors.flag.common": "common",
  "admin.colors.flag.hidden": "hidden from customers",
  "admin.colors.flag.none": "—",
  "admin.colors.edit": "Edit",
  "admin.colors.archive": "Archive",
  "admin.colors.restore": "Restore",
  "admin.colors.moveUp": "Move {name} up",
  "admin.colors.moveDown": "Move {name} down",
  "admin.colors.cancel": "Cancel",
  "admin.colors.save": "Save color",
  "admin.colors.createTitle": "Add standard color",
  "admin.colors.editTitle": "Edit {name}",
  "admin.colors.created": "Added {name}.",
  "admin.colors.saved": "Saved {name}.",
  "admin.colors.archived": "Archived {name}.",
  "admin.colors.restored": "Restored {name}.",
  "admin.colors.restoreReason": "Restore standard color",
  "admin.colors.reorderReason": "Reorder standard colors",
  "admin.colors.field.slug": "Slug",
  "admin.colors.field.slugImmutable": "Slug can't be changed after creation.",
  "admin.colors.field.name": "Name (canonical English)",
  "admin.colors.field.hex": "Hex color",
  "admin.colors.field.hexPicker": "Color picker",
  "admin.colors.field.description": "Description",
  "admin.colors.field.sortOrder": "Sort order",
  "admin.colors.field.isCommon": "Common color (suggest to providers)",
  "admin.colors.field.showToCustomers": "Show to customers",
  "admin.colors.field.isActive": "Active (selectable)",
  "admin.colors.field.reason": "Reason (audit)",
  "admin.colors.archiveNote":
    "Existing orders and provider support keep this color. It just stops being selectable in new flows.",
  "admin.colors.confirmArchive.title": "Archive this color?",
  "admin.colors.confirmArchive.body":
    "Archiving {name} removes it from new customer and provider selections.",
  "admin.colors.confirmArchive.acknowledge":
    "I understand existing orders and provider support are unaffected.",
  // First-party API keys admin panel. Admin surfaces are English-only by
  // product decision (operator surface, not customer-facing).
  "admin.fpkeys.title": "First-party API keys",
  "admin.fpkeys.subtitle":
    "Server-side keys held by Tinkr's own apps (dashboard, MakerChip, Doodles). The secret is shown once at creation; only a hash and a short prefix are stored.",
  "admin.fpkeys.openCreate": "Provision key",
  "admin.fpkeys.noSession": "Sign in as an admin to manage first-party API keys.",
  "admin.fpkeys.loading": "Loading first-party API keys…",
  "admin.fpkeys.empty": "No first-party API keys yet. Provision the first key.",
  "admin.fpkeys.error": "Could not load first-party API keys.",
  "admin.fpkeys.error.selfKey":
    "That key is serving this dashboard right now. Provision and deploy a replacement first, then revoke this one.",
  "admin.fpkeys.error.writeRejected": "The key write was rejected. Refresh and try again.",
  "admin.fpkeys.col.name": "Name",
  "admin.fpkeys.col.keyPrefix": "Key",
  "admin.fpkeys.col.status": "Status",
  "admin.fpkeys.col.created": "Created",
  "admin.fpkeys.col.lastUsed": "Last used",
  "admin.fpkeys.col.requests": "Requests (30d)",
  "admin.fpkeys.col.errors": "Errors (30d)",
  "admin.fpkeys.col.throttled": "429s (30d)",
  "admin.fpkeys.col.actions": "Actions",
  "admin.fpkeys.state.active": "Active",
  "admin.fpkeys.state.revoked": "Revoked",
  "admin.fpkeys.state.expired": "Expired",
  "admin.fpkeys.state.current": "This dashboard",
  "admin.fpkeys.rotate": "Rotate",
  "admin.fpkeys.revoke": "Revoke",
  "admin.fpkeys.currentKeyGuard":
    "This key authenticated the current request. Deploy a replacement key first.",
  "admin.fpkeys.cancel": "Cancel",
  "admin.fpkeys.create": "Provision",
  "admin.fpkeys.createTitle": "Provision first-party API key",
  "admin.fpkeys.createSubtitle":
    "One key per app per environment. The plaintext appears once after creation.",
  "admin.fpkeys.createNote":
    "After provisioning, store the secret in the app's server-side environment (for example TINKR_FIRST_PARTY_API_KEY on Cloudflare Pages, or a Worker secret). Never expose it to browser code.",
  "admin.fpkeys.field.name": "Name",
  "admin.fpkeys.field.nameHelp": "Which app and environment holds this key.",
  "admin.fpkeys.field.expires": "Expires (optional)",
  "admin.fpkeys.field.expiresHelp":
    "Leave empty for no expiry. The key stays valid through the end of the chosen day (local time); an expired key stops authenticating immediately, which takes the app offline.",
  "admin.fpkeys.field.reason": "Reason (audit)",
  "admin.fpkeys.created": "Provisioned {name}.",
  "admin.fpkeys.rotated":
    "Rotated {name}. The old secret has stopped working; deploy the new secret now.",
  "admin.fpkeys.revoked": "Revoked {name}.",
  "admin.fpkeys.revealed.title": "Copy this key now",
  "admin.fpkeys.revealed.created": "Provisioned {name}.",
  "admin.fpkeys.revealed.rotated": "Rotated {name}.",
  "admin.fpkeys.revealed.note":
    "Shown once and auto-hides in 60 seconds. Store it in the app's server-side environment; never in browser code.",
  "admin.fpkeys.revealed.copy": "Copy",
  "admin.fpkeys.revealed.copied": "Copied to clipboard.",
  "admin.fpkeys.revealed.copyFailed": "Copy failed. Select the key text manually.",
  "admin.fpkeys.revealed.hide": "Hide",
  "admin.fpkeys.confirmRevoke.title": "Revoke {name}?",
  "admin.fpkeys.confirmRevoke.body":
    "Requests using this key start failing immediately. The app holding it loses API access until a replacement key is deployed.",
  "admin.fpkeys.confirmRotate.title": "Rotate {name}?",
  "admin.fpkeys.confirmRotate.body":
    "Rotation revokes the current secret immediately and issues a new one. The app fails until the new secret is deployed.",
  "admin.fpkeys.confirm.acknowledge":
    "I understand this app's API access stops until the new secret is deployed.",
  "admin.fpkeys.never": "Never",
  "admin.fpkeys.usage.title": "Daily usage",
  "admin.fpkeys.usage.subtitle":
    "Last 30 days per key. Successful requests are weighted estimates when sampling is enabled; throttled and failed calls are counted individually. Operator visibility only; the API is not billed.",
  "admin.fpkeys.usage.keyLabel": "Key",
  "admin.fpkeys.usage.col.day": "Day (UTC)",
  "admin.fpkeys.usage.col.requests": "Requests",
  "admin.fpkeys.usage.col.success": "Success",
  "admin.fpkeys.usage.col.clientErrors": "4xx",
  "admin.fpkeys.usage.col.throttled": "429",
  "admin.fpkeys.usage.col.serverErrors": "5xx",
  "admin.fpkeys.usage.empty": "No requests recorded in this window.",
  "admin.fpkeys.usage.unavailable":
    "Usage stats are temporarily unavailable. Key actions above still work.",
  // Admin legal documents panel. Rows in legal_documents are the
  // version/acceptance authority; rendered bodies ship as code in the docs
  // portal, so publishing is fail-closed against the deployed-body manifest.
  // Admin copy is English-only (operator surface).
  "admin.legal.title": "Legal Documents",
  "admin.legal.subtitle":
    "Versioned Terms of Service, Privacy Policy and Provider Supplier Agreement records — the acceptance authority behind the login gate, checkout gates and provider onboarding.",
  "admin.legal.lockstepNote":
    "Document bodies ship as code in the docs portal. Publishing a version here is the second step: the code change that renders the new body (and bumps the shared version constants) must be deployed first — publishing is refused until the deployed body matches. Publishing supersedes the current version and re-prompts every affected account at next sign-in.",
  "admin.legal.deployedBodies": "Deployed bodies:",
  "admin.legal.openCreate": "New draft",
  "admin.legal.createTitle": "Create draft version",
  "admin.legal.createSubtitle":
    "Drafts are inert: never current, never published, and safe to retire. Use version N.M for a release candidate or draft-YYYY-MM-DD for a working copy.",
  "admin.legal.create": "Create draft",
  "admin.legal.cancel": "Cancel",
  "admin.legal.publish": "Publish",
  "admin.legal.retire": "Retire",
  "admin.legal.loading": "Loading legal documents…",
  "admin.legal.empty": "No legal document versions recorded.",
  "admin.legal.noSession": "Sign in with an admin account to manage legal documents.",
  "admin.legal.created": "Draft {version} created.",
  "admin.legal.publishedMessage":
    "Version {version} published. Affected accounts will be re-prompted at next sign-in.",
  "admin.legal.retiredMessage": "Version {version} retired.",
  "admin.legal.type.terms": "Terms of Service",
  "admin.legal.type.privacy": "Privacy Policy",
  "admin.legal.type.providerAgreement": "Provider Supplier Agreement",
  "admin.legal.state.current": "Current",
  "admin.legal.state.draft": "Draft",
  "admin.legal.state.published": "Published",
  "admin.legal.state.retired": "Retired",
  "admin.legal.col.document": "Document",
  "admin.legal.col.version": "Version",
  "admin.legal.col.title": "Title",
  "admin.legal.col.status": "Status",
  "admin.legal.col.effective": "Effective",
  "admin.legal.col.acceptances": "Acceptances",
  "admin.legal.col.actions": "Actions",
  "admin.legal.field.documentType": "Document",
  "admin.legal.field.version": "Version",
  "admin.legal.field.versionHelp":
    "N.M (e.g. 1.1) for a release candidate, or draft-YYYY-MM-DD for a working copy. Only N.M versions can ever be published.",
  "admin.legal.field.title": "Title",
  "admin.legal.field.summary": "Summary of changes",
  "admin.legal.field.summaryHelp":
    "Shown to users on the re-acceptance prompt. Optional but strongly recommended.",
  "admin.legal.field.reason": "Reason (audit log)",
  "admin.legal.draftNote":
    "Creating a draft changes nothing for users. Publishing is a separate, guarded step.",
  "admin.legal.confirmPublish.title": "Publish {document} {version}?",
  "admin.legal.confirmPublish.body":
    "Publishing supersedes the current version in the same transaction and re-prompts every affected account (sign-in gate for Terms/Privacy; provider onboarding for the Supplier Agreement). The deployed docs portal must already render this exact version — the API refuses otherwise. This cannot be undone; the only way past a published version is publishing its successor.",
  "admin.legal.confirmPublish.typeVersion": "Type the version to confirm ({version})",
  "admin.legal.confirmPublish.acknowledge":
    "I confirm the rendered body for this exact version is live in the docs portal and counsel/founder sign-off is recorded in the reason.",
  "admin.legal.confirmRetire.title": "Retire {document} {version}?",
  "admin.legal.confirmRetire.body":
    "Retiring abandons this non-current version. Recorded acceptances keep their evidence, but the version can never be published afterwards. The current published version can never be retired — only superseded by a publish.",
  "admin.legal.confirmRetire.acknowledge":
    "I understand this version is permanently retired.",
  "admin.legal.error": "Something went wrong managing legal documents.",
  "admin.legal.error.version":
    "Version must be N.M (e.g. 1.1) or draft-YYYY-MM-DD.",
  "admin.legal.error.title": "Title is required (max 200 characters).",
  "admin.legal.error.summary": "Summary must be 2000 characters or fewer.",
  "admin.legal.error.reason": "A reason of at least 3 characters is required.",
  "admin.legal.error.versionExists":
    "That version already exists for this document type.",
  "admin.legal.error.bodyDraft":
    "Blocked: the deployed docs portal still marks this document body as a draft. Ship the code change that publishes the body first.",
  "admin.legal.error.bodyVersionMismatch":
    "Blocked: the deployed docs portal renders version {deployed}, not this one. Ship the body + version-constant code change first, then publish.",
  "admin.legal.error.retireCurrent":
    "The current published version cannot be retired — publish a successor instead.",
  // Admin coupons panel. Coupons discount checkout and are funded from
  // Tinkr's platform margin, never from provider payouts. Admin copy is
  // English-only (operator surface).
  "admin.coupons.title": "Coupons",
  "admin.coupons.subtitle":
    "Percent or fixed-amount discounts applied at checkout, optionally scoped to one traffic channel.",
  "admin.coupons.fundedNote":
    "Coupons are funded from Tinkr's platform margin, not provider payouts. Providers are always paid on the full order; if a discount is larger than the platform fee on an order, Tinkr absorbs the difference.",
  "admin.coupons.openCreate": "Create coupon",
  "admin.coupons.noSession": "Sign in as an admin to manage coupons.",
  "admin.coupons.loading": "Loading coupons…",
  "admin.coupons.empty": "No coupons yet. Create the first one.",
  "admin.coupons.searchLabel": "Search",
  "admin.coupons.searchPlaceholder": "Filter by code, description or status…",
  "admin.coupons.emptyFiltered": "No coupons match this search.",
  "admin.coupons.error": "Could not load coupons.",
  "admin.coupons.error.codeExists": "A coupon with this code already exists.",
  "admin.coupons.channelsUnavailable":
    "Channel scoping is temporarily unavailable. You can still create ecosystem-wide coupons.",
  "admin.coupons.discount.unknown": "—",
  "admin.coupons.col.code": "Code",
  "admin.coupons.col.discount": "Discount",
  "admin.coupons.col.scope": "Scope",
  "admin.coupons.col.window": "Window",
  "admin.coupons.col.usage": "Usage",
  "admin.coupons.col.status": "Status",
  "admin.coupons.col.actions": "Actions",
  "admin.coupons.state.active": "Active",
  "admin.coupons.state.revoked": "Revoked",
  "admin.coupons.state.expired": "Expired",
  "admin.coupons.state.exhausted": "Exhausted",
  "admin.coupons.scope.ecosystem": "Entire ecosystem",
  "admin.coupons.window.noEnd": "No end date",
  "admin.coupons.usage.unlimited": "∞",
  "admin.coupons.revoke": "Revoke",
  "admin.coupons.cancel": "Cancel",
  "admin.coupons.create": "Create coupon",
  "admin.coupons.createTitle": "Create coupon",
  "admin.coupons.createSubtitle":
    "Set the discount, optional scope, optional window, and optional usage limit. A reason is required for the audit log.",
  "admin.coupons.field.code": "Code",
  "admin.coupons.field.codeHelp":
    "Uppercase letters, numbers, hyphens, underscores. What customers type at checkout.",
  "admin.coupons.field.description": "Description (optional)",
  "admin.coupons.field.discountType": "Discount type",
  "admin.coupons.discountType.percent": "Percent off",
  "admin.coupons.discountType.fixed": "Fixed amount off",
  "admin.coupons.field.percent": "Percent off",
  "admin.coupons.field.percentHelp": "0.01% to 100%, up to 2 decimal places.",
  "admin.coupons.field.restrictCurrency": "Restrict to one currency",
  "admin.coupons.field.percentCurrency": "Currency",
  "admin.coupons.field.currencyPlaceholder": "Select a currency…",
  "admin.coupons.field.maxDiscount": "Max discount amount (optional)",
  "admin.coupons.field.maxDiscountHelp": "Caps the discount amount in the currency above.",
  "admin.coupons.field.fixedAmount": "Amount off",
  "admin.coupons.field.fixedCurrency": "Currency",
  "admin.coupons.field.scope": "Scope",
  "admin.coupons.field.startsAt": "Starts (optional)",
  "admin.coupons.field.startsAtHelp": "Leave empty to start immediately.",
  "admin.coupons.field.endsAt": "Ends (optional)",
  "admin.coupons.field.endsAtHelp": "Leave empty for no end date.",
  "admin.coupons.field.usageLimit": "Total usage limit (optional)",
  "admin.coupons.field.usageLimitHelp": "Leave empty for unlimited redemptions.",
  "admin.coupons.field.reason": "Reason (audit)",
  "admin.coupons.created": "Created {code}.",
  "admin.coupons.revoked": "Revoked {code}.",
  "admin.coupons.confirmRevoke.title": "Revoke {code}?",
  "admin.coupons.confirmRevoke.body":
    "This coupon stops applying to new checkouts immediately. Orders already placed with it are unaffected.",
  "admin.coupons.confirm.acknowledge": "I understand this takes effect immediately.",
  "admin.coupons.error.code": "Enter a code (letters, numbers, hyphens, underscores).",
  "admin.coupons.error.reason": "Reason must be at least 3 characters.",
  "admin.coupons.error.startsAt": "Enter a valid start date and time.",
  "admin.coupons.error.endsAt": "Enter a valid end date and time.",
  "admin.coupons.error.endBeforeStart": "The end date must be after the start date.",
  "admin.coupons.error.usageLimit": "Usage limit must be a whole number of 1 or more.",
  "admin.coupons.error.percent": "Enter a percent between 0.01 and 100, up to 2 decimal places.",
  "admin.coupons.error.currency": "Enter a recognised currency code (fiat or stablecoin).",
  "admin.coupons.error.maxDiscount": "Enter a valid max discount amount.",
  "admin.coupons.error.fixedAmount": "Enter a valid amount greater than zero.",
  "admin.coupons.error.wholeCurrency": "This currency has no decimal places; enter a whole number.",
  // Per-screen SEO metadata (src/lib/seo.ts). Public copy: localized for
  // every supported locale and enforced by publicLocalizedMessageKeys.
  "seo.splash.title": "Tinkr | 3D printing fulfillment network for print providers",
  "seo.splash.description":
    "Tinkr routes paid 3D print jobs to workshops like yours. Files arrive print-ready and pricing follows your rate card. Platforms add on-demand 3D printing with the partner API.",
  "seo.api.title": "Tinkr Partner API | On-demand 3D printing for your platform",
  "seo.api.description":
    "Add on-demand 3D printing to your storefront, marketplace, or app. Send production-ready orders to the Tinkr provider network with quotes, webhooks, and settlement built in.",
  "seo.auth.title": "Provider sign in and signup | Tinkr",
  "seo.auth.description":
    "Sign in to your Tinkr provider workspace or create an account to start receiving 3D print jobs.",
  "seo.docs.title": "Documentation for providers and partners | Tinkr",
  "seo.docs.description":
    "Guides for Tinkr print providers and integration partners: onboarding, rate cards, payouts, the partner jobs API, and webhooks.",
  // Database rule and rejection-code copy (2026-09-07 silent-500 sweep).
  "apiError.constraint.showcaseUrl": "Add a showcase or portfolio URL.",
  "apiError.constraint.shippingLaneDuplicate": "You already have a shipping tier with the same destination, service level, limits and currency.",
  "apiError.constraint.materialDuplicate": "Each material can appear only once on a rate card.",
  "apiError.constraint.localeTag": "Use a locale tag such as en, ja or en-US.",
  "apiError.constraint.endAfterStart": "The end time must be at or after the start time.",
  "apiError.constraint.expiryAfterEffective": "The expiry must be in the future.",
  "apiError.constraint.reasonLength": "The reason must be between 3 and 1000 characters.",
  "apiError.constraint.httpUrl": "Enter a full URL starting with http:// or https://.",
  "apiError.constraint.httpsUrl": "Enter a full https:// URL.",
  "apiError.constraint.webhookEndpoint": "The endpoint must be an https:// URL with no spaces.",
  "apiError.constraint.urlNoSpaces": "The URL must not contain spaces.",
  "apiError.constraint.providerExists": "This account already has a provider profile.",
  "apiError.constraint.applicationExists": "This account already has a provider application.",
  "apiError.kind.checkViolation": "That input does not meet the \"{rule}\" rule.",
  "apiError.kind.notNull": "{column} is required.",
  "apiError.kind.foreignKey": "One of the referenced records does not exist.",
  "apiError.kind.tooLong": "A value is longer than allowed.",
  "apiError.kind.invalidValue": "A value has the wrong type or is out of range.",
  "apiError.kind.notPermitted": "This action is not permitted for your account ({reason}).",
  "apiError.kind.generic": "The request contains a value the system cannot accept.",
  "apiError.duplicate": "A record with the same {columns} already exists.",
  "apiError.duplicateGeneric": "A matching record already exists.",
  "apiError.validation.field": "{field}: {message}",
  "apiError.rejection.generic": "The request was rejected ({code}).",
  "apiError.rejection.trackingRequired": "A tracking number or tracking link is required for this shipping service.",
  "apiError.rejection.printPhotoRequired": "Add at least one photo of the finished print before you mark the order shipped.",
  "apiError.rejection.shipmentAlreadyRecorded": "A shipment has already been recorded for this order.",
  "apiError.rejection.shipmentLocked": "The shipment can no longer be edited for this order.",
  "apiError.rejection.shipmentNotRecorded": "Record the shipment before editing its tracking.",
  "apiError.rejection.idempotencyConflict": "This request was already submitted with different details. Refresh and try again.",
  "apiError.rejection.invalidState": "The order has moved on since this page loaded. Refresh and try again.",
  "apiError.rejection.orderPaymentRequired": "The customer's payment has not been confirmed yet.",
  "apiError.rejection.engagementNotFound": "This order is no longer assigned to you.",
  "apiError.rejection.invalidShippingRates": "One of the shipping tiers is invalid or duplicates another tier. Nothing was saved.",
  "apiError.rejection.invalidRateCard": "The rate card contains an invalid or duplicated entry. Nothing was saved.",
  "apiError.rejection.endpointUrlRequired": "Enter the webhook endpoint URL.",
  "apiError.rejection.eventTypesRequired": "Select at least one event type before enabling the subscription.",
  "apiError.rejection.signingSecretRequired": "A signing secret is required before enabling the subscription.",
  "apiError.rejection.subscriptionNotFound": "That webhook subscription no longer exists.",
  "apiError.rejection.productionReviewPending": "A production application is already under review.",
  "apiError.rejection.invalidApplicationFields": "Some application fields are missing or invalid.",
  "apiError.rejection.partnerIdentityNotVerified": "The partner representative must complete identity verification before you can approve production access. Ask the partner to finish it, then refresh this review.",
  "apiError.rejection.partnerBusinessReviewNotApproved": "Approve the partner business review before approving production access. Review it in the partner directory, then retry.",
  "apiError.rejection.identityRefreshThrottled": "A verification link was requested recently. Wait a moment and refresh this page for the current link.",
  "apiError.rejection.invalidOrigin": "Origins must be https:// URLs with a lowercase host and no path.",
  "apiError.rejection.appAlreadyExists": "An app with that ID already exists.",
  "apiError.rejection.duplicateOriginEnvironment": "That origin is already registered for this environment.",
  "apiError.rejection.appOriginRequired": "Add at least one origin for the app.",
  "apiError.rejection.activeRequestKey": "You cannot revoke the key that authorised this request."
} as const;

export type MessageKey = keyof typeof en;
export type Dictionary = Record<MessageKey, string>;
export type Translate = (key: MessageKey) => string;
export const englishMessageKeys = Object.freeze(Object.keys(en) as MessageKey[]);

// MERGED-PERF-001: the prior `DictionaryMap` type required `en` to be
// the full Dictionary and non-EN locales to be Partial<Dictionary> at
// the type level. After splitting non-EN locales into dynamic chunks,
// every entry in `dictionaries` starts empty and gets populated by
// `loadLocale`, so the runtime type collapses to a single
// `Record<SupportedLocale, Partial<Dictionary>>`. The old type alias
// is no longer referenced — removed.

export const publicLocalizedMessageKeys = [
  "brand.fulfillment",
  "brand.operations",
  "consent.aria",
  "consent.body",
  "consent.accept",
  "consent.decline",
  "nav.apiAccess",
  "nav.login",
  "nav.returnDashboard",
  "nav.dashboard",
  "nav.language",
  "nav.themeLight",
  "nav.themeDark",
  "splash.apiAccess",
  "splash.appsBody",
  "splash.appsDecorCta",
  "splash.appsDecorDesc",
  "splash.appsDoodlesCta",
  "splash.appsDoodlesDesc",
  "splash.appsEyebrow",
  "splash.appsMakerchipCta",
  "splash.appsMakerchipDesc",
  "splash.appsPlatformAndroid",
  "splash.appsPlatformApple",
  "splash.appsPlatformWeb",
  "splash.appsStatusBeta",
  "splash.appsStatusComingSoon",
  "splash.appsStatusLive",
  "splash.appsTitle",
  "splash.closerEyebrow",
  "splash.closerLine1",
  "splash.closerLine2",
  "splash.closerLine3",
  "splash.consoleBody",
  "splash.consoleLink",
  "splash.consoleTitle",
  "seo.api.description",
  "seo.api.title",
  "seo.auth.description",
  "seo.auth.title",
  "seo.docs.description",
  "seo.docs.title",
  "seo.splash.description",
  "seo.splash.title",
  "splash.heroEyebrow",
  "splash.heroLine1",
  "splash.heroLine2",
  "splash.storySkip",
  "splash.storyReplay",
  "splash.skipToContent",
  "splash.heroSub",
  "splash.hiwEyebrow",
  "splash.hiwRateNote",
  "splash.hiwStep1Body",
  "splash.hiwStep1Title",
  "splash.hiwStep2Body",
  "splash.hiwStep2Title",
  "splash.hiwStep3Body",
  "splash.hiwStep3Title",
  "splash.hiwStep4Body",
  "splash.hiwStep4Title",
  "splash.hiwTitle",
  "splash.itemBracket",
  "splash.itemCameraMount",
  "splash.itemDroneArm",
  "splash.itemJigSet",
  "splash.providerSignup",
  "splash.railEyebrow",
  "splash.simAccept",
  "splash.simAccepted",
  "splash.simDecline",
  "splash.simNewOffer",
  "splash.simPrinting",
  "splash.tagGearHousing",
  "splash.tagLayer",
  "splash.tagOffer",
  "splash.tagQaPassed",
  "splash.tagShipped",
  "splash.tagTracking",
  "auth.accountCountry",
  "auth.accountCountryHelp",
  "auth.countrySearch",
  "auth.countryNotOpen",
  "auth.countryNoMatches",
  "auth.countryAvailabilityHelp",
  "auth.countryNoneOpen",
  "auth.countryChecking",
  "auth.countryCheckFailed",
  "auth.countryUnavailable",
  "auth.accountCountryLocked",
  "auth.accountCountryPlaceholder",
  "auth.alreadyHaveAccount",
  "auth.backToLogin",
  "auth.businessEmail",
  "auth.businessName",
  "auth.businessNamePlaceholder",
  "auth.changePassword",
  "auth.changePasswordBody",
  "auth.changePasswordTitle",
  "auth.checkEmailBody",
  "auth.checkEmailTitle",
  "auth.signupConfirmationBody",
  "auth.confirmPassword",
  "auth.confirmPasswordPlaceholder",
  "auth.continueWithApple",
  "auth.continueWithGoogle",
  "auth.countryRequired",
  "auth.createAccount",
  "auth.createAccountLink",
  "auth.currentPassword",
  "auth.currentPasswordPlaceholder",
  "auth.currentPasswordRequired",
  "auth.dashboardResetLink",
  "auth.emailPlaceholder",
  "auth.errorFallback",
  "auth.forgotPassword",
  "auth.forgotShort",
  "auth.hidePassword",
  "auth.invalidCredentials",
  "auth.invitePasswordUpdated",
  "auth.inviteSetupLinkRequired",
  "auth.inviteSetupReadyBody",
  "auth.inviteSetupTitle",
  "auth.legalConsent",
  "auth.legalConsentPrivacy",
  "auth.legalConsentRequired",
  "auth.legalConsentTerms",
  "auth.legalDraftUnavailable",
  "auth.loginSuccess",
  "auth.loginTitle",
  "auth.missingConfig",
  "auth.newPassword",
  "auth.newToTinkr",
  "auth.or",
  "auth.panelLoginBody",
  "auth.panelLoginTitle1",
  "auth.panelLoginTitle2",
  "auth.panelSignupBody",
  "auth.panelSignupTitle1",
  "auth.panelSignupTitle2",
  "auth.panelTagline",
  "auth.password",
  "auth.passwordHelp",
  "auth.passwordMismatch",
  "auth.passwordPlaceholder",
  "auth.passwordResetBody",
  "auth.passwordResetLinkRequired",
  "auth.passwordResetReadyBody",
  "auth.passwordResetSent",
  "auth.passwordResetTitle",
  "auth.passwordServerRequirement",
  "auth.passwordTooShort",
  "auth.passwordUpdated",
  "auth.rateLimited",
  "auth.resendVerification",
  "auth.resolvingSession",
  "auth.sendSecureLink",
  "auth.sending",
  "auth.setPassword",
  "auth.showPassword",
  "auth.signInLink",
  "auth.signInWithPassword",
  "auth.signupTitle",
  "auth.updatePassword",
  "auth.verificationResent",
  "auth.verifyBeforeDashboard",
  "auth.verifyBody",
  "auth.verifyTitle",
  "api.alreadyApproved",
  "api.body",
  "api.comingSoonChip",
  "api.comingSoonNotice",
  "api.controlledLane",
  "api.docsLink",
  "api.explainerAccessBody",
  "api.explainerAccessTitle",
  "api.explainerReviewBody",
  "api.explainerReviewTitle",
  "api.explainerUseCaseBody",
  "api.explainerUseCaseTitle",
  "api.lanePartnerJobs",
  "api.lanePartnerJobsDesc",
  "api.laneProviderPlugins",
  "api.laneProviderPluginsDesc",
  "api.laneSettlementEvidence",
  "api.laneSettlementEvidenceDesc",
  "api.laneWebhookDelivery",
  "api.laneWebhookDeliveryDesc",
  "api.loginWithAccess",
  "api.reviewSla",
  "api.title",
  "legalGate.eyebrow",
  "legalGate.title",
  "legalGate.body",
  "legalGate.versionLabel",
  "legalGate.effectiveLabel",
  "legalGate.readDocument",
  "legalGate.checkbox",
  "legalGate.accept",
  "legalGate.acceptError",
  "legalGate.checking",
  "legalGate.unavailableEyebrow",
  "legalGate.unavailableTitle",
  "legalGate.unavailableBody",
  "legalGate.retry",
  "legalGate.signOut",
  "stitch.nav.account",
  "account.eyebrow",
  "account.title",
  "account.deletion.title",
  "account.deletion.body",
  "account.deletion.retentionNote",
  "account.deletion.blockedJobs",
  "account.deletion.blockedPayouts",
  "account.deletion.blockedPartner",
  "account.deletion.confirmLabel",
  "account.deletion.passwordLabel",
  "account.deletion.submit",
  "account.deletion.pendingTitle",
  "account.deletion.pendingBody",
  "account.deletion.cancel",
  "account.deletion.cancelled",
  "account.deletion.requested",
  "account.deletion.error",
  "account.deletion.statusLoading",
  "account.export.title",
  "account.actions.title",
  "account.actions.body",
  "account.export.body",
  "account.export.submit",
  "account.export.ready",
  "account.export.error",
  "footer.contact",
  "footer.registered",
  "footer.companyNo",
  "footer.registeredOffice",
  "footer.terms",
  "footer.privacy",
  "footer.legalLinksLabel",
  "footer.rights",
  // MERGED-PROV-004: provider notification bell copy. Per apps/web/AGENTS.md
  // provider-facing surfaces are localized for every supported locale, so
  // any new dashboard-chrome key visible to providers belongs in this list
  // for the i18n coverage test to enforce.
  "stitch.notifications",
  "stitch.notifications.empty",
  "partnerAnnouncements.loading",
  "partnerAnnouncements.empty",
  "partnerAnnouncements.refresh",
  "partnerAnnouncements.error",
  "partnerAnnouncements.ackFailed",
  "partnerAnnouncements.docs",
  "partnerApiBrief.sdk.updates",
  "stitch.notifications.bellWithCount",
  "stitch.notifications.sectionOrders",
  "stitch.notifications.sectionAnnouncements",
  "stitch.notifications.markRead",
  "stitch.notifications.markReadSaving",
  "stitch.notifications.clear",
  "stitch.notifications.clearing",
  "stitch.notifications.clearOne",
  "stitch.notifications.clearRead",
  "stitch.notifications.clearAll",
  "stitch.notifications.clearAllConfirm",
  "stitch.notifications.clearAllConfirmAction",
  "stitch.notifications.cancel",
  "partnerAnnouncements.clearAcknowledged",
  "partnerAnnouncements.clearFailed",
  "workQueue.p2.tab.missed",
  "workQueue.p2.tab.missedLabel",
  "workQueue.p2.tab.completed",
  "workQueue.p2.tab.completedLabel",
  "workQueue.p2.status.completedOn",
  "workQueue.completed.empty",
  "workQueue.completed.emptyBody",
  "workQueue.missed.intro",
  "workQueue.missed.empty",
  "workQueue.missed.summaryLabel",
  "workQueue.missed.col.closed",
  "workQueue.missed.col.outcome",
  "workQueue.missed.outcome.timedOut",
  "workQueue.missed.outcome.taken",
  "workQueue.missed.outcome.cancelled",
  "workQueue.missed.outcome.withdrawn",
  "workQueue.missed.explain.timedOut",
  "workQueue.missed.explain.taken",
  "workQueue.missed.explain.cancelled",
  "workQueue.missed.explain.withdrawn",
  "workQueue.missed.payout",
  "workQueue.missed.offeredAt",
  "workQueue.missed.respondBy",
  "workQueue.missed.tip",
  "workQueue.missed.tipAction",
  "workQueue.missed.seeMissed",
  // Provider-facing money surfaces ship translated in all supported locales
  // (enforced by the i18n coverage test).

  // Warm-workshop redesign (2026-07): console chrome, P1-P4, B1-B2, splash.
  "providerPayouts.locked",
  "stitch.hub.partner",
  "stitch.nav.group.account",
  "stitch.nav.group.developer",
  "stitch.nav.group.money",
  "stitch.nav.group.overview",
  "stitch.nav.group.shopSetup",
  "stitch.nav.provider.payouts",
  "warm.b1.keys.badge.live",
  "warm.b1.keys.badge.test",
  "warm.b1.keys.empty",
  "warm.b1.keys.error",
  "warm.b1.keys.manage",
  "warm.b1.keys.title",
  "warm.b1.legend.liveKeys",
  "warm.b1.legend.sandboxKeys",
  "warm.b1.links.apiReference",
  "warm.b1.links.integrationBrief",
  "warm.b1.orders.caption",
  "warm.b1.orders.empty",
  "warm.b1.orders.error",
  "warm.b1.orders.statOrders",
  "warm.b1.orders.statPaid",
  "warm.b1.orders.statVolume",
  "warm.b1.orders.title",
  "warm.b1.quickstart.copied",
  "warm.b1.quickstart.copy",
  "warm.b1.quickstart.copyFailed",
  "warm.b1.quickstart.title.production",
  "warm.b1.quickstart.title.sandbox",
  "warm.b1.recent.empty",
  "warm.b1.recent.error",
  "warm.b1.recent.title",
  "warm.b1.retry",
  "warm.b1.subtitle.production",
  "warm.b1.subtitle.sandbox",
  "warm.b2.col.created",
  "warm.b2.col.environment",
  "warm.b2.col.lastUsed",
  "warm.b2.col.nameKey",
  "warm.b2.env.live",
  "warm.b2.env.test",
  "warm.b2.keys.create",
  "warm.b2.keys.sub",
  "warm.b2.keys.title",
  "warm.b2.never",
  "warm.b2.reveal.created",
  "warm.b2.reveal.rotated",
  "warm.b2.webhooks.activeDelivery",
  "warm.b2.webhooks.added",
  "warm.b2.webhooks.attempts",
  "warm.b2.webhooks.noDeliveries",
  "warm.b2.webhooks.title",
  "warm.chip.inReview",
  "warm.chip.liveMode",
  "warm.chip.openForJobs",
  "warm.chip.paused",
  "warm.chip.sandbox",
  "warm.topbar.apiReference",
  "warm.topbar.docs",
  "warmCap.addPrinter",
  "warmCap.addRateCard",
  "warmCap.addRateMaterial",
  "warmCap.capabilityPaused",
  "warmCap.capabilityResumed",
  "warmCap.delete",
  "warmCap.edit",
  "warmCap.minOrder",
  "warmCap.noRates",
  "warmCap.perCm3",
  "warmCap.perGram",
  "warmCap.printerEditorAdd",
  "warmCap.printerEditorEdit",
  "warmCap.printersLabel",
  "warmCap.rateCardsLabel",
  "warmCap.rateEditorLabel",
  "warmCap.specEnclosed",
  "warmCap.specPaused",
  "warmCap.toggleLabel",
  "warmHome.checklist.continue",
  "warmHome.checklist.done",
  "warmHome.checklist.locked",
  "warmHome.checklist.progress",
  "warmHome.checklist.stepAccount",
  "warmHome.checklist.stepCapabilities",
  "warmHome.checklist.stepGoLive",
  "warmHome.checklist.stepPayout",
  "warmHome.checklist.stepRateCard",
  "warmHome.checklist.title",
  "warmHome.checklist.verifyNow",
  "warmHome.dayOne.emptyBody",
  "warmHome.dayOne.emptyTitle",
  "warmHome.dayOne.greeting",
  "warmHome.dayOne.greetingNamed",
  "warmHome.dayOne.offersTitle",
  "warmHome.dayOne.subReady",
  "warmHome.dayOne.subStepsMany",
  "warmHome.dayOne.subStepsOne",
  "warmHome.dayOne.waitingCount",
  "warmHome.dayOne.zeroWaiting",
  "warmHome.earnings.eligible",
  "warmHome.earnings.none",
  "warmHome.earnings.payouts",
  "warmHome.greeting",
  "warmHome.greetingNamed",
  "warmHome.kpi.alsoCurrencies",
  "warmHome.kpi.capacityUsed",
  "warmHome.kpi.dueToday",
  "warmHome.kpi.earnedMonth",
  "warmHome.kpi.firstExpires",
  "warmHome.kpi.fromJobsMany",
  "warmHome.kpi.fromJobsOne",
  "warmHome.kpi.jobsInProgress",
  "warmHome.kpi.noPayablesMonth",
  "warmHome.kpi.openOffers",
  "warmHome.legend.acceptingJobs",
  "warmHome.legend.networkLive",
  "warmHome.legend.payoutsAction",
  "warmHome.legend.payoutsVerified",
  "warmHome.legend.setup",
  "warmHome.links.guide",
  "warmHome.links.support",
  "warmHome.nudges.addCapabilities",
  "warmHome.nudges.addRateCard",
  "warmHome.nudges.offerExpiring",
  "warmHome.nudges.qualityIncident",
  "warmHome.nudges.shopPaused",
  "warmHome.nudges.title",
  "warmHome.nudges.verifyPayouts",
  "warmHome.offers.accept",
  "warmHome.offers.colExpires",
  "warmHome.offers.colItem",
  "warmHome.offers.colMaterial",
  "warmHome.offers.colOrder",
  "warmHome.offers.colPayout",
  "warmHome.offers.confirmAccept",
  "warmHome.offers.confirmDecline",
  "warmHome.offers.decline",
  "warmHome.offers.empty",
  "warmHome.offers.expired",
  "warmHome.offers.hint",
  "warmHome.offers.moreParts",
  "warmHome.offers.viewAll",
  "warmHome.offers.openWorkQueue",
  "warmHome.offers.title",
  "warmHome.payout.hint",
  "warmHome.payout.notVerified",
  "warmHome.payout.title",
  "warmHome.payout.verified",
  "operations.warning.orderEligibility",
  "warmHome.payout.reviewSetup",
  "warmHome.checklist.stepIdentity",
  "warmHome.checklist.stepBusinessReview",
  "warmHome.checklist.stepAgreement",
  "onboardingWizard.business.reviewHint",
  "onboardingWizard.individual.identityHint",
  "onboardingWizard.verification.identity",
  "onboardingWizard.verification.businessReview",
  "onboardingWizard.verification.agreement",
  "onboardingWizard.verification.payoutSetup",
  "onboardingWizard.verification.providerApproval",
  "onboardingWizard.verification.checkingResult",
  "onboardingWizard.verification.resultDelayed",
  "onboardingWizard.verification.processing",
  "onboardingWizard.verification.unavailable",
  "onboardingWizard.verification.retryBody",
  "onboardingWizard.verification.startFailed",
  "onboardingWizard.agreement.cardTitle",
  "onboardingWizard.agreement.cardBody",
  "onboardingWizard.agreement.documentName",
  "onboardingWizard.hero.agreementTitle",
  "onboardingWizard.hero.agreementBody",
  "onboardingWizard.progress.agreement",
  "onboardingWizard.progress.approved",
  "onboardingWizard.pending.agreementTitle",
  "onboardingWizard.pending.agreementBody",
  "onboardingWizard.pending.agreementBodyPayoutPending",
  "onboardingWizard.pending.agreementRequired",
  "onboardingWizard.pending.approvedTitle",
  "onboardingWizard.pending.approvedBodyIntakeOff",
  "onboardingWizard.pending.approvedBodyBlocked",
  "onboardingWizard.pending.intakeTitle",
  "onboardingWizard.pending.intakeBody",
  "onboardingWizard.pending.startAcceptingJobs",
  "onboardingWizard.pending.startAcceptingJobsBusy",
  "onboardingWizard.pending.remainingTitle",
  "onboardingWizard.agreement.versionLabel",
  "onboardingWizard.agreement.readDocument",
  "onboardingWizard.agreement.checkbox",
  "onboardingWizard.agreement.accept",
  "onboardingWizard.agreement.accepting",
  "onboardingWizard.agreement.accepted",
  "onboardingWizard.agreement.error",
  "onboardingWizard.agreement.unavailable",
  "onboardingWizard.agreement.loadError",
  "onboardingWizard.agreement.retry",
  "onboardingWizard.agreement.errorUnpublished",
  "onboardingWizard.agreement.errorVersionMismatch",
  "onboardingWizard.agreement.docsOutdated",
  "onboardingWizard.review.identity",
  "onboardingWizard.review.identityGood",
  "onboardingWizard.review.identityMissing",
  "onboardingWizard.review.businessReview",
  "onboardingWizard.review.agreement",
  "onboardingWizard.review.payoutSetup",
  "onboardingWizard.review.payoutConfigured",
  "onboardingWizard.review.payoutPending",
  "onboardingWizard.review.identityRequired",
  "onboardingWizard.review.agreementRequired",
  "onboardingWizard.review.businessReviewRequired",
  // Onboarding wizard rework (2026-07-24) — enforce translations in every locale.
  "onboardingWizard.progress.stepOf",
  "onboardingWizard.checklist.title",
  "onboardingWizard.welcome.phase1Title",
  "onboardingWizard.welcome.phase1Body",
  "onboardingWizard.welcome.phase2Title",
  "onboardingWizard.welcome.phase2Body",
  "onboardingWizard.welcome.phase3Title",
  "onboardingWizard.welcome.phase3Body",
  "onboardingWizard.business.why",
  "onboardingWizard.capabilities.why",
  "onboardingWizard.capabilities.checkPrinter",
  "onboardingWizard.capabilities.checkPrices",
  "onboardingWizard.capabilities.checkShipping",
  "onboardingWizard.capabilities.missingPrinter",
  "onboardingWizard.capabilities.missingPrices",
  "onboardingWizard.capabilities.missingShipping",
  "onboardingWizard.error.setupMissing",
  "onboardingWizard.serviceArea.why",
  "onboardingWizard.serviceArea.gateArea",
  "onboardingWizard.serviceArea.gateFulfillment",
  "onboardingWizard.serviceArea.gateCarriers",
  "onboardingWizard.portfolio.why",
  "onboardingWizard.verification.why",
  "onboardingWizard.verification.actNowTitle",
  "onboardingWizard.verification.afterSubmitTitle",
  "onboardingWizard.verification.afterSubmitBody",
  "onboardingWizard.verification.businessReviewWhat",
  "onboardingWizard.verification.payoutSetupWhat",
  "onboardingWizard.verification.providerApprovalWhat",
  "onboardingWizard.review.why",
  "onboardingWizard.review.afterSubmit",
  "onboardingWizard.pending.bulletReview",
  "onboardingWizard.pending.meanwhileTitle",
  "onboardingWizard.pending.bulletPolish",
  "onboardingWizard.pending.bulletEstimate",
  "providerPayouts.settlements.approved",
  "providerPayouts.settlements.count",
  "providerPayouts.settlements.countOne",
  "providerPayouts.settlements.paid",
  "providerPayouts.settlements.paidSub",
  "providerPayouts.settlements.title",
  "providerPayouts.settlements.body",
  "providerPayouts.settlements.empty",
  "providerPayouts.settlements.period",
  "providerPayouts.settlements.reference",
  "providerPayouts.settlements.net",
  "providerPayouts.settlements.status",
  "providerPayouts.setup.title",
  "providerPayouts.setup.configured",
  "providerPayouts.setup.placeholder",
  "providerPayouts.setup.selfIntro",
  "providerPayouts.setup.emailLabel",
  "providerPayouts.setup.currencyLabel",
  "providerPayouts.setup.currencyPlaceholder",
  "providerPayouts.setup.submit",
  "providerPayouts.setup.submitting",
  "providerPayouts.setup.success",
  "providerPayouts.setup.hint",
  "providerPayouts.setup.pendingNote",
  "providerPayouts.setup.step1Title",
  "providerPayouts.setup.step1Body",
  "providerPayouts.setup.step2Title",
  "providerPayouts.setup.step2Body",
  "providerPayouts.setup.step3Title",
  "providerPayouts.setup.step3Body",
  "providerPayouts.setup.currencyFact",
  "providerPayouts.setup.done.registered",
  "providerPayouts.setup.done.claimEmail",
  "providerPayouts.setup.done.claimEmailBody",
  "providerPayouts.setup.done.payoutsStart",
  "providerPayouts.setup.done.noEmail",
  "providerPayouts.setup.done.noEmailLink",
  "providerPayouts.eligibility.title",
  "providerPayouts.eligibility.ready",
  "providerPayouts.eligibility.blocked",
  "providerPayouts.pending.title",
  "providerPayouts.pending.body",
  "providerPayouts.pending.empty",
  "providerPayouts.pending.count",
  "providerPayouts.pending.ordersHeading",
  "providerPayouts.pending.order",
  "providerPayouts.pending.statusOwed",
  "providerPayouts.pending.statusPaid",
  "providerPayouts.pending.statusHold",
  "providerPayouts.standing.title",
  "providerPayouts.standing.count",
  "providerPayouts.standing.window",
  "providerPayouts.standing.clear",
  "providerPayouts.standing.recentHeading",
  "providerPayouts.standing.kindProduction",
  "providerPayouts.standing.kindDelivery",
  "providerPayouts.standing.kindWithdrew",
  "providerPayouts.standing.kindOther",
  "providerPayouts.standing.pausedTitle",
  "providerPayouts.standing.pausedBody",
  "providerPayouts.standing.pausedReason",
  "providerPayouts.standing.pausedContact",
  "workQueue.shipment.title",
  "workQueue.shipment.hint",
  "workQueue.shipment.carrier",
  "workQueue.shipment.number",
  "workQueue.shipment.url",
  "workQueue.shipment.record",
  "workQueue.shipment.track",
  "workQueue.shipment.loading",
  "workQueue.shipment.required",
  "workQueue.shipment.edit",
  "workQueue.shipment.editHint",
  "workQueue.shipment.saveChanges",
  "workQueue.shipment.cancelEdit",
  "workQueue.shipment.changeCourier",
  "workQueue.shipment.savedCourier",
  "workQueue.shipment.chooseCourier",
  "workQueue.shipment.savedCouriers",
  "workQueue.shipment.countryCouriers",
  "workQueue.shipment.otherCourier",
  "workQueue.shipment.courierName",
  "workQueue.shipment.courierNameTooLong",
  "workQueue.shipment.courierScope",
  "workQueue.shipment.couriersUnavailable",
  "workQueue.shipment.couriersLoading",
  "workQueue.shipment.addTrackingUrl",
  "workQueue.shipment.optionalTrackingUrl",
  "workQueue.dialog.startPrinting.title",
  "workQueue.dialog.startPrinting.body",
  "workQueue.dialog.startPrinting.bodyDueBy",
  "workQueue.dialog.markShipped.title",
  "workQueue.dialog.markShipped.body",
  "workQueue.dialog.markShipped.bodyNoWindow",
  "workQueue.evidence.required",
  "workQueue.markingShipped",
  "workQueue.shippedPill",
  "workQueue.notStartedPill",
  "packingCheck.eyebrow",
  "packingCheck.title",
  "packingCheck.close",
  "packingCheck.step.inspect",
  "packingCheck.step.wrap",
  "packingCheck.step.box",
  "packingCheck.step.fill",
  "packingCheck.step.seal",
  "packingCheck.step.photo",
  "packingCheck.dontShowAgain",
  "packingCheck.openGuide",
  "packingCheck.continue",
  "providerGovernance.status.notStarted",
  "providerGovernance.status.requiresInput",
  "providerGovernance.status.processing",
  "providerGovernance.status.verified",
  "providerGovernance.status.failed",
  "providerGovernance.status.cancelled",
  "providerGovernance.status.expired",
  "providerGovernance.status.notSubmitted",
  "providerGovernance.status.pending",
  "providerGovernance.status.approved",
  "providerGovernance.status.rejected",
  "providerGovernance.status.moreInformationRequired",
  "providerGovernance.status.notAccepted",
  "providerGovernance.status.accepted",
  "providerGovernance.status.configured",
  "providerGovernance.status.disabled",
  "providerGovernance.status.draft",
  "providerGovernance.status.awaitingApproval",
  "providerGovernance.status.paid",
  "providerGovernance.status.awaitingFunding",
  "providerGovernance.status.queued",
  "providerGovernance.status.returned",
  "providerGovernance.blockingReason.identityNotVerified",
  "providerGovernance.blockingReason.businessNotApproved",
  "providerGovernance.blockingReason.providerAgreementNotAccepted",
  "providerGovernance.blockingReason.providerNotApproved",
  "providerGovernance.blockingReason.profileIncomplete",
  "providerGovernance.blockingReason.countryNotEnabled",
  "providerGovernance.blockingReason.countryClosed",
  "providerGovernance.blockingReason.providerNotAcceptingOrders",
  "providerGovernance.blockingReason.payoutMethodNotConfigured",
  "providerGovernance.blockingReason.payoutCountryUnsupported",
  "providerGovernance.payoutProvider.manual",
  "providerGovernance.payoutProvider.external",
  "access.gate.awaitingLaunch.body",
  "access.gate.ready.body",
  "onboardingWizard.adminRejection.callToAction",
  "onboardingWizard.adminRejection.reason.payoutCountryUnsupported",
  "warmHome.printers.idle",
  "warmHome.printers.paused",
  "warmHome.printers.printing",
  "warmHome.printers.title",
  "warmHome.printers.unitCount",
  "warmHome.printing.allJobs",
  "warmHome.printing.awaitingQa",
  "warmHome.printing.shipped",
  "warmHome.printing.autoDeliver",
  "warmHome.printing.due",
  "warmHome.printing.empty",
  "warmHome.printing.inProgress",
  "warmHome.printing.title",
  "warmHome.subOffersMany",
  "warmHome.subOffersOne",
  "warmHome.subOpenNoOffers",
  "warmHome.subOpenOffersMany",
  "warmHome.subOpenOffersOne",
  "warmHome.subPaused",
  "warmHome.time.days",
  "warmHome.time.hoursMinutes",
  "warmHome.time.minutes",
  "workQueue.p2.col.accepted",
  "workQueue.p2.col.expires",
  "workQueue.p2.col.item",
  "workQueue.p2.col.material",
  "workQueue.p2.col.order",
  "workQueue.p2.col.status",
  "workQueue.p2.customerNotes",
  "workQueue.p2.declineEllipsis",
  "workQueue.p2.expired",
  "workQueue.p2.expiresIn",
  "workQueue.p2.footer",
  "workQueue.p2.itemLabel",
  "workQueue.p2.itemMore",
  "workQueue.p2.parts",
  "workQueue.p2.qa.awaitingResponse",
  "workQueue.p2.spec.color",
  "workQueue.p2.spec.deadline",
  "workQueue.p2.spec.infill",
  "workQueue.p2.spec.layer",
  "workQueue.p2.spec.material",
  "workQueue.p2.spec.priority",
  "workQueue.p2.priorityPreset.speed",
  "workQueue.p2.priorityPreset.balanced",
  "workQueue.p2.priorityPreset.quality",
  "workQueue.p2.priorityPreset.price",
  "workQueue.p2.qualityLevel.standard",
  "workQueue.p2.qualityLevel.documented",
  "workQueue.p2.qualityLevel.certified_provider",
  "workQueue.p2.qualityLevel.regulated_manual_review",
  "workQueue.p2.spec.quality",
  "workQueue.p2.spec.quantity",
  "workQueue.p2.spec.shipTo",
  "workQueue.p2.spec.shipping",
  "workQueue.p2.spec.size",
  "workQueue.p2.status.handOffOverdue",
  "workQueue.p2.status.autoDeliverBy",
  "workQueue.p2.status.handOffWithin",
  "workQueue.p2.status.startBy",
  "workQueue.p2.status.startOverdue",
  "workQueue.p2.status.startWithin",
  "workQueue.p2.tab.accepted",
  "workQueue.p2.tab.acceptedLabel",
  "workQueue.p2.tab.offers",
  "workQueue.p2.tab.offersLabel",
  "workQueue.p2.tab.qa",
  "workQueue.p2.tab.qaLabel",
  "workQueue.p2.tabsLabel",
  "workQueue.p2.timeHm",
  "workQueue.p2.tuneMatching",
  "workQueue.copyOrderId",
  "workQueue.orderIdCopied",
  "workQueue.adminHelp",
  "workQueue.adminHelpSubject",
  "workQueue.section.specs",
  "workQueue.section.model",
  "workQueue.section.payout",
  "workQueue.address.title",
  "workQueue.address.reveal",
  "workQueue.report.open",
  "workQueue.report.hint",
  "workQueue.report.reason",
  "workQueue.report.reasonPlaceholder",
  "workQueue.report.reason.print_failure",
  "workQueue.report.reason.material_unavailable",
  "workQueue.report.reason.spec_impossible",
  "workQueue.report.reason.customer_unreachable",
  "workQueue.report.reason.other",
  "workQueue.report.summary",
  "workQueue.report.summaryPlaceholder",
  "workQueue.report.minHint",
  "workQueue.report.submit",
  "workQueue.report.success",
  "workQueue.report.error.incomplete",
  "workQueue.actuals.open",
  "workQueue.actuals.title",
  "workQueue.actuals.hint",
  "workQueue.actuals.tookLonger",
  "workQueue.actuals.hours",
  "workQueue.actuals.grams",
  "workQueue.actuals.submit",
  "workQueue.actuals.invalid",
  "workQueue.actuals.thanks",
  "workQueue.actuals.reportedLongerYes",
  "workQueue.actuals.reportedLongerNo",
  "workQueue.actuals.reportedLine",
  "workQueue.actuals.estimatedLine",
  "workQueue.title",
] as const satisfies readonly MessageKey[];

const dictionaries: Record<SupportedLocale, Partial<Dictionary>> = {
  en,
  es: {},
  fr: {},
  de: {},
  pt: {},
  "zh-Hans": {},
  ja: {},
  ko: {},
  ar: {},
  hi: {},
  ms: {},
};

// MERGED-PERF-001 / ADR-WES1: dictionaries load in parts.
// The build (vite-i18n-split.ts) keeps only the public keys in `en` above and
// serves the rest of English, and each other locale, as separate chunks
// (src/i18n/message-loaders.ts). `loadLocale` fetches a locale's public part
// plus every part the app already asked for; `requireMessageParts` asks for
// the console part (with ConsoleSurface) or the English admin part (with the
// admin shell). In tsc and Vitest `en` is whole and a locale's public load is
// its whole file, so source mode behaves like an unsplit dictionary.
const requiredParts = new Set<DeferredMessagePart>();
const requestedLocales = new Set<SupportedLocale>(["en"]);
const loadedParts = new Set<string>();
const pendingLoads = new Map<string, Promise<void>>();
const messageListeners = new Set<() => void>();
let messagesVersion = 0;

function loadMessagePart(locale: SupportedLocale, part: MessagePart): Promise<void> {
  const id = `${locale}:${part}`;
  if (loadedParts.has(id)) return Promise.resolve();
  const inFlight = pendingLoads.get(id);
  if (inFlight) return inFlight;
  const loader = messagePartLoader(locale, part);
  if (!loader) {
    // Bundled, English-only, or an unknown locale (translate() falls back to EN).
    loadedParts.add(id);
    return Promise.resolve();
  }
  const promise = loader()
    .then((messages) => {
      Object.assign(dictionaries[locale], messages);
      loadedParts.add(id);
      messagesVersion += 1;
      for (const listener of messageListeners) listener();
    })
    .finally(() => pendingLoads.delete(id));
  pendingLoads.set(id, promise);
  return promise;
}

/**
 * Load a locale's public copy plus every part the app already requires (the
 * console part once the console has opened). Idempotent; a failed load can be
 * retried.
 */
export async function loadLocale(locale: SupportedLocale): Promise<void> {
  requestedLocales.add(locale);
  await Promise.all([
    loadMessagePart(locale, "public"),
    ...[...requiredParts].map((part) => loadMessagePart(locale, part))
  ]);
}

/**
 * Make `parts` available for English and every locale requested so far, and
 * for every locale loaded later. English failing rejects (the copy would be
 * blank); another locale failing falls back to English, as a failed
 * `loadLocale` always has.
 */
export function requireMessageParts(parts: readonly DeferredMessagePart[]): Promise<void> {
  for (const part of parts) requiredParts.add(part);
  const english = Promise.all(parts.map((part) => loadMessagePart("en", part)));
  const others = [...requestedLocales]
    .filter((locale) => locale !== "en")
    .flatMap((locale) =>
      parts.map((part) =>
        loadMessagePart(locale, part).catch((error) => {
          console.error("Failed to load locale messages", { locale, part, error });
        })
      )
    );
  return Promise.all([english, ...others]).then(() => undefined);
}

/** Changes whenever a message part arrives; see lib/use-messages-version.ts. */
export function subscribeToMessages(listener: () => void): () => void {
  messageListeners.add(listener);
  return () => {
    messageListeners.delete(listener);
  };
}

export function getMessagesVersion(): number {
  return messagesVersion;
}

const reportedMissingKeys = new Set<string>();

/**
 * A key outside the loaded parts. `message-parts.test.ts` keeps public screens
 * on public keys, so this is a safety net: load everything left and let
 * `useMessagesVersion` (lib/use-messages-version.ts) repaint, instead of
 * showing a blank label for good.
 */
function onMissingMessage(key: string): void {
  if (reportedMissingKeys.has(key)) return;
  reportedMissingKeys.add(key);
  console.warn(`[i18n] "${key}" was rendered before its message part loaded; loading the rest.`);
  void requireMessageParts(["console", "admin"]).catch((error) => {
    console.error("Failed to load message parts", { error });
  });
}

const supportedLookup = new Set<string>(supportedLocales);

export function detectLocale(preferredLocales: readonly string[] = []): SupportedLocale {
  for (const preferred of preferredLocales) {
    const normalized = preferred.trim();

    if (supportedLookup.has(normalized)) {
      return normalized as SupportedLocale;
    }

    const lower = normalized.toLowerCase();
    if (lower.startsWith("zh")) {
      return "zh-Hans";
    }

    const base = lower.split("-")[0];
    if (supportedLookup.has(base)) {
      return base as SupportedLocale;
    }
  }

  return "en";
}

export function translate(locale: SupportedLocale, key: MessageKey): string {
  const value = dictionaries[locale]?.[key] ?? en[key];
  if (value !== undefined || !messagesSplit) return value;
  onMissingMessage(key);
  return "";
}

export function hasLocaleTranslation(locale: SupportedLocale, key: MessageKey): boolean {
  return Object.prototype.hasOwnProperty.call(dictionaries[locale], key);
}
